/*
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║          NAUT X MEDIA GROUP — PORTFOLIO v2.0                    ║
 * ║          Production Build — Clerk + Supabase + Vercel           ║
 * ║          Last Updated: April 2026                               ║
 * ╚══════════════════════════════════════════════════════════════════╝
 *
 * ═══════════════════════════════════════════════════════════════════
 *  QUICK START — GET THIS LIVE IN 15 MINUTES
 * ═══════════════════════════════════════════════════════════════════
 *
 *  STEP 1: CREATE THE PROJECT
 *  ──────────────────────────
 *  npx create-react-app naut-x-site
 *  cd naut-x-site
 *  npm install @clerk/clerk-react @supabase/supabase-js
 *
 *  STEP 2: REPLACE src/App.js WITH THIS ENTIRE FILE
 *  ──────────────────────────────────────────────────
 *  Change first line to:
 *    import React, { useState, useEffect, useRef, useCallback } from "react";
 *
 *  STEP 3: SET UP CLERK (Auth)
 *  ───────────────────────────
 *  1. Go to https://clerk.com → Create account → New Application
 *  2. Name it "Naut X Media Group"
 *  3. Enable sign-in methods: Email, Google, Twitter
 *  4. Go to API Keys → copy Publishable Key (starts with pk_)
 *  5. Create .env in project root:
 *       REACT_APP_CLERK_PUBLISHABLE_KEY=pk_live_xxxxx
 *
 *  STEP 4: SET UP SUPABASE (Database)
 *  ───────────────────────────────────
 *  1. Go to https://supabase.com → Create account → New Project
 *  2. Wait for init (~2 min) → Settings → API
 *  3. Copy "Project URL" and "anon public" key
 *  4. Add to .env:
 *       REACT_APP_SUPABASE_URL=https://xxxxx.supabase.co
 *       REACT_APP_SUPABASE_ANON_KEY=eyJhbGciOi...
 *  5. Run the SQL schema below in Supabase SQL Editor
 *
 *  STEP 5: DEPLOY TO VERCEL
 *  ────────────────────────
 *  1. Push to GitHub
 *  2. Go to https://vercel.com → Import repo
 *  3. Add environment variables in Vercel dashboard:
 *       REACT_APP_CLERK_PUBLISHABLE_KEY
 *       REACT_APP_SUPABASE_URL
 *       REACT_APP_SUPABASE_ANON_KEY
 *  4. Deploy → done. Auto-deploys on every push.
 *
 *  STEP 6: CUSTOM DOMAIN (Optional)
 *  ─────────────────────────────────
 *  1. Buy domain (Namecheap, Cloudflare, etc.)
 *  2. Vercel → Settings → Domains → Add domain
 *  3. Point DNS nameservers to Vercel
 *  4. SSL is automatic
 *
 * ═══════════════════════════════════════════════════════════════════
 *  SUPABASE SQL SCHEMA — Run this in Supabase SQL Editor
 * ═══════════════════════════════════════════════════════════════════
 *
 *  -- Site stats (projects shipped, clients, etc.)
 *  CREATE TABLE site_stats (
 *    id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
 *    key TEXT UNIQUE NOT NULL,
 *    value INTEGER NOT NULL DEFAULT 0,
 *    updated_at TIMESTAMPTZ DEFAULT now()
 *  );
 *
 *  INSERT INTO site_stats (key, value) VALUES
 *    ('projects_shipped', 2),
 *    ('active_clients', 2),
 *    ('satisfaction', 100);
 *
 *  -- Contact form submissions
 *  CREATE TABLE contact_submissions (
 *    id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
 *    name TEXT NOT NULL,
 *    email TEXT NOT NULL,
 *    message TEXT NOT NULL,
 *    created_at TIMESTAMPTZ DEFAULT now()
 *  );
 *
 *  -- Visitor analytics (optional)
 *  CREATE TABLE page_views (
 *    id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
 *    page TEXT NOT NULL,
 *    referrer TEXT,
 *    user_agent TEXT,
 *    created_at TIMESTAMPTZ DEFAULT now()
 *  );
 *
 *  -- Enable Row Level Security
 *  ALTER TABLE site_stats ENABLE ROW LEVEL SECURITY;
 *  ALTER TABLE contact_submissions ENABLE ROW LEVEL SECURITY;
 *  ALTER TABLE page_views ENABLE ROW LEVEL SECURITY;
 *
 *  -- Public read access for stats
 *  CREATE POLICY "Public read stats" ON site_stats
 *    FOR SELECT USING (true);
 *
 *  -- Public insert for contact form
 *  CREATE POLICY "Public insert contact" ON contact_submissions
 *    FOR INSERT WITH CHECK (true);
 *
 *  -- Public insert for page views
 *  CREATE POLICY "Public insert views" ON page_views
 *    FOR INSERT WITH CHECK (true);
 *
 * ═══════════════════════════════════════════════════════════════════
 *  QUICK EDIT TAGS — Search for 🔧 EDIT: to find editable sections
 * ═══════════════════════════════════════════════════════════════════
 *    🔧 EDIT: STATS        → Update project count, clients, etc.
 *    🔧 EDIT: PROJECTS     → Add/remove portfolio projects
 *    🔧 EDIT: SERVICES     → Update service offerings
 *    🔧 EDIT: SOCIALS      → Update social media links
 *    🔧 EDIT: ABOUT TEXT   → Update bio/description
 *    🔧 EDIT: CRYPTO LIST  → Change tracked cryptocurrencies
 *    🔧 EDIT: API KEYS     → Environment variable config
 *
 * ═══════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ClerkProvider, SignIn, SignedIn, SignedOut, useUser, useClerk, useAuth } from "@clerk/clerk-react";
import { createClient } from "@supabase/supabase-js";

/* ═══════════════════════════════════════════════════════════════
 *  🔒 SECURITY-FIRST CONFIGURATION
 *  ─────────────────────────────────────────────────────────────
 *  • Public keys (Clerk publishable, Supabase anon) ARE safe in
 *    client code BY DESIGN — they're built to be public, but ONLY
 *    if your Supabase RLS policies are correct (see schema files).
 *
 *  • SECRET keys (Finnhub, Twelve Data, Stripe, etc.) MUST go
 *    in serverless functions only — NEVER in client code.
 *    See /api/finnhub.js and /api/twelvedata.js
 *
 *  • Set these in .env.local for dev, Vercel dashboard for prod.
 *    Never commit .env files (add to .gitignore).
 * ═══════════════════════════════════════════════════════════════ */
const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Hard fail at boot if env vars missing — prevents accidentally shipping with no config
if (!CLERK_KEY || !SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // eslint-disable-next-line no-console
  console.error(
    "❌ Missing required env vars. Set REACT_APP_CLERK_PUBLISHABLE_KEY, " +
    "REACT_APP_SUPABASE_URL, and REACT_APP_SUPABASE_ANON_KEY in .env.local " +
    "(local) or Vercel dashboard (production)."
  );
}

const supabase = createClient(SUPABASE_URL || "", SUPABASE_ANON_KEY || "");

/* ═══════════════════════════════════════════════════════════════
 *  ADMIN CONFIG
 *  Whitelist of admin emails. Client-side check is just UX —
 *  REAL security lives in Supabase RLS policies (see admin-schema.sql)
 *  which verify Clerk JWT email server-side.
 * ═══════════════════════════════════════════════════════════════ */
const ADMIN_EMAILS = [
  "ethan.barton2112@gmail.com", // 🔧 EDIT: replace with YOUR Clerk login email
  // Add additional admin emails here
];

/* ═══════════════════════════════════════════════════════════════
 *  SECRET API KEYS — DO NOT PUT THEM HERE
 *  ─────────────────────────────────────────────────────────────
 *  Finnhub and Twelve Data keys live in serverless functions:
 *    /api/finnhub.js     → calls Finnhub from server, returns data
 *    /api/twelvedata.js  → calls Twelve Data from server
 *
 *  Frontend just calls /api/finnhub and /api/twelvedata —
 *  the actual keys never reach the browser.
 *
 *  Set FINNHUB_API_KEY and TWELVEDATA_API_KEY (no REACT_APP_ prefix)
 *  in Vercel dashboard. Those are server-side env vars only.
 * ═══════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════
 *  NAUT X MEDIA GROUP — v2.0
 *  Luxury Command Center + Editorial Aesthetic
 *  Olive / Gold / Ruby / Black
 * ═══════════════════════════════════════════════════════════════ */

const GOLD = "#b8a04a";
const GOLD_DIM = "rgba(184,160,74,0.6)";
const RUBY = "#9b1b30";
const CREAM = "#d4cdb4";
const CREAM_DIM = "rgba(212,205,180,0.7)";
const BG = "#08080a";
const BG_CARD = "rgba(10,10,8,0.92)";
const OLIVE = "#4a5a2b";
const OLIVE_DIM = "rgba(74,90,43,0.55)";

const FONTS = {
  display: "'Cormorant Garamond', serif",
  body: "'Outfit', sans-serif",
  mono: "'JetBrains Mono', monospace",
};

// --- CONFIG ---
const CONFIG = {
  name: "NAUT X",
  tagline: "MEDIA GROUP",
  subtitle: "Digital Architecture & Creative Engineering",
  socials: {
    x: "https://x.com/NautX_",
    ig: "https://www.instagram.com/e10.210",
    yt: "https://www.youtube.com/@NautXMedia",
    email: "admin@nautxmediagroup.com",
  },
  services: [
    { num: "01", title: "Full-Stack Development", desc: "Engineered web platforms with auth, databases, payments, and real infrastructure. Production-grade, not templates." },
    { num: "02", title: "Brand & Identity Systems", desc: "Visual identities that command attention. Logo systems, color architecture, typography, and brand guidelines." },
    { num: "03", title: "Creative Direction", desc: "End-to-end vision. Strategy, UX, motion design, and the kind of obsessive polish that separates the serious from the forgettable." },
    { num: "04", title: "Crypto & Web3 Interfaces", desc: "Token dashboards, DeFi platforms, NFT experiences. If it's on-chain, we build the frontend people actually want to use." },
  ],
  projects: [
    {
      title: "Goddess Selene",
      type: "FULL-STACK PLATFORM",
      desc: "Complete web application featuring Clerk authentication, Supabase real-time backend, gamification engine with leaderboards, spin wheels, task systems, and confessionals. 4,000+ lines of production React.",
      tech: ["React", "Clerk Auth", "Supabase", "Real-time DB"],
      accent: RUBY,
      year: "2026",
      status: "LIVE",
      url: "https://goddess-selene.com",
    },
    {
      title: "Naut X — This Site",
      type: "PERSONAL PORTFOLIO",
      desc: "The site you're looking at right now. Live crypto feeds, particle systems, scroll-driven animations, and a custom command center dashboard. Built from scratch.",
      tech: ["React", "Canvas API", "REST APIs", "CSS Animations"],
      accent: GOLD,
      year: "2026",
      status: "LIVE",
    },
    {
      title: "FIXER.NET",
      type: "OFFLINE DM TOOL",
      desc: "Single-file HTML Cyberpunk RED gamemaster control panel with AI-powered NPC/location/encounter generation, full gear database, session notes, and localStorage persistence. Zero backend, runs entirely offline in any browser.",
      tech: ["Vanilla JS", "HTML/CSS", "Claude API", "LocalStorage"],
      accent: OLIVE,
      year: "2026",
      status: "LIVE",
    },
    {
      title: "Custom Art Website for Client",
      type: "CLIENT PROJECT",
      desc: "Coming Soon!",
      tech: ["TBA"],
      accent: GOLD,
      year: "2026",
      status: "OFFLINE",
    },
  ],
};

/* ═══════════════════════════════════════════════════════════════
 *  UTILITY HOOKS
 * ═══════════════════════════════════════════════════════════════ */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis];
}

function useMousePos() {
  const pos = useRef({ x: -500, y: -500 });
  useEffect(() => {
    const h = (e) => { pos.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener("mousemove", h);
    return () => window.removeEventListener("mousemove", h);
  }, []);
  return pos;
}

function useLiveTime() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setT(new Date()), 1000); return () => clearInterval(i); }, []);
  return t;
}

/* ═══════════════════════════════════════════════════════════════
 *  ANIMATED NUMBER — counts up on mount
 * ═══════════════════════════════════════════════════════════════ */
function AnimNum({ end, duration = 2000, prefix = "", suffix = "" }) {
  const [val, setVal] = useState(0);
  const [ref, vis] = useReveal();
  const started = useRef(false);

  useEffect(() => {
    if (!vis || started.current) return;
    started.current = true;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setVal(Math.floor(eased * end));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [vis, end, duration]);

  return <span ref={ref}>{prefix}{val.toLocaleString()}{suffix}</span>;
}

/* ═══════════════════════════════════════════════════════════════
 *  REVEAL WRAPPER
 * ═══════════════════════════════════════════════════════════════ */
function Reveal({ children, delay = 0, dir = "up", style = {} }) {
  const [ref, vis] = useReveal();
  const t = { up: "translateY(50px)", down: "translateY(-50px)", left: "translateX(60px)", right: "translateX(-60px)", none: "none" };
  return (
    <div ref={ref} style={{
      ...style, opacity: vis ? 1 : 0, transform: vis ? "none" : t[dir],
      transition: `all 1s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
    }}>{children}</div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  MATRIX RAIN — olive/gold/ruby digital rain
 *  Multi-layer depth, mouse-reactive, character morphing
 * ═══════════════════════════════════════════════════════════════ */
function MatrixRain() {
  const canvas = useRef(null);
  const mouse = useMousePos();

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let w, h, columns, streams, raf;

    // Character sets — katakana + latin + numbers + crypto symbols
    const CHARS = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン" +
      "ABCDEFGHIJKLMNOPQRSTUVWXYZ" +
      "0123456789" +
      "₿Ξ◆◇▸▹△▽※∞Ω∆λ≡≈";

    const randChar = () => CHARS[Math.floor(Math.random() * CHARS.length)];

    // Color palette for streams
    const COLORS = {
      olive: { r: 90, g: 110, b: 50 },     // primary — most streams
      gold: { r: 184, g: 160, b: 74 },      // accent — rare highlight streams
      ruby: { r: 155, g: 27, b: 48 },       // accent — very rare
      dim: { r: 60, g: 75, b: 35 },         // background layer
    };

    const createStreams = () => {
      const fontSize = 14;
      columns = Math.ceil(w / fontSize);
      streams = [];

      for (let i = 0; i < columns; i++) {
        // Determine layer: 0 = far back (dim/slow), 1 = mid, 2 = front (bright/fast)
        const layer = Math.random() < 0.15 ? 2 : Math.random() < 0.4 ? 1 : 0;
        // Color type: most olive, some gold, rare ruby
        const colorRoll = Math.random();
        const colorType = colorRoll < 0.78 ? "olive" : colorRoll < 0.93 ? "gold" : "ruby";

        const speed = layer === 2 ? (1.5 + Math.random() * 2.5) : layer === 1 ? (0.8 + Math.random() * 1.2) : (0.3 + Math.random() * 0.6);
        const length = Math.floor(8 + Math.random() * 22);
        const opacity = layer === 2 ? 0.9 : layer === 1 ? 0.5 : 0.2;
        const size = layer === 2 ? fontSize : layer === 1 ? fontSize * 0.85 : fontSize * 0.7;

        streams.push({
          x: i * fontSize + Math.random() * 4 - 2,
          y: Math.random() * h * 2 - h,
          speed,
          length,
          opacity,
          size,
          colorType,
          layer,
          chars: Array.from({ length }, randChar),
          morphTimer: Math.random() * 100,
          morphRate: 2 + Math.random() * 8,
        });
      }

      // Sort by layer so far-back renders first
      streams.sort((a, b) => a.layer - b.layer);
    };

    const resize = () => {
      w = c.width = window.innerWidth;
      h = c.height = window.innerHeight;
      createStreams();
    };
    resize();
    window.addEventListener("resize", resize);

    let frameCount = 0;

    const draw = () => {
      // Fade trail — dark overlay each frame instead of full clear
      ctx.fillStyle = "rgba(8, 8, 10, 0.12)";
      ctx.fillRect(0, 0, w, h);

      frameCount++;
      const mx = mouse.current.x, my = mouse.current.y;

      for (let s = 0; s < streams.length; s++) {
        const stream = streams[s];

        // Morph characters periodically
        stream.morphTimer += 1;
        if (stream.morphTimer > stream.morphRate) {
          stream.morphTimer = 0;
          const idx = Math.floor(Math.random() * stream.chars.length);
          stream.chars[idx] = randChar();
        }

        // Move stream down
        stream.y += stream.speed;

        // Reset when fully off screen
        if (stream.y - stream.length * stream.size > h) {
          stream.y = -stream.length * stream.size - Math.random() * 200;
          // Re-roll color occasionally on reset
          if (Math.random() < 0.1) {
            const roll = Math.random();
            stream.colorType = roll < 0.78 ? "olive" : roll < 0.93 ? "gold" : "ruby";
          }
        }

        const baseColor = COLORS[stream.colorType];

        // Mouse proximity check
        const streamCenterY = stream.y - (stream.length * stream.size) / 2;
        const dx = mx - stream.x;
        const dy = my - streamCenterY;
        const mouseDist = Math.sqrt(dx * dx + dy * dy);
        const mouseInfluence = mouseDist < 200 ? (200 - mouseDist) / 200 : 0;

        ctx.font = `${stream.size}px "JetBrains Mono", "Courier New", monospace`;

        for (let j = 0; j < stream.chars.length; j++) {
          const charY = stream.y - j * stream.size;

          // Skip chars fully off screen
          if (charY < -stream.size || charY > h + stream.size) continue;

          // Brightness falls off toward the tail
          const headDist = j / stream.length;
          let alpha;

          if (j === 0) {
            // Head character — brightest
            alpha = stream.opacity * 1.2;
          } else {
            // Exponential falloff
            alpha = stream.opacity * Math.pow(1 - headDist, 1.8);
          }

          // Mouse boost — characters near cursor glow brighter + shift to gold
          let r = baseColor.r, g = baseColor.g, b = baseColor.b;
          if (mouseInfluence > 0) {
            const charDist = Math.sqrt((mx - stream.x) ** 2 + (my - charY) ** 2);
            if (charDist < 180) {
              const charInf = (180 - charDist) / 180;
              // Shift toward bright gold
              r = Math.floor(r + (220 - r) * charInf * 0.7);
              g = Math.floor(g + (200 - g) * charInf * 0.7);
              b = Math.floor(b + (100 - b) * charInf * 0.3);
              alpha = Math.min(1, alpha + charInf * 0.5);
            }
          }

          alpha = Math.max(0, Math.min(1, alpha));

          if (j === 0) {
            // Head glow effect
            ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.6)`;
            ctx.shadowBlur = stream.layer === 2 ? 12 : 6;
            ctx.fillStyle = `rgba(${Math.min(255, r + 80)}, ${Math.min(255, g + 80)}, ${Math.min(255, b + 40)}, ${alpha})`;
          } else {
            ctx.shadowColor = "transparent";
            ctx.shadowBlur = 0;
            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
          }

          ctx.fillText(stream.chars[j], stream.x, charY);
        }
      }

      // Reset shadow
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
    };

    // Initial fill with black
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, w, h);

    // Throttle to ~30fps + pause when user is typing in inputs
    let lastTime = 0;
    const targetFPS = 30;
    const frameInterval = 1000 / targetFPS;
    let isPaused = false;

    const checkFocus = () => {
      const el = document.activeElement;
      isPaused = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
    };
    document.addEventListener("focusin", checkFocus);
    document.addEventListener("focusout", checkFocus);

    const drawThrottled = (now) => {
      raf = requestAnimationFrame(drawThrottled);
      if (isPaused) return;
      if (now - lastTime < frameInterval) return;
      lastTime = now;
      draw();
    };

    drawThrottled(0);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("focusin", checkFocus);
      document.removeEventListener("focusout", checkFocus);
    };
  }, []);

  return <canvas ref={canvas} style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }} />;
}

/* ═══════════════════════════════════════════════════════════════
 *  CURSOR GLOW
 * ═══════════════════════════════════════════════════════════════ */
function CursorGlow() {
  const [p, setP] = useState({ x: -500, y: -500 });
  const rafRef = useRef(null);
  const pendingPos = useRef({ x: -500, y: -500 });

  useEffect(() => {
    const h = (e) => {
      pendingPos.current = { x: e.clientX, y: e.clientY };
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        setP(pendingPos.current);
        rafRef.current = null;
      });
    };
    window.addEventListener("mousemove", h, { passive: true });
    return () => {
      window.removeEventListener("mousemove", h);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);
  return (
    <div className="cursor-glow" style={{
      position: "fixed", left: p.x - 250, top: p.y - 250, width: 500, height: 500,
      borderRadius: "50%", pointerEvents: "none", zIndex: 1,
      background: "radial-gradient(circle, rgba(184,160,74,0.045) 0%, transparent 65%)",
      transition: "left 0.12s ease-out, top 0.12s ease-out",
    }} />
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  GRAIN OVERLAY
 * ═══════════════════════════════════════════════════════════════ */
function Grain() {
  return <div style={{
    position: "fixed", inset: 0, zIndex: 9999, pointerEvents: "none",
    opacity: 0.03, mixBlendMode: "overlay",
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
    backgroundSize: "128px 128px",
  }} />;
}

/* ═══════════════════════════════════════════════════════════════
 *  MAGNETIC BUTTON
 * ═══════════════════════════════════════════════════════════════ */
function MagBtn({ children, href, onClick, variant = "outline", style = {} }) {
  const ref = useRef(null);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [hov, setHov] = useState(false);

  const move = (e) => {
    const r = ref.current.getBoundingClientRect();
    setOff({ x: (e.clientX - r.left - r.width / 2) * 0.2, y: (e.clientY - r.top - r.height / 2) * 0.2 });
  };

  const base = {
    display: "inline-flex", alignItems: "center", gap: 10,
    padding: variant === "fill" ? "18px 44px" : "16px 40px",
    border: variant === "fill" ? "none" : `1px solid ${hov ? GOLD : "rgba(184,160,74,0.3)"}`,
    background: variant === "fill"
      ? (hov ? GOLD : "rgba(184,160,74,0.1)")
      : (hov ? "rgba(184,160,74,0.08)" : "transparent"),
    color: variant === "fill" ? (hov ? BG : GOLD) : GOLD,
    cursor: "pointer", fontFamily: FONTS.body, fontSize: 12, fontWeight: 500,
    letterSpacing: 3, textTransform: "uppercase", textDecoration: "none",
    transform: `translate(${off.x}px, ${off.y}px)`,
    transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1), background 0.3s, color 0.3s, border-color 0.3s",
    ...style,
  };

  const Tag = href ? "a" : "button";
  const props = href ? { href, target: "_blank", rel: "noopener noreferrer" } : { onClick };

  return (
    <Tag ref={ref} {...props}
      onMouseMove={move} onMouseLeave={() => { setOff({ x: 0, y: 0 }); setHov(false); }}
      onMouseEnter={() => setHov(true)} style={base}>
      {children}
    </Tag>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  SECTION LABEL
 * ═══════════════════════════════════════════════════════════════ */
function SectionLabel({ text, num }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 20, marginBottom: 56,
    }}>
      {num && <span style={{
        fontFamily: FONTS.mono, fontSize: 12, color: RUBY, letterSpacing: 2, opacity: 0.85,
      }}>{num}</span>}
      <div style={{ width: 48, height: 1, background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
      <span style={{
        fontFamily: FONTS.body, fontSize: 12, letterSpacing: 6,
        textTransform: "uppercase", color: GOLD,
      }}>{text}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  TICKER
 * ═══════════════════════════════════════════════════════════════ */
function Ticker({ words, speed = 30, accent = false }) {
  const t = words.join("  ◆  ");
  const doubled = `${t}  ◆  ${t}  ◆  `;
  return (
    <div style={{
      overflow: "hidden", whiteSpace: "nowrap", padding: "22px 0",
      borderTop: `1px solid ${accent ? "rgba(155,27,48,0.25)" : "rgba(184,160,74,0.18)"}`,
      borderBottom: `1px solid ${accent ? "rgba(155,27,48,0.25)" : "rgba(184,160,74,0.18)"}`,
      background: "rgba(8,8,10,0.88)",
      position: "relative", zIndex: 2,
    }}>
      <div style={{
        display: "inline-block", animation: `ticker ${speed}s linear infinite`,
        fontFamily: FONTS.mono, fontSize: 13, letterSpacing: 4, fontWeight: 400,
        textTransform: "uppercase", color: accent ? "rgba(155,27,48,0.7)" : "rgba(184,160,74,0.55)",
      }}>{doubled}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  GLITCH TEXT
 * ═══════════════════════════════════════════════════════════════ */
function Glitch({ text, style = {} }) {
  return (
    <span style={{ position: "relative", display: "inline-block", ...style }}>
      <span aria-hidden style={{
        position: "absolute", inset: 0, color: RUBY, clipPath: "inset(0 0 70% 0)",
        animation: "g1 4s infinite linear alternate-reverse",
      }}>{text}</span>
      <span aria-hidden style={{
        position: "absolute", inset: 0, color: GOLD, clipPath: "inset(70% 0 0 0)",
        animation: "g2 3.5s infinite linear alternate-reverse",
      }}>{text}</span>
      {text}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  NAV
 * ═══════════════════════════════════════════════════════════════ */
function Nav({ active, onAdmin }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const clickCount = useRef(0);
  const clickTimer = useRef(null);
  const { user, isSignedIn } = useUser();
  const { signOut } = useClerk();
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  // Hidden admin trigger: triple-click "MG" within 1.5s
  const handleMgClick = (e) => {
    e.stopPropagation();
    clickCount.current++;
    if (clickCount.current === 3) {
      clickCount.current = 0;
      clearTimeout(clickTimer.current);
      onAdmin();
    } else {
      clearTimeout(clickTimer.current);
      clickTimer.current = setTimeout(() => { clickCount.current = 0; }, 1500);
    }
  };

  const links = [
    { id: "about", label: "About" },
    { id: "services", label: "Services" },
    { id: "work", label: "Work" },
    { id: "terminal", label: "Command Center" },
    { id: "contact", label: "Contact" },
  ];
  const go = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); setMobileOpen(false); };

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      padding: scrolled ? "12px 36px" : "22px 36px",
      background: scrolled ? "rgba(8,8,10,0.94)" : "transparent",
      backdropFilter: scrolled ? "blur(24px) saturate(1.2)" : "none",
      borderBottom: scrolled ? "1px solid rgba(184,160,74,0.12)" : "none",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      transition: "all 0.5s cubic-bezier(0.16,1,0.3,1)",
    }}>
      <div onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} style={{
        cursor: "pointer", fontFamily: FONTS.display, fontSize: 20, fontWeight: 600,
        letterSpacing: 5, color: CREAM, userSelect: "none",
      }}>
        NAUT<span style={{ color: RUBY, fontWeight: 700 }}>X</span>
        <span onClick={handleMgClick} style={{ fontSize: 9, letterSpacing: 3, color: GOLD_DIM, marginLeft: 12, fontFamily: FONTS.mono, verticalAlign: "middle", cursor: "default" }}>MG</span>
      </div>

      <div className="nav-desk" style={{ display: "flex", alignItems: "center", gap: 32 }}>
        {links.map((l) => (
          <button key={l.id} onClick={() => go(l.id)} style={{
            background: "none", border: "none", cursor: "pointer", padding: "4px 0",
            fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 2.5,
            color: active === l.id ? GOLD : "rgba(212,205,180,0.55)",
            textTransform: "uppercase", transition: "color 0.3s",
            borderBottom: active === l.id ? `1px solid ${GOLD}` : "1px solid transparent",
          }}>{l.label}</button>
        ))}
        {isSignedIn && (
          <button onClick={() => signOut()} style={{
            background: "none", border: "1px solid rgba(155,27,48,0.25)",
            cursor: "pointer", padding: "4px 14px",
            fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2,
            color: "rgba(155,27,48,0.6)", textTransform: "uppercase",
            transition: "all 0.3s",
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(155,27,48,0.5)"; e.currentTarget.style.color = RUBY; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(155,27,48,0.25)"; e.currentTarget.style.color = "rgba(155,27,48,0.6)"; }}
          >Sign Out</button>
        )}
      </div>

      <button onClick={() => setMobileOpen(!mobileOpen)} className="nav-mob" style={{
        display: "none", background: "none", border: "none", cursor: "pointer",
        flexDirection: "column", gap: 5, padding: 8,
      }}>
        <span style={{ width: 22, height: 1.5, background: GOLD, transition: "all .3s", transform: mobileOpen ? "rotate(45deg) translate(3.5px,3.5px)" : "none" }} />
        <span style={{ width: 22, height: 1.5, background: GOLD, transition: "all .3s", opacity: mobileOpen ? 0 : 1 }} />
        <span style={{ width: 22, height: 1.5, background: GOLD, transition: "all .3s", transform: mobileOpen ? "rotate(-45deg) translate(3.5px,-3.5px)" : "none" }} />
      </button>

      {mobileOpen && (
        <div style={{
          position: "fixed", inset: 0, top: 56, background: "rgba(8,8,10,0.98)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 36, zIndex: 99,
        }}>
          {links.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} style={{
              background: "none", border: "none", cursor: "pointer",
              fontFamily: FONTS.display, fontSize: 28, letterSpacing: 6,
              color: GOLD, textTransform: "uppercase",
            }}>{l.label}</button>
          ))}
          {isSignedIn && (
            <button onClick={() => { signOut(); setMobileOpen(false); }} style={{
              background: "none", border: "1px solid rgba(155,27,48,0.3)",
              cursor: "pointer", padding: "10px 28px", marginTop: 12,
              fontFamily: FONTS.mono, fontSize: 12, letterSpacing: 3,
              color: RUBY, textTransform: "uppercase",
            }}>Sign Out</button>
          )}
        </div>
      )}
    </nav>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  HERO
 * ═══════════════════════════════════════════════════════════════ */
function Hero({ onCrypto }) {
  const [loaded, setLoaded] = useState(false);
  const time = useLiveTime();
  useEffect(() => { setTimeout(() => setLoaded(true), 300); }, []);

  const fmtTime = time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

  return (
    <section style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      justifyContent: "center", position: "relative",
      padding: "140px 36px 100px", overflow: "hidden",
    }}>
      {/* Ambient orb */}
      <div style={{
        position: "absolute", top: "15%", right: "-10%", width: "50vw", height: "50vw",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle, rgba(155,27,48,0.04) 0%, transparent 60%)",
      }} />
      <div style={{
        position: "absolute", bottom: "10%", left: "-5%", width: "40vw", height: "40vw",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle, rgba(184,160,74,0.03) 0%, transparent 55%)",
      }} />

      {/* Top-right live time */}
      <div style={{
        position: "absolute", top: 90, right: 36,
        fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 3, color: "rgba(184,160,74,0.45)",
        opacity: loaded ? 1 : 0, transition: "opacity 2s 1.5s",
      }}>
        <span style={{ color: "rgba(74,90,43,0.5)", marginRight: 8 }}>SYS</span>{fmtTime}
      </div>

      {/* Overline */}
      <div style={{
        opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(16px)",
        transition: "all 1s cubic-bezier(0.16,1,0.3,1) 0.4s",
        fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 6,
        textTransform: "uppercase", color: OLIVE_DIM, marginBottom: 28,
      }}>
        <span style={{ color: RUBY, marginRight: 8 }}>▸</span>{CONFIG.subtitle}
      </div>

      {/* Title */}
      <h1 style={{
        opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(36px)",
        transition: "all 1.2s cubic-bezier(0.16,1,0.3,1) 0.6s",
        fontFamily: FONTS.display, fontSize: "clamp(56px, 11vw, 140px)",
        fontWeight: 300, letterSpacing: "0.03em", color: CREAM,
        margin: 0, lineHeight: 0.95,
      }}>
        <Glitch text={CONFIG.name} />
      </h1>

      {/* Tagline */}
      <div style={{
        opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(24px)",
        transition: "all 1s cubic-bezier(0.16,1,0.3,1) 0.9s",
        fontFamily: FONTS.display, fontSize: "clamp(16px, 2.5vw, 24px)",
        fontWeight: 300, fontStyle: "italic", letterSpacing: 8,
        textTransform: "uppercase", color: GOLD_DIM, marginTop: 12,
      }}>{CONFIG.tagline}</div>

      {/* Accent line */}
      <div style={{
        width: loaded ? 80 : 0, height: 2,
        background: `linear-gradient(90deg, ${RUBY}, ${GOLD}, transparent)`,
        marginTop: 48, transition: "width 1.5s cubic-bezier(0.16,1,0.3,1) 1.2s",
      }} />

      {/* CTA row */}
      <div style={{
        opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(16px)",
        transition: "all 1s cubic-bezier(0.16,1,0.3,1) 1.5s",
        marginTop: 48, display: "flex", gap: 16, flexWrap: "wrap",
      }}>
        <MagBtn variant="fill" onClick={() => document.getElementById("work")?.scrollIntoView({ behavior: "smooth" })}>
          View Work <span style={{ fontSize: 16, lineHeight: 1 }}>&rarr;</span>
        </MagBtn>
        <MagBtn onClick={() => document.getElementById("terminal")?.scrollIntoView({ behavior: "smooth" })}>
          Command Center
        </MagBtn>
        <MagBtn onClick={onCrypto} style={{ borderColor: "rgba(184,160,74,0.4)", color: GOLD }}>
          Members Only
        </MagBtn>
      </div>

      {/* Scroll line */}
      <div className="scroll-indicator" style={{
        position: "absolute", bottom: 36, left: 36,
        opacity: loaded ? 1 : 0, transition: "opacity 2s 2.2s",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <div style={{ width: 1, height: 48, background: `linear-gradient(to bottom, ${GOLD_DIM}, transparent)`, animation: "scrollPulse 2.5s infinite" }} />
        <span style={{ fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 4, color: "rgba(184,160,74,0.4)", textTransform: "uppercase", writingMode: "vertical-lr" }}>Scroll</span>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  ABOUT SECTION
 * ═══════════════════════════════════════════════════════════════ */
function About() {
  const [siteStats, setSiteStats] = useState({
    projects_shipped: 2,
    active_clients: 2,
    satisfaction: 100,
  });

  // Fetch stats from Supabase + subscribe to realtime updates
  useEffect(() => {
    const loadStats = async () => {
      try {
        const { data } = await supabase.from("site_stats").select("*");
        if (data && data.length > 0) {
          const map = {};
          data.forEach(row => { map[row.key] = row.value; });
          setSiteStats(prev => ({ ...prev, ...map }));
        }
      } catch {}
    };
    loadStats();

    const channel = supabase
      .channel("stats_realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "site_stats" }, loadStats)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  return (
    <section id="about" style={{ padding: "120px 36px", maxWidth: 1200, margin: "0 auto" }}>
      <Reveal><SectionLabel text="About" num="01" /></Reveal>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80 }} className="about-grid">
        <div>
          <Reveal delay={0.1}>
            <h2 style={{
              fontFamily: FONTS.display, fontSize: "clamp(30px, 4vw, 52px)",
              fontWeight: 300, color: CREAM, lineHeight: 1.2, margin: 0,
            }}>
              I don't build websites.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <h2 style={{
              fontFamily: FONTS.display, fontSize: "clamp(30px, 4vw, 52px)",
              fontWeight: 300, fontStyle: "italic", color: GOLD, lineHeight: 1.2,
              margin: "8px 0 0",
            }}>
              I build machines<br />that print money.
            </h2>
          </Reveal>
          <Reveal delay={0.35}>
            <p style={{
              fontFamily: FONTS.body, fontSize: 15, lineHeight: 1.9,
              color: CREAM_DIM, marginTop: 32, maxWidth: 480,
            }}>
              Naut X Media Group is where design meets infrastructure. Full-stack web development,
              brand systems, and digital products built for people who take their shit seriously.
              Crypto native. Always building. If you're looking for someone who half-asses it — wrong door.
            </p>
          </Reveal>
        </div>

        {/* Stats panel — pulls live from Supabase */}
        <div>
          <Reveal delay={0.3}>
            <div style={{
              border: `1px solid rgba(184,160,74,0.16)`, background: BG_CARD,
              padding: 40, backdropFilter: "blur(12px)",
            }}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 4, color: OLIVE_DIM, textTransform: "uppercase", marginBottom: 32 }}>
                <span style={{ color: RUBY, marginRight: 6 }}>●</span> Live Stats
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
                <div>
                  <div style={{ fontFamily: FONTS.display, fontSize: 44, fontWeight: 300, color: GOLD }}>
                    <AnimNum end={siteStats.projects_shipped} key={`ps-${siteStats.projects_shipped}`} />
                  </div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, marginTop: 8, textTransform: "uppercase" }}>Projects Shipped</div>
                </div>
                <div>
                  <div style={{ fontFamily: FONTS.display, fontSize: 44, fontWeight: 300, color: CREAM }}>
                    <AnimNum end={siteStats.active_clients} key={`ac-${siteStats.active_clients}`} />
                  </div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, marginTop: 8, textTransform: "uppercase" }}>Active Clients</div>
                </div>
                <div>
                  <div style={{ fontFamily: FONTS.display, fontSize: 44, fontWeight: 300, color: RUBY }}>
                    24/7
                  </div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, marginTop: 8, textTransform: "uppercase" }}>Uptime</div>
                </div>
                <div>
                  <div style={{ fontFamily: FONTS.display, fontSize: 44, fontWeight: 300, color: OLIVE }}>
                    <AnimNum end={siteStats.satisfaction} suffix="%" key={`sat-${siteStats.satisfaction}`} />
                  </div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, marginTop: 8, textTransform: "uppercase" }}>Satisfaction</div>
                </div>
              </div>

              {/* Live pulse indicator */}
              <div style={{ marginTop: 28, display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 6, height: 6, borderRadius: "50%", background: "#4caf50",
                  animation: "livePulse 2s infinite", boxShadow: "0 0 8px rgba(76,175,80,0.5)",
                }} />
                <span style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 2, color: "rgba(76,175,80,0.8)" }}>SYSTEMS ONLINE</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  SERVICES
 * ═══════════════════════════════════════════════════════════════ */
function Services() {
  const [hov, setHov] = useState(-1);

  return (
    <section id="services" style={{ padding: "100px 36px 120px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Reveal><SectionLabel text="Services" num="02" /></Reveal>

        {CONFIG.services.map((s, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div
              onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(-1)}
              style={{
                display: "grid", gridTemplateColumns: "48px 1fr auto", alignItems: "center", gap: 28,
                padding: "32px 0", borderBottom: "1px solid rgba(184,160,74,0.12)",
                cursor: "default", transition: "all 0.5s cubic-bezier(0.16,1,0.3,1)",
                transform: hov === i ? "translateX(16px)" : "none",
                opacity: hov !== -1 && hov !== i ? 0.35 : 1,
              }}
            >
              <span style={{
                fontFamily: FONTS.mono, fontSize: 12, color: hov === i ? RUBY : "rgba(155,27,48,0.55)",
                transition: "color 0.4s", letterSpacing: 2,
              }}>{s.num}</span>
              <div>
                <h3 style={{
                  fontFamily: FONTS.display, fontSize: "clamp(22px, 3vw, 34px)",
                  fontWeight: 400, color: hov === i ? CREAM : "rgba(212,205,180,0.75)",
                  margin: "0 0 6px", transition: "color 0.4s",
                }}>{s.title}</h3>
                <p style={{
                  fontFamily: FONTS.body, fontSize: 14, lineHeight: 1.7,
                  color: "rgba(212,205,180,0.6)", margin: 0, maxWidth: 540,
                  opacity: hov === i ? 1 : 0.75, transition: "opacity 0.4s",
                }}>{s.desc}</p>
              </div>
              <div style={{
                fontFamily: FONTS.display, fontSize: 28, color: GOLD_DIM,
                transform: hov === i ? "translateX(8px) rotate(-45deg)" : "none",
                transition: "all 0.5s cubic-bezier(0.16,1,0.3,1)",
                opacity: hov === i ? 1 : 0.35,
              }}>↗</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  WORK / PROJECTS
 * ═══════════════════════════════════════════════════════════════ */
function Work() {
  const [hovP, setHovP] = useState(-1);
  const [projects, setProjects] = useState(CONFIG.projects);

  // Fetch projects from Supabase + subscribe to realtime updates
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const { data } = await supabase.from("projects").select("*").order("sort_order");
        if (data && data.length > 0) {
          // Transform Supabase format to component format
          const mapped = data.map(p => ({
            title: p.title,
            type: p.type,
            desc: p.description,
            tech: typeof p.tech === "string" ? p.tech.split(",").map(t => t.trim()) : (p.tech || []),
            accent: p.accent_color || GOLD,
            year: p.year,
            status: p.status,
            url: p.url,
          }));
          setProjects(mapped);
        }
      } catch {}
    };
    loadProjects();

    // Real-time subscription - updates instantly when admin edits
    const channel = supabase
      .channel("projects_realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "projects" }, loadProjects)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  return (
    <section id="work" style={{ padding: "100px 36px 120px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Reveal><SectionLabel text="Featured Work" num="03" /></Reveal>

        <div className="projects-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 28 }}>
          {projects.map((p, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div
                onMouseEnter={() => setHovP(i)} onMouseLeave={() => setHovP(-1)}
                style={{
                  position: "relative", padding: "44px 36px", overflow: "hidden",
                  border: `1px solid ${hovP === i ? "rgba(184,160,74,0.25)" : "rgba(184,160,74,0.12)"}`,
                  background: BG_CARD, backdropFilter: "blur(10px)",
                  transition: "all 0.6s cubic-bezier(0.16,1,0.3,1)",
                  transform: hovP === i ? "translateY(-6px)" : "none",
                }}
              >
                {/* Top accent bar */}
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 2,
                  background: `linear-gradient(90deg, ${p.accent}, transparent)`,
                  opacity: hovP === i ? 1 : 0.5, transition: "opacity 0.4s",
                }} />

                {/* Header row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                  <div>
                    <div style={{
                      fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3,
                      textTransform: "uppercase", color: p.accent, marginBottom: 8, fontWeight: 500,
                    }}>{p.type}</div>
                    <h3 style={{
                      fontFamily: FONTS.display, fontSize: 28, fontWeight: 400, color: CREAM, margin: 0,
                    }}>{p.title}</h3>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2 }}>{p.year}</div>
                    {(() => {
                      const isOffline = p.status === "OFFLINE";
                      const statusColor = isOffline ? "#9b1b30" : "#4caf50";
                      const statusBorder = isOffline ? "rgba(155,27,48,0.35)" : "rgba(76,175,80,0.35)";
                      return (
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: 5, marginTop: 6,
                          padding: "3px 10px", border: `1px solid ${statusBorder}`,
                          fontSize: 10, fontFamily: FONTS.mono, letterSpacing: 2, color: statusColor,
                        }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: statusColor, animation: "livePulse 2s infinite" }} />
                          {p.status}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <p style={{
                  fontFamily: FONTS.body, fontSize: 14, lineHeight: 1.75,
                  color: "rgba(212,205,180,0.65)", margin: "0 0 24px",
                }}>{p.desc}</p>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {p.tech.map((t, j) => (
                    <span key={j} style={{
                      padding: "5px 12px", fontSize: 10, letterSpacing: 2,
                      fontFamily: FONTS.mono, textTransform: "uppercase",
                      border: "1px solid rgba(184,160,74,0.2)", color: GOLD_DIM,
                    }}>{t}</span>
                  ))}
                </div>

                {p.url && (
                  <a href={p.url} target="_blank" rel="noopener noreferrer" style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    marginTop: 18, padding: "12px 24px", textDecoration: "none",
                    border: `1px solid ${p.accent}40`,
                    background: `${p.accent}10`,
                    color: p.accent === RUBY ? "#ff6b7a" : GOLD,
                    fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 3,
                    textTransform: "uppercase", transition: "all 0.3s",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${p.accent}25`; e.currentTarget.style.borderColor = p.accent; }}
                    onMouseLeave={e => { e.currentTarget.style.background = `${p.accent}10`; e.currentTarget.style.borderColor = `${p.accent}40`; }}
                  >
                    Visit Site <span style={{ fontSize: 14 }}>&#8599;</span>
                  </a>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  TERMINAL — LIVE COMMAND CENTER
 * ═══════════════════════════════════════════════════════════════ */

// Stock rotation groups - cycles every 60 seconds
const STOCK_GROUPS = [
  { label: "MAJOR INDICES", symbols: [
    { symbol: "SPY", name: "S&P 500 ETF", ticker: "SPY" },
    { symbol: "QQQ", name: "NASDAQ 100 ETF", ticker: "QQQ" },
    { symbol: "DIA", name: "Dow Jones ETF", ticker: "DIA" },
    { symbol: "IWM", name: "Russell 2000 ETF", ticker: "IWM" },
    { symbol: "VOO", name: "Vanguard S&P 500", ticker: "VOO" },
    { symbol: "VTI", name: "Total Market ETF", ticker: "VTI" },
  ]},
  { label: "BIG TECH", symbols: [
    { symbol: "AAPL", name: "Apple", ticker: "AAPL" },
    { symbol: "MSFT", name: "Microsoft", ticker: "MSFT" },
    { symbol: "GOOGL", name: "Alphabet", ticker: "GOOGL" },
    { symbol: "AMZN", name: "Amazon", ticker: "AMZN" },
    { symbol: "META", name: "Meta", ticker: "META" },
    { symbol: "NVDA", name: "NVIDIA", ticker: "NVDA" },
  ]},
  { label: "GROWTH & CRYPTO", symbols: [
    { symbol: "TSLA", name: "Tesla", ticker: "TSLA" },
    { symbol: "AMD", name: "AMD", ticker: "AMD" },
    { symbol: "PLTR", name: "Palantir", ticker: "PLTR" },
    { symbol: "COIN", name: "Coinbase", ticker: "COIN" },
    { symbol: "SQ", name: "Block (Square)", ticker: "SQ" },
    { symbol: "MSTR", name: "MicroStrategy", ticker: "MSTR" },
  ]},
  { label: "ETFs & FUNDS", symbols: [
    { symbol: "ARKK", name: "ARK Innovation", ticker: "ARKK" },
    { symbol: "XLF", name: "Financials ETF", ticker: "XLF" },
    { symbol: "XLE", name: "Energy ETF", ticker: "XLE" },
    { symbol: "GLD", name: "Gold ETF", ticker: "GLD" },
    { symbol: "SLV", name: "Silver ETF", ticker: "SLV" },
    { symbol: "BITX", name: "2x Bitcoin ETF", ticker: "BITX" },
  ]},
  { label: "ENERGY & FINANCE", symbols: [
    { symbol: "XOM", name: "ExxonMobil", ticker: "XOM" },
    { symbol: "CVX", name: "Chevron", ticker: "CVX" },
    { symbol: "JPM", name: "JPMorgan", ticker: "JPM" },
    { symbol: "GS", name: "Goldman Sachs", ticker: "GS" },
    { symbol: "BAC", name: "Bank of America", ticker: "BAC" },
    { symbol: "V", name: "Visa", ticker: "V" },
  ]},
];

function Terminal() {
  const [crypto, setCrypto] = useState([]);
  const [fear, setFear] = useState(null);
  const [forex, setForex] = useState(null);
  const [stocks, setStocks] = useState({});
  const [socialStats, setSocialStats] = useState(null);
  const [stockGroupIdx, setStockGroupIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshPulse, setRefreshPulse] = useState(false);

  // New feature states
  const [orderBook, setOrderBook] = useState({ bids: [], asks: [], symbol: "BTCUSDT" });
  const [obSymbol, setObSymbol] = useState("BTCUSDT");
  const [trending, setTrending] = useState([]);
  const [topGainers, setTopGainers] = useState([]);
  const [whales, setWhales] = useState([]);
  const [nftCollections, setNftCollections] = useState([]);
  const [macro, setMacro] = useState({ dxy: null, fedRate: null, cpi: null, vix: null });
  const [news, setNews] = useState([]);
  const [watchlist, setWatchlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nxmg-watchlist") || "[]"); } catch { return []; }
  });
  const [watchlistData, setWatchlistData] = useState([]);
  const [portfolioAddr, setPortfolioAddr] = useState(() => localStorage.getItem("nxmg-wallet") || "");
  const [portfolioData, setPortfolioData] = useState(null);
  const [alerts, setAlerts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nxmg-alerts") || "[]"); } catch { return []; }
  });
  const [priceFlash, setPriceFlash] = useState({}); // { btc: 'up'|'down' }
  const prevPrices = useRef({});
  const wsRef = useRef(null);
  const [tab, setTab] = useState("crypto");
  const time = useLiveTime();

  // Rotate stock groups every 60 seconds
  useEffect(() => {
    const rotateInterval = setInterval(() => {
      setStockGroupIdx(prev => (prev + 1) % STOCK_GROUPS.length);
    }, 30000);
    return () => clearInterval(rotateInterval);
  }, [STOCK_GROUPS.length]);

  // Fetch stocks when group changes
  const fetchStocks = useCallback(async (groupIdx) => {
    try {
      const group = STOCK_GROUPS[groupIdx];
      const stockData = {};
      await Promise.all(group.symbols.map(async (s) => {
        try {
          const r = await fetch(`/api/finnhub?symbol=${encodeURIComponent(s.symbol)}`);
          const d = await r.json();
          if (d && d.c) stockData[s.ticker] = { price: d.c, change: d.dp, name: s.name, prev: d.pc };
        } catch {}
      }));
      setStocks(stockData);
    } catch {}
  }, []);

  useEffect(() => { fetchStocks(stockGroupIdx); }, [stockGroupIdx, fetchStocks]);

  // Fetch all other data (runs every 15s)
  const fetchData = useCallback(async () => {
    try {
      // Crypto prices
      const res = await fetch(
        "https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=bitcoin,ethereum,solana,dogecoin,ripple,cardano,polkadot,chainlink,avalanche-2,litecoin&order=market_cap_desc&sparkline=false&price_change_percentage=1h,24h,7d"
      );
      if (!res.ok) throw new Error("API error");
      const data = await res.json();
      setCrypto(data);

      // Fear and Greed
      try {
        const fRes = await fetch("https://api.alternative.me/fng/?limit=1");
        const fData = await fRes.json();
        if (fData?.data?.[0]) setFear(fData.data[0]);
      } catch {}

      // Forex rates (free, no key)
      try {
        const fxRes = await fetch("https://open.er-api.com/v6/latest/USD");
        const fxData = await fxRes.json();
        if (fxData?.rates) setForex(fxData.rates);
      } catch {}

      // Social media stats from Supabase
      try {
        const { data: socData } = await supabase.from("social_stats").select("*").order("platform");
        if (socData && socData.length > 0) {
          const mapped = {};
          socData.forEach(row => { mapped[row.platform] = row; });
          setSocialStats(mapped);
        }
      } catch {}

      setLoading(false);
      setError(false);
      setLastRefresh(new Date());
      setRefreshPulse(true);
      setTimeout(() => setRefreshPulse(false), 1000);
    } catch {
      setError(true);
      setLoading(false);
    }
  }, []);

  // Refresh stocks on every data cycle too
  const fetchAll = useCallback(() => {
    fetchData();
    fetchStocks(stockGroupIdx);
  }, [fetchData, fetchStocks, stockGroupIdx]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 15000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  // Binance WebSocket - real-time prices for BTC, ETH, SOL
  useEffect(() => {
    const ws = new WebSocket("wss://stream.binance.com:9443/stream?streams=btcusdt@ticker/ethusdt@ticker/solusdt@ticker");
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        const ticker = msg.data;
        if (!ticker || !ticker.s) return;
        const symbol = ticker.s.toLowerCase().replace("usdt", "");
        const newPrice = parseFloat(ticker.c);
        const oldPrice = prevPrices.current[symbol];

        if (oldPrice && newPrice !== oldPrice) {
          const direction = newPrice > oldPrice ? "up" : "down";
          setPriceFlash(prev => ({ ...prev, [symbol]: direction }));
          setTimeout(() => setPriceFlash(prev => ({ ...prev, [symbol]: null })), 600);
        }
        prevPrices.current[symbol] = newPrice;

        // Update crypto state with live price
        setCrypto(prev => prev.map(c => {
          const map = { btc: "bitcoin", eth: "ethereum", sol: "solana" };
          if (c.id === map[symbol]) {
            return { ...c, current_price: newPrice, price_change_percentage_24h: parseFloat(ticker.P) };
          }
          return c;
        }));

        // Check price alerts
        setAlerts(prevAlerts => {
          const updated = prevAlerts.map(a => {
            if (a.symbol === symbol && !a.triggered) {
              if ((a.direction === "above" && newPrice >= a.target) ||
                  (a.direction === "below" && newPrice <= a.target)) {
                if ("Notification" in window && Notification.permission === "granted") {
                  new Notification(`${symbol.toUpperCase()} alert`, { body: `Hit $${newPrice.toFixed(2)}` });
                }
                return { ...a, triggered: true };
              }
            }
            return a;
          });
          localStorage.setItem("nxmg-alerts", JSON.stringify(updated));
          return updated;
        });
      } catch {}
    };

    return () => { ws.close(); };
  }, []);

  // Order Book - REST snapshot + WebSocket updates
  useEffect(() => {
    let ws;
    let cancelled = false;

    // Fetch initial snapshot via REST
    const fetchSnapshot = async () => {
      try {
        const r = await fetch(`https://api.binance.com/api/v3/depth?symbol=${obSymbol}&limit=20`);
        const d = await r.json();
        if (cancelled) return;
        if (d.bids && d.asks) {
          setOrderBook({
            symbol: obSymbol,
            bids: d.bids.slice(0, 15).map(b => ({ price: parseFloat(b[0]), qty: parseFloat(b[1]) })),
            asks: d.asks.slice(0, 15).map(a => ({ price: parseFloat(a[0]), qty: parseFloat(a[1]) })),
          });
        }
      } catch (e) { console.log("Order book snapshot error:", e); }
    };

    fetchSnapshot();

    // Then connect WebSocket for live updates
    try {
      ws = new WebSocket(`wss://stream.binance.com:9443/ws/${obSymbol.toLowerCase()}@depth20@1000ms`);
      ws.onmessage = (event) => {
        if (cancelled) return;
        try {
          const data = JSON.parse(event.data);
          if (data.bids && data.asks) {
            setOrderBook({
              symbol: obSymbol,
              bids: data.bids.slice(0, 15).map(b => ({ price: parseFloat(b[0]), qty: parseFloat(b[1]) })),
              asks: data.asks.slice(0, 15).map(a => ({ price: parseFloat(a[0]), qty: parseFloat(a[1]) })),
            });
          }
        } catch {}
      };
      ws.onerror = (e) => console.log("Order book WS error:", e);
    } catch (e) { console.log("Order book WS init error:", e); }

    return () => {
      cancelled = true;
      if (ws) ws.close();
    };
  }, [obSymbol]);

  // Trending coins from CoinGecko
  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const r = await fetch("https://api.coingecko.com/api/v3/search/trending");
        const d = await r.json();
        if (d.coins) setTrending(d.coins.slice(0, 10));
      } catch {}

      try {
        const r2 = await fetch("https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=100&page=1&price_change_percentage=24h");
        const d2 = await r2.json();
        if (Array.isArray(d2)) {
          const sorted = [...d2].filter(c => c.price_change_percentage_24h != null).sort((a, b) => b.price_change_percentage_24h - a.price_change_percentage_24h).slice(0, 10);
          setTopGainers(sorted);
        }
      } catch {}
    };
    fetchTrending();
    const i = setInterval(fetchTrending, 120000);
    return () => clearInterval(i);
  }, []);

  // NFT Floor Prices - use known collection IDs
  useEffect(() => {
    const fetchNfts = async () => {
      const ids = ["bored-ape-yacht-club", "cryptopunks", "pudgy-penguins", "azuki", "mutant-ape-yacht-club", "doodles-official", "moonbirds", "cool-cats-nft"];
      try {
        const detailed = await Promise.all(ids.map(async (id) => {
          try {
            const dr = await fetch(`https://api.coingecko.com/api/v3/nfts/${id}`);
            if (!dr.ok) return null;
            return await dr.json();
          } catch { return null; }
        }));
        setNftCollections(detailed.filter(Boolean));
      } catch {}
    };
    fetchNfts();
    const i = setInterval(fetchNfts, 300000);
    return () => clearInterval(i);
  }, []);

  // Whale BUYS/SELLS via Binance trade WebSocket - BTC only, lower threshold for more activity
  useEffect(() => {
    const MIN_USD = 100000; // $100K+ BTC trades
    const ws = new WebSocket("wss://stream.binance.com:9443/ws/btcusdt@aggTrade");

    ws.onmessage = (event) => {
      try {
        const trade = JSON.parse(event.data);
        if (!trade || !trade.p) return;

        const price = parseFloat(trade.p);
        const qty = parseFloat(trade.q);
        const usdValue = price * qty;

        if (usdValue >= MIN_USD) {
          const whale = {
            chain: "BTC",
            color: "#f7931a",
            hash: String(trade.a),
            value: qty,
            price,
            usdValue,
            side: trade.m ? "SELL" : "BUY",
            time: trade.T / 1000,
            explorer: "https://www.binance.com/en/trade/BTC_USDT",
          };
          setWhales(prev => [whale, ...prev].slice(0, 50));
        }
      } catch {}
    };

    ws.onerror = (e) => console.log("Whale WS error:", e);

    return () => { ws.close(); };
  }, []);

  // Macro data: DXY, VIX, 10Y Treasury via Twelve Data (supports indices)
  useEffect(() => {
    const fetchMacro = async () => {
      try {
        const symbols = [
          { key: "dxy", symbol: "DXY" },
          { key: "vix", symbol: "VIX" },
          { key: "tnx", symbol: "TNX" },
        ];
        const data = {};
        await Promise.all(symbols.map(async (s) => {
          try {
            const r = await fetch(`/api/twelvedata?symbol=${encodeURIComponent(s.symbol)}`);
            const d = await r.json();
            if (d && d.close) {
              data[s.key] = {
                price: parseFloat(d.close),
                change: parseFloat(d.percent_change) || 0,
              };
            }
          } catch {}
        }));
        // Fed funds rate and CPI (latest known values)
        data.fedRate = 4.25;
        data.cpi = 3.2;
        setMacro(prev => ({ ...prev, ...data }));
      } catch {}
    };
    fetchMacro();
    const i = setInterval(fetchMacro, 120000);
    return () => clearInterval(i);
  }, []);

  // News from CryptoCompare (free, CORS-friendly, no key)
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const r = await fetch("https://min-api.cryptocompare.com/data/v2/news/?lang=EN");
        const d = await r.json();
        if (d.Data) {
          setNews(d.Data.slice(0, 15).map(n => ({
            title: n.title,
            description: n.body,
            url: n.url,
            author: n.source,
            updated_at: new Date(n.published_on * 1000).toISOString(),
          })));
        }
      } catch {}
    };
    fetchNews();
    const i = setInterval(fetchNews, 300000);
    return () => clearInterval(i);
  }, []);

  // Watchlist data
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  useEffect(() => {
    if (watchlist.length === 0) { setWatchlistData([]); return; }
    let cancelled = false;
    const fetchWatchlist = async () => {
      setWatchlistLoading(true);
      try {
        const ids = watchlist.join(",");
        const r = await fetch(`https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${ids}&per_page=250`);
        const d = await r.json();
        if (!cancelled && Array.isArray(d)) setWatchlistData(d);
      } catch {}
      if (!cancelled) setWatchlistLoading(false);
    };
    fetchWatchlist();
    const i = setInterval(fetchWatchlist, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [watchlist]);

  // Save watchlist to localStorage when changed
  useEffect(() => {
    localStorage.setItem("nxmg-watchlist", JSON.stringify(watchlist));
  }, [watchlist]);

  // Halving countdown
  const halvingDate = new Date("2028-03-26T00:00:00Z");
  const daysToHalving = Math.ceil((halvingDate - new Date()) / (1000 * 60 * 60 * 24));

  // Watchlist helpers
  const addToWatchlist = (id) => { if (!watchlist.includes(id)) setWatchlist([...watchlist, id]); };
  const removeFromWatchlist = (id) => setWatchlist(watchlist.filter(w => w !== id));

  // Alert helpers
  const addAlert = (symbol, target, direction) => {
    const newAlert = { id: Date.now(), symbol, target: parseFloat(target), direction, triggered: false };
    const updated = [...alerts, newAlert];
    setAlerts(updated);
    localStorage.setItem("nxmg-alerts", JSON.stringify(updated));
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
  };
  const removeAlert = (id) => {
    const updated = alerts.filter(a => a.id !== id);
    setAlerts(updated);
    localStorage.setItem("nxmg-alerts", JSON.stringify(updated));
  };

  // Portfolio fetch (Etherscan-style address - basic ETH balance)
  const fetchPortfolio = async () => {
    if (!portfolioAddr) return;
    const addr = portfolioAddr.trim();
    // Validate address format: Solana (base58, 32-44 chars) OR Ethereum (0x + 40 hex)
    const isSol = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr) && !addr.startsWith("0x");
    const isEth = /^0x[a-fA-F0-9]{40}$/.test(addr);
    if (!isSol && !isEth) return; // silently reject malformed addresses
    localStorage.setItem("nxmg-wallet", addr);
    try {
      const safeAddr = encodeURIComponent(addr);
      if (isSol) {
        const r = await fetch(`https://public-api.solscan.io/account/${safeAddr}`);
        const d = await r.json();
        setPortfolioData({ chain: "Solana", balance: (d.lamports || 0) / 1e9, address: addr });
      } else {
        const r = await fetch(`https://api.ethplorer.io/getAddressInfo/${safeAddr}?apiKey=freekey`);
        const d = await r.json();
        setPortfolioData({
          chain: "Ethereum",
          balance: d.ETH?.balance || 0,
          balanceUsd: (d.ETH?.balance || 0) * (d.ETH?.price?.rate || 0),
          tokens: d.tokens || [],
          address: addr,
        });
      }
    } catch {}
  };

  // Auto-load portfolio if address saved
  useEffect(() => { if (portfolioAddr) fetchPortfolio(); /* eslint-disable-next-line */ }, []);

  const fmtPrice = (n) => {
    if (n >= 1000) return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (n >= 1) return "$" + n.toFixed(2);
    return "$" + n.toFixed(4);
  };
  const fmtCap = (n) => {
    if (n >= 1e12) return "$" + (n / 1e12).toFixed(2) + "T";
    if (n >= 1e9) return "$" + (n / 1e9).toFixed(1) + "B";
    return "$" + (n / 1e6).toFixed(0) + "M";
  };
  const fmtPct = (n) => {
    if (n == null) return "—";
    const s = n.toFixed(2);
    return n >= 0 ? `+${s}%` : `${s}%`;
  };
  const pctColor = (n) => n == null ? CREAM_DIM : n >= 0 ? "#4caf50" : "#ef5350";
  const fmtNum = (n) => {
    if (n == null || n === 0) return "0";
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
    return n.toLocaleString();
  };

  const fearColor = (v) => {
    if (v <= 25) return "#ef5350";
    if (v <= 45) return "#ff9800";
    if (v <= 55) return "#ffeb3b";
    if (v <= 75) return "#8bc34a";
    return "#4caf50";
  };

  const tabs = [
    { id: "crypto", label: "Crypto" },
    { id: "orderbook", label: "Book" },
    { id: "trending", label: "Trending" },
    { id: "whales", label: "Whales" },
    { id: "nft", label: "NFT" },
    { id: "macro", label: "Macro" },
    { id: "events", label: "Events" },
    { id: "news", label: "News" },
    { id: "watchlist", label: "Watch" },
    { id: "portfolio", label: "Wallet" },
    { id: "markets", label: "Stocks" },
    { id: "social", label: "Social" },
    { id: "global", label: "Global" },
  ];

  return (
    <section id="terminal" style={{ padding: "100px 36px 120px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Reveal><SectionLabel text="Command Center" num="04" /></Reveal>

        <Reveal delay={0.1}>
          <div style={{
            border: "1px solid rgba(184,160,74,0.16)", background: "rgba(10,10,8,0.95)",
            backdropFilter: "blur(16px)", overflow: "hidden",
          }}>
            {/* Terminal header bar */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "14px 24px", borderBottom: "1px solid rgba(184,160,74,0.12)",
              background: "rgba(14,13,11,0.8)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#ef5350" }} />
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#ffeb3b" }} />
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#4caf50" }} />
                <span style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 2, color: CREAM_DIM, marginLeft: 12 }}>
                  NAUT_X://COMMAND_CENTER
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: refreshPulse ? "#4caf50" : "rgba(76,175,80,0.3)",
                  boxShadow: refreshPulse ? "0 0 10px rgba(76,175,80,0.8)" : "none",
                  transition: "all 0.3s",
                }} />
                <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: "rgba(184,160,74,0.5)", letterSpacing: 2 }}>
                  {time.toLocaleTimeString([], { hour12: false })}
                </span>
              </div>
            </div>

            {/* Tab bar */}
            <div className="terminal-tabs" style={{
              display: "flex", borderBottom: "1px solid rgba(184,160,74,0.12)",
              background: "rgba(14,13,11,0.6)",
              overflowX: "auto", overflowY: "hidden",
              scrollbarWidth: "none", msOverflowStyle: "none",
            }}>
              {tabs.map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)} style={{
                  background: tab === t.id ? "rgba(184,160,74,0.08)" : "transparent",
                  border: "none", borderBottom: tab === t.id ? `2px solid ${GOLD}` : "2px solid transparent",
                  padding: "12px 16px", cursor: "pointer",
                  fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 1.5,
                  color: tab === t.id ? GOLD : CREAM_DIM, textTransform: "uppercase",
                  transition: "all 0.3s", whiteSpace: "nowrap", flexShrink: 0,
                }}>{t.label}</button>
              ))}
            </div>

            {/* Content */}
            <div style={{ padding: "28px 24px", minHeight: 340 }}>
              {loading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 280, flexDirection: "column", gap: 16 }}>
                  <div style={{
                    width: 24, height: 24, border: `2px solid ${GOLD_DIM}`, borderTop: `2px solid ${GOLD}`,
                    borderRadius: "50%", animation: "spin 1s linear infinite",
                  }} />
                  <span style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: GOLD_DIM }}>FETCHING LIVE DATA...</span>
                </div>
              ) : tab === "crypto" ? (
                error ? (
                  <div style={{ textAlign: "center", padding: 60 }}>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 12, color: "#ef5350", marginBottom: 12 }}>CONNECTION ERROR</div>
                    <div style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM_DIM, marginBottom: 20 }}>Unable to reach CoinGecko API. Will retry automatically.</div>
                    <MagBtn onClick={fetchData}>Retry</MagBtn>
                  </div>
                ) : (
                <>
                  {/* Crypto price table */}
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONTS.mono, fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid rgba(184,160,74,0.15)" }}>
                          {["Asset", "Price", "1h", "24h", "7d", "Market Cap", "Volume (24h)"].map((h) => (
                            <th key={h} style={{
                              padding: "10px 12px", textAlign: h === "Asset" ? "left" : "right",
                              fontSize: 10, letterSpacing: 2, color: CREAM_DIM, textTransform: "uppercase", fontWeight: 500,
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {crypto.map((c) => (
                          <tr key={c.id} style={{
                            borderBottom: "1px solid rgba(184,160,74,0.07)",
                            transition: "background 0.2s",
                          }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(184,160,74,0.06)"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                          >
                            <td style={{ padding: "14px 12px", display: "flex", alignItems: "center", gap: 10 }}>
                              <img src={c.image} alt="" style={{ width: 22, height: 22, borderRadius: 4 }} />
                              <div>
                                <div style={{ color: CREAM, fontWeight: 500, fontSize: 13 }}>{c.symbol?.toUpperCase()}</div>
                                <div style={{ color: CREAM_DIM, fontSize: 10, letterSpacing: 1 }}>{c.name}</div>
                              </div>
                            </td>
                            <td style={{
                              padding: "14px 12px", textAlign: "right", color: CREAM, fontWeight: 500,
                              transition: "background 0.6s, color 0.3s",
                              background: priceFlash[c.symbol] === "up" ? "rgba(76,175,80,0.18)" :
                                          priceFlash[c.symbol] === "down" ? "rgba(239,83,80,0.18)" : "transparent",
                            }}>{fmtPrice(c.current_price)}</td>
                            <td style={{ padding: "14px 12px", textAlign: "right", color: pctColor(c.price_change_percentage_1h_in_currency) }}>{fmtPct(c.price_change_percentage_1h_in_currency)}</td>
                            <td style={{ padding: "14px 12px", textAlign: "right", color: pctColor(c.price_change_percentage_24h) }}>{fmtPct(c.price_change_percentage_24h)}</td>
                            <td style={{ padding: "14px 12px", textAlign: "right", color: pctColor(c.price_change_percentage_7d_in_currency) }}>{fmtPct(c.price_change_percentage_7d_in_currency)}</td>
                            <td style={{ padding: "14px 12px", textAlign: "right", color: CREAM_DIM }}>{fmtCap(c.market_cap)}</td>
                            <td style={{ padding: "14px 12px", textAlign: "right", color: CREAM_DIM }}>{fmtCap(c.total_volume)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Bottom status bar */}
                  <div style={{
                    marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center",
                    paddingTop: 16, borderTop: "1px solid rgba(184,160,74,0.12)", flexWrap: "wrap", gap: 12,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{
                        width: 6, height: 6, borderRadius: "50%",
                        background: refreshPulse ? "#fff" : "#4caf50",
                        boxShadow: refreshPulse ? "0 0 12px #4caf50" : "none",
                        animation: "livePulse 2s infinite",
                        transition: "all 0.3s",
                      }} />
                      <span style={{
                        fontFamily: FONTS.mono, fontSize: 10,
                        color: refreshPulse ? "#4caf50" : "rgba(76,175,80,0.8)",
                        letterSpacing: 2, transition: "color 0.3s",
                      }}>
                        {refreshPulse ? "REFRESHING..." : "LIVE"}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2 }}>
                        {lastRefresh ? `LAST UPDATE: ${lastRefresh.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : ""}
                      </span>
                      <button onClick={fetchAll} style={{
                        background: "none", border: "1px solid rgba(184,160,74,0.2)",
                        color: GOLD_DIM, cursor: "pointer",
                        fontFamily: FONTS.mono, fontSize: 9, padding: "4px 12px",
                        letterSpacing: 2, transition: "all 0.3s",
                      }}>REFRESH</button>
                    </div>
                  </div>
                </>
                )
              ) : tab === "markets" ? (
                /* Forex & Traditional Assets Tab */
                <div>
                  {/* Forex Rates */}
                  {forex && (
                    <>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, textTransform: "uppercase", marginBottom: 16 }}>
                        Live Exchange Rates (USD Base)
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 32 }}>
                        {[
                          { code: "EUR", name: "Euro", flag: "\u20AC" },
                          { code: "GBP", name: "British Pound", flag: "\u00A3" },
                          { code: "JPY", name: "Japanese Yen", flag: "\u00A5" },
                          { code: "CHF", name: "Swiss Franc", flag: "Fr" },
                          { code: "CAD", name: "Canadian Dollar", flag: "C$" },
                          { code: "AUD", name: "Australian Dollar", flag: "A$" },
                          { code: "CNY", name: "Chinese Yuan", flag: "\u00A5" },
                          { code: "MXN", name: "Mexican Peso", flag: "MX$" },
                          { code: "BRL", name: "Brazilian Real", flag: "R$" },
                          { code: "INR", name: "Indian Rupee", flag: "\u20B9" },
                          { code: "KRW", name: "South Korean Won", flag: "\u20A9" },
                          { code: "SGD", name: "Singapore Dollar", flag: "S$" },
                        ].map((cur) => (
                          <div key={cur.code} style={{
                            border: "1px solid rgba(184,160,74,0.12)", padding: "16px 18px",
                            background: "rgba(14,13,11,0.8)",
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                              <span style={{ fontFamily: FONTS.mono, fontSize: 13, color: CREAM, fontWeight: 500 }}>{cur.code}</span>
                              <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: GOLD_DIM }}>{cur.flag}</span>
                            </div>
                            <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: GOLD, fontWeight: 500 }}>
                              {forex[cur.code] ? (cur.code === "JPY" || cur.code === "KRW" ? forex[cur.code].toFixed(2) : forex[cur.code].toFixed(4)) : "N/A"}
                            </div>
                            <div style={{ fontFamily: FONTS.body, fontSize: 10, color: CREAM_DIM, marginTop: 4 }}>{cur.name}</div>
                          </div>
                        ))}
                      </div>

                      {/* Live Stock Data via Finnhub - Rotates every 60s */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, textTransform: "uppercase" }}>
                          {STOCK_GROUPS[stockGroupIdx]?.label || "Stocks"} {Object.keys(stocks).length > 0 ? "" : "(Loading...)"}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {/* Group dots */}
                          <div style={{ display: "flex", gap: 4 }}>
                            {STOCK_GROUPS.map((_, idx) => (
                              <button key={idx} onClick={() => setStockGroupIdx(idx)} style={{
                                width: idx === stockGroupIdx ? 16 : 6, height: 6, borderRadius: 3, border: "none",
                                background: idx === stockGroupIdx ? GOLD : "rgba(184,160,74,0.2)",
                                cursor: "pointer", transition: "all 0.3s", padding: 0,
                              }} />
                            ))}
                          </div>
                          {/* Next button */}
                          <button onClick={() => setStockGroupIdx(prev => (prev + 1) % STOCK_GROUPS.length)} style={{
                            background: "none", border: "1px solid rgba(184,160,74,0.15)", cursor: "pointer",
                            fontFamily: FONTS.mono, fontSize: 9, color: GOLD_DIM, padding: "4px 10px",
                            letterSpacing: 2, transition: "all 0.3s",
                          }}>NEXT &#8250;</button>
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
                        {Object.keys(stocks).length > 0 ? Object.entries(stocks).map(([ticker, s]) => (
                          <div key={ticker} style={{
                            border: "1px solid rgba(184,160,74,0.12)", padding: "18px 20px",
                            background: "rgba(14,13,11,0.8)", animation: "fadeIn 0.4s ease",
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                              <span style={{ fontFamily: FONTS.mono, fontSize: 13, color: CREAM, fontWeight: 500 }}>{s.name}</span>
                              <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD_DIM, letterSpacing: 1 }}>{ticker}</span>
                            </div>
                            <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: GOLD, fontWeight: 500 }}>
                              ${s.price?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            <div style={{
                              fontFamily: FONTS.mono, fontSize: 12, marginTop: 6,
                              color: s.change >= 0 ? "#4caf50" : "#ef5350",
                            }}>
                              {s.change >= 0 ? "+" : ""}{s.change?.toFixed(2)}%
                            </div>
                          </div>
                        )) : (
                          [1,2,3,4,5,6].map(t => (
                            <div key={t} style={{
                              border: "1px solid rgba(184,160,74,0.08)", padding: "18px 20px",
                              background: "rgba(14,13,11,0.8)", textAlign: "center", height: 100,
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <div style={{
                                width: 16, height: 16, border: `2px solid ${GOLD_DIM}`, borderTop: `2px solid ${GOLD}`,
                                borderRadius: "50%", animation: "spin 1s linear infinite",
                              }} />
                            </div>
                          ))
                        )}
                      </div>

                      {/* Status bar */}
                      <div style={{
                        marginTop: 20, padding: "14px 18px", borderTop: "1px solid rgba(184,160,74,0.08)",
                        display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#4caf50", animation: "livePulse 2s infinite" }} />
                          <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: "rgba(76,175,80,0.8)", letterSpacing: 2 }}>ALL FEEDS LIVE</span>
                        </div>
                        <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2 }}>
                          ROTATES EVERY 30S • {stockGroupIdx + 1}/{STOCK_GROUPS.length} GROUPS
                        </span>
                      </div>
                    </>
                  )}
                  {!forex && (
                    <div style={{ textAlign: "center", padding: 60 }}>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 12, color: GOLD_DIM }}>LOADING FOREX DATA...</div>
                    </div>
                  )}
                </div>
              ) : tab === "social" ? (
                /* Social Analytics Tab */
                <div>
                  {socialStats ? (
                    <>
                      {/* Platform cards - each shows only its relevant metrics */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>

                        {/* Twitter / X */}
                        {socialStats.twitter && (() => {
                          const s = socialStats.twitter;
                          return (
                            <div style={{ border: "1px solid rgba(184,160,74,0.15)", padding: "24px", background: "rgba(14,13,11,0.8)" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                                <div style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(212,205,180,0.2)", color: CREAM, fontFamily: FONTS.mono, fontSize: 14, fontWeight: 600 }}>{"\uD835\uDD4F"}</div>
                                <div>
                                  <div style={{ fontFamily: FONTS.display, fontSize: 18, color: CREAM }}>Twitter / X</div>
                                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM }}>@NautX_</div>
                                </div>
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <div>
                                  <div style={{ fontFamily: FONTS.display, fontSize: 28, color: GOLD, fontWeight: 300 }}>{(s.followers || 0).toLocaleString()}</div>
                                  <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, marginTop: 4 }}>FOLLOWERS</div>
                                </div>
                                <div>
                                  <div style={{ fontFamily: FONTS.display, fontSize: 28, color: CREAM, fontWeight: 300 }}>{fmtNum(s.impressions)}</div>
                                  <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, marginTop: 4 }}>IMPRESSIONS</div>
                                </div>
                                <div>
                                  <div style={{ fontFamily: FONTS.display, fontSize: 28, color: CREAM, fontWeight: 300 }}>{fmtNum(s.engagements)}</div>
                                  <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, marginTop: 4 }}>ENGAGEMENTS</div>
                                </div>
                                <div>
                                  <div style={{ fontFamily: FONTS.display, fontSize: 28, color: OLIVE, fontWeight: 300 }}>{s.engagement_rate || 0}%</div>
                                  <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, marginTop: 4 }}>ENG. RATE</div>
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: 16, marginTop: 16, paddingTop: 14, borderTop: "1px solid rgba(184,160,74,0.08)", flexWrap: "wrap" }}>
                                {s.likes > 0 && <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: CREAM_DIM }}><span style={{ color: GOLD }}>{fmtNum(s.likes)}</span> likes</div>}
                                {s.replies > 0 && <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: CREAM_DIM }}><span style={{ color: CREAM }}>{s.replies}</span> replies</div>}
                                {s.reposts > 0 && <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: CREAM_DIM }}><span style={{ color: CREAM }}>{s.reposts}</span> reposts</div>}
                                {s.profile_visits > 0 && <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: CREAM_DIM }}><span style={{ color: CREAM }}>{s.profile_visits}</span> visits</div>}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Instagram - just followers */}
                        {socialStats.instagram && (
                          <div style={{ border: "1px solid rgba(184,160,74,0.15)", padding: "24px", background: "rgba(14,13,11,0.8)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                              <div style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(225,48,108,0.25)", color: "#E1306C", fontFamily: FONTS.mono, fontSize: 14, fontWeight: 600 }}>IG</div>
                              <div>
                                <div style={{ fontFamily: FONTS.display, fontSize: 18, color: CREAM }}>Instagram</div>
                                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM }}>@e10.210</div>
                              </div>
                            </div>
                            <div>
                              <div style={{ fontFamily: FONTS.display, fontSize: 44, color: GOLD, fontWeight: 300 }}>{(socialStats.instagram.followers || 0).toLocaleString()}</div>
                              <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2, marginTop: 6 }}>FOLLOWERS</div>
                            </div>
                          </div>
                        )}

                        {/* YouTube - subscribers and views */}
                        {socialStats.youtube && (
                          <div style={{ border: "1px solid rgba(184,160,74,0.15)", padding: "24px", background: "rgba(14,13,11,0.8)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                              <div style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(255,0,0,0.25)", color: "#FF0000", fontFamily: FONTS.mono, fontSize: 14, fontWeight: 600 }}>YT</div>
                              <div>
                                <div style={{ fontFamily: FONTS.display, fontSize: 18, color: CREAM }}>YouTube</div>
                                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM }}>@NautXMedia</div>
                              </div>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                              <div>
                                <div style={{ fontFamily: FONTS.display, fontSize: 36, color: GOLD, fontWeight: 300 }}>{(socialStats.youtube.subscribers || 0).toLocaleString()}</div>
                                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2, marginTop: 6 }}>SUBSCRIBERS</div>
                              </div>
                              <div>
                                <div style={{ fontFamily: FONTS.display, fontSize: 36, color: CREAM, fontWeight: 300 }}>{fmtNum(socialStats.youtube.views)}</div>
                                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2, marginTop: 6 }}>TOTAL VIEWS</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Totals row */}
                      <div style={{
                        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14,
                        marginTop: 20, paddingTop: 20, borderTop: "1px solid rgba(184,160,74,0.08)",
                      }}>
                        {(() => {
                          const all = Object.values(socialStats);
                          const totalFollowers = all.reduce((a, s) => a + (s.followers || 0), 0) + all.reduce((a, s) => a + (s.subscribers || 0), 0);
                          const totalViews = all.reduce((a, s) => a + (s.views || 0), 0) + all.reduce((a, s) => a + (s.impressions || 0), 0);
                          return [
                            { label: "TOTAL AUDIENCE", val: totalFollowers, color: GOLD },
                            { label: "TOTAL REACH", val: totalViews, color: CREAM },
                          ].map(t => (
                            <div key={t.label} style={{ border: "1px solid rgba(184,160,74,0.1)", padding: "16px", background: "rgba(14,13,11,0.6)" }}>
                              <div style={{ fontFamily: FONTS.display, fontSize: 30, color: t.color, fontWeight: 300 }}>
                                {fmtNum(t.val)}
                              </div>
                              <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, marginTop: 4 }}>{t.label}</div>
                            </div>
                          ));
                        })()}
                      </div>

                      {/* Status */}
                      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, paddingTop: 14, borderTop: "1px solid rgba(184,160,74,0.06)" }}>
                        <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2 }}>UPDATE VIA ADMIN PANEL</span>
                        <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2 }}>
                          LAST UPDATED: {Object.values(socialStats)[0]?.updated_at ? new Date(Object.values(socialStats)[0].updated_at).toLocaleDateString() : "N/A"}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: "center", padding: 60 }}>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 13, color: CREAM_DIM, marginBottom: 12 }}>NO SOCIAL DATA YET</div>
                      <div style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM_DIM }}>
                        Run the social_stats SQL schema in Supabase, then update numbers via the admin panel.
                      </div>
                    </div>
                  )}
                </div>
              ) : tab === "global" ? (
                /* Global Stats Tab */
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20 }}>
                  {/* Fear & Greed */}
                  {fear && (
                    <div style={{
                      border: "1px solid rgba(184,160,74,0.18)", padding: 28,
                      background: "rgba(14,13,11,0.8)",
                    }}>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, textTransform: "uppercase", marginBottom: 16 }}>Fear & Greed Index</div>
                      <div style={{ fontFamily: FONTS.display, fontSize: 56, fontWeight: 300, color: fearColor(parseInt(fear.value)) }}>
                        {fear.value}
                      </div>
                      <div style={{
                        fontFamily: FONTS.mono, fontSize: 13, letterSpacing: 2,
                        color: fearColor(parseInt(fear.value)), marginTop: 4, textTransform: "uppercase",
                      }}>{fear.value_classification}</div>
                      {/* Gauge bar */}
                      <div style={{ width: "100%", height: 4, background: "rgba(184,160,74,0.08)", marginTop: 16, borderRadius: 2 }}>
                        <div style={{
                          width: `${fear.value}%`, height: "100%", borderRadius: 2,
                          background: `linear-gradient(90deg, #ef5350, #ff9800, #ffeb3b, #8bc34a, #4caf50)`,
                          transition: "width 1s ease",
                        }} />
                      </div>
                    </div>
                  )}

                  {/* Total Market Cap */}
                  {crypto.length > 0 && (
                    <div style={{
                      border: "1px solid rgba(184,160,74,0.18)", padding: 28,
                      background: "rgba(14,13,11,0.8)",
                    }}>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, textTransform: "uppercase", marginBottom: 16 }}>Total Market Cap (Top 6)</div>
                      <div style={{ fontFamily: FONTS.display, fontSize: 40, fontWeight: 300, color: GOLD }}>
                        {fmtCap(crypto.reduce((a, c) => a + c.market_cap, 0))}
                      </div>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 12, color: CREAM_DIM, marginTop: 8 }}>
                        Combined volume: {fmtCap(crypto.reduce((a, c) => a + c.total_volume, 0))}
                      </div>
                    </div>
                  )}

                  {/* BTC Dominance card */}
                  {crypto.length > 0 && (() => {
                    const totalCap = crypto.reduce((a, c) => a + c.market_cap, 0);
                    const btc = crypto.find(c => c.id === "bitcoin");
                    const dom = btc ? ((btc.market_cap / totalCap) * 100).toFixed(1) : 0;
                    return (
                      <div style={{
                        border: "1px solid rgba(184,160,74,0.18)", padding: 28,
                        background: "rgba(14,13,11,0.8)",
                      }}>
                        <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, textTransform: "uppercase", marginBottom: 16 }}>BTC Dominance</div>
                        <div style={{ fontFamily: FONTS.display, fontSize: 48, fontWeight: 300, color: "#f7931a" }}>
                          {dom}%
                        </div>
                        <div style={{ width: "100%", height: 4, background: "rgba(184,160,74,0.08)", marginTop: 16, borderRadius: 2 }}>
                          <div style={{ width: `${dom}%`, height: "100%", borderRadius: 2, background: "#f7931a", transition: "width 1s" }} />
                        </div>
                      </div>
                    );
                  })()}

                  {/* System uptime */}
                  <div style={{
                    border: "1px solid rgba(184,160,74,0.18)", padding: 28,
                    background: "rgba(14,13,11,0.8)",
                  }}>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, textTransform: "uppercase", marginBottom: 16 }}>System Status</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {[
                        { label: "API Response", val: "< 200ms", color: "#4caf50" },
                        { label: "Uptime", val: "99.9%", color: "#4caf50" },
                        { label: "Active Sessions", val: "LIVE", color: GOLD },
                      ].map((s, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: CREAM_DIM, letterSpacing: 1 }}>{s.label}</span>
                          <span style={{ fontFamily: FONTS.mono, fontSize: 13, color: s.color, fontWeight: 500, letterSpacing: 1 }}>{s.val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : tab === "orderbook" ? (
                /* ORDER BOOK TAB - Live Binance WebSocket */
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, textTransform: "uppercase" }}>
                      Live Order Book - {orderBook.symbol}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {["BTCUSDT", "ETHUSDT", "SOLUSDT"].map(s => (
                        <button key={s} onClick={() => setObSymbol(s)} style={{
                          background: obSymbol === s ? "rgba(184,160,74,0.15)" : "transparent",
                          border: `1px solid ${obSymbol === s ? GOLD : "rgba(184,160,74,0.15)"}`,
                          color: obSymbol === s ? GOLD : CREAM_DIM, cursor: "pointer",
                          padding: "6px 14px", fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 1,
                        }}>{s.replace("USDT", "")}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <div style={{ border: "1px solid rgba(76,175,80,0.15)", padding: "12px", background: "rgba(14,13,11,0.6)" }}>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: "#4caf50", letterSpacing: 2, marginBottom: 8 }}>BIDS (BUYS)</div>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 11 }}>
                        {orderBook.bids.map((b, i) => {
                          const total = b.price * b.qty;
                          const max = Math.max(...orderBook.bids.map(x => x.price * x.qty));
                          const pct = (total / max) * 100;
                          return (
                            <div key={i} style={{ position: "relative", display: "flex", justifyContent: "space-between", padding: "4px 6px" }}>
                              <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: `${pct}%`, background: "rgba(76,175,80,0.08)" }} />
                              <span style={{ color: "#4caf50", position: "relative" }}>${b.price.toLocaleString()}</span>
                              <span style={{ color: CREAM_DIM, position: "relative" }}>{b.qty.toFixed(4)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ border: "1px solid rgba(239,83,80,0.15)", padding: "12px", background: "rgba(14,13,11,0.6)" }}>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: "#ef5350", letterSpacing: 2, marginBottom: 8 }}>ASKS (SELLS)</div>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 11 }}>
                        {orderBook.asks.map((a, i) => {
                          const total = a.price * a.qty;
                          const max = Math.max(...orderBook.asks.map(x => x.price * x.qty));
                          const pct = (total / max) * 100;
                          return (
                            <div key={i} style={{ position: "relative", display: "flex", justifyContent: "space-between", padding: "4px 6px" }}>
                              <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: `${pct}%`, background: "rgba(239,83,80,0.08)" }} />
                              <span style={{ color: "#ef5350", position: "relative" }}>${a.price.toLocaleString()}</span>
                              <span style={{ color: CREAM_DIM, position: "relative" }}>{a.qty.toFixed(4)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 14, fontFamily: FONTS.mono, fontSize: 9, color: "rgba(212,205,180,0.4)", textAlign: "center", letterSpacing: 2 }}>
                    LIVE • BINANCE WEBSOCKET • UPDATES EVERY 100MS
                  </div>
                </div>
              ) : tab === "trending" ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, marginBottom: 12, textTransform: "uppercase" }}>🔥 Trending Searches</div>
                    {trending.map((c, i) => (
                      <div key={c.item.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", border: "1px solid rgba(184,160,74,0.08)", marginBottom: 6, background: "rgba(14,13,11,0.6)" }}>
                        <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: GOLD_DIM, width: 20 }}>#{i + 1}</span>
                        {c.item.thumb && <img src={c.item.thumb} alt="" style={{ width: 20, height: 20, borderRadius: "50%" }} />}
                        <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: CREAM, flex: 1 }}>{c.item.name}</span>
                        <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM }}>#{c.item.market_cap_rank || "?"}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, marginBottom: 12, textTransform: "uppercase" }}>📈 Top Gainers (24h)</div>
                    {topGainers.slice(0, 10).map((c, i) => (
                      <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", border: "1px solid rgba(184,160,74,0.08)", marginBottom: 6, background: "rgba(14,13,11,0.6)" }}>
                        <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: GOLD_DIM, width: 20 }}>#{i + 1}</span>
                        {c.image && <img src={c.image} alt="" style={{ width: 20, height: 20, borderRadius: "50%" }} />}
                        <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: CREAM, flex: 1 }}>{c.symbol?.toUpperCase()}</span>
                        <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: "#4caf50" }}>+{c.price_change_percentage_24h?.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : tab === "whales" ? (
                <div>
                  {/* Whale Trades */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, textTransform: "uppercase" }}>
                      🐋 Live Whale Trades ({whales.length})
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#4caf50", animation: "livePulse 2s infinite" }} />
                      <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: "rgba(76,175,80,0.7)", letterSpacing: 1 }}>BITCOIN</span>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
                    {(() => {
                      const buys = whales.filter(w => w.side === "BUY");
                      const sells = whales.filter(w => w.side === "SELL");
                      const buyVol = buys.reduce((s, w) => s + w.usdValue, 0);
                      const sellVol = sells.reduce((s, w) => s + w.usdValue, 0);
                      return [
                        { label: "BUYS", val: buys.length, vol: buyVol, color: "#4caf50" },
                        { label: "SELLS", val: sells.length, vol: sellVol, color: "#ef5350" },
                        { label: "NET FLOW", val: buyVol > sellVol ? "BULLISH" : "BEARISH", vol: Math.abs(buyVol - sellVol), color: buyVol > sellVol ? "#4caf50" : "#ef5350" },
                      ].map(s => (
                        <div key={s.label} style={{ border: `1px solid ${s.color}30`, padding: "12px 14px", background: "rgba(14,13,11,0.6)" }}>
                          <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2 }}>{s.label}</div>
                          <div style={{ fontFamily: FONTS.display, fontSize: 22, color: s.color, fontWeight: 400, marginTop: 2 }}>{s.val}</div>
                          <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, marginTop: 2 }}>
                            ${s.vol >= 1000000 ? (s.vol / 1000000).toFixed(2) + "M" : s.vol >= 1000 ? (s.vol / 1000).toFixed(1) + "K" : s.vol.toFixed(0)}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>

                  {/* Scrollable trade list */}
                  <div style={{
                    maxHeight: 480, overflowY: "auto", paddingRight: 6,
                    border: "1px solid rgba(184,160,74,0.08)", background: "rgba(10,10,8,0.4)",
                  }}>
                    {whales.length > 0 ? whales.map((w, i) => (
                      <a key={`${w.hash}-${i}`} href={w.explorer} target="_blank" rel="noopener noreferrer" style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "12px 16px", borderBottom: "1px solid rgba(184,160,74,0.05)",
                        textDecoration: "none", transition: "background 0.2s",
                        borderLeft: `3px solid ${w.side === "BUY" ? "#4caf50" : "#ef5350"}`,
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(184,160,74,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
                          <span style={{
                            fontFamily: FONTS.mono, fontSize: 9, padding: "3px 8px",
                            border: `1px solid ${w.color}50`, color: w.color, letterSpacing: 1,
                            flexShrink: 0,
                          }}>{w.chain}</span>
                          <span style={{
                            fontFamily: FONTS.mono, fontSize: 10, padding: "3px 10px",
                            background: w.side === "BUY" ? "rgba(76,175,80,0.15)" : "rgba(239,83,80,0.15)",
                            color: w.side === "BUY" ? "#4caf50" : "#ef5350",
                            letterSpacing: 1, fontWeight: 600, flexShrink: 0,
                          }}>{w.side}</span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontFamily: FONTS.display, fontSize: 18, color: CREAM, fontWeight: 300 }}>
                              {w.value.toLocaleString("en-US", { maximumFractionDigits: 4 })} {w.chain}
                            </div>
                            <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, marginTop: 1 }}>
                              @ ${w.price.toLocaleString("en-US", { maximumFractionDigits: w.price < 1 ? 6 : 2 })}
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                          <div style={{ fontFamily: FONTS.mono, fontSize: 13, color: GOLD, fontWeight: 500 }}>
                            ${w.usdValue.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                          </div>
                          <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, marginTop: 1 }}>
                            {new Date(w.time * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                          </div>
                        </div>
                      </a>
                    )) : (
                      <div style={{ textAlign: "center", padding: 60 }}>
                        <div style={{ width: 20, height: 20, border: `2px solid ${GOLD_DIM}`, borderTop: `2px solid ${GOLD}`, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 12px" }} />
                        <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD_DIM, letterSpacing: 2 }}>WAITING FOR WHALE TRADES...</div>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 10, fontFamily: FONTS.mono, fontSize: 9, color: "rgba(212,205,180,0.3)", textAlign: "center", letterSpacing: 2 }}>
                    LIVE BINANCE STREAM • BTC TRADES &gt; $100K • CLICK TO VIEW
                  </div>
                </div>
              ) : tab === "nft" ? (
                <div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, marginBottom: 16, textTransform: "uppercase" }}>NFT Collections</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
                    {nftCollections.map(nft => (
                      <div key={nft.id} style={{ border: "1px solid rgba(184,160,74,0.12)", padding: "16px", background: "rgba(14,13,11,0.7)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                          {nft.image?.small && <img src={nft.image.small} alt="" style={{ width: 36, height: 36, borderRadius: 4 }} />}
                          <div style={{ fontFamily: FONTS.display, fontSize: 16, color: CREAM, fontWeight: 400 }}>{nft.name}</div>
                        </div>
                        <div style={{ fontFamily: FONTS.display, fontSize: 24, color: GOLD, fontWeight: 300 }}>{nft.floor_price?.usd ? "$" + nft.floor_price.usd.toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—"}</div>
                        <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 1, marginTop: 2 }}>FLOOR PRICE</div>
                        {nft.floor_price_24h_percentage_change?.usd != null && (
                          <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: nft.floor_price_24h_percentage_change.usd >= 0 ? "#4caf50" : "#ef5350", marginTop: 8 }}>
                            {nft.floor_price_24h_percentage_change.usd >= 0 ? "+" : ""}{nft.floor_price_24h_percentage_change.usd.toFixed(2)}% 24h
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : tab === "macro" ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
                  {[
                    { label: "DXY (Dollar Index)", val: macro.dxy?.price ? macro.dxy.price.toFixed(2) : null, change: macro.dxy?.change, color: GOLD },
                    { label: "VIX (Volatility)", val: macro.vix?.price ? macro.vix.price.toFixed(2) : null, change: macro.vix?.change, color: CREAM },
                    { label: "10Y Treasury", val: macro.tnx?.price ? macro.tnx.price.toFixed(2) + "%" : null, change: macro.tnx?.change, color: CREAM },
                    { label: "Fed Funds Rate", val: macro.fedRate + "%", change: null, color: GOLD },
                    { label: "CPI Inflation", val: macro.cpi + "%", change: null, color: CREAM },
                  ].map(m => (
                    <div key={m.label} style={{ border: "1px solid rgba(184,160,74,0.12)", padding: "20px", background: "rgba(14,13,11,0.7)" }}>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2, color: CREAM_DIM, textTransform: "uppercase", marginBottom: 8 }}>{m.label}</div>
                      <div style={{ fontFamily: FONTS.display, fontSize: 32, fontWeight: 300, color: m.color }}>{m.val || "—"}</div>
                      {m.change != null && m.val && (<div style={{ fontFamily: FONTS.mono, fontSize: 11, color: m.change >= 0 ? "#4caf50" : "#ef5350", marginTop: 4 }}>{m.change >= 0 ? "+" : ""}{m.change?.toFixed(2)}%</div>)}
                    </div>
                  ))}
                  <div style={{ border: "1px solid rgba(247,147,26,0.25)", padding: "20px", background: "rgba(247,147,26,0.04)", gridColumn: "span 2" }}>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2, color: CREAM_DIM, textTransform: "uppercase", marginBottom: 8 }}>₿ Bitcoin Halving Countdown</div>
                    <div style={{ fontFamily: FONTS.display, fontSize: 48, fontWeight: 300, color: "#f7931a" }}>{daysToHalving} <span style={{ fontSize: 18 }}>days</span></div>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: CREAM_DIM, marginTop: 4 }}>Estimated April 2028 • Next reward halving</div>
                  </div>
                </div>
              ) : tab === "events" ? (
                <div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, marginBottom: 16, textTransform: "uppercase" }}>Upcoming Events</div>
                  {[
                    { date: "Apr 15", title: "US Tax Day", type: "MACRO", desc: "Crypto capital gains filing deadline" },
                    { date: "Apr 30", title: "FOMC Meeting", type: "FED", desc: "Federal Reserve interest rate decision" },
                    { date: "May 12", title: "ETH Pectra Upgrade", type: "PROTOCOL", desc: "Ethereum mainnet hard fork" },
                    { date: "May 28", title: "BTC ETF Inflows Report", type: "MARKET", desc: "Monthly institutional flow data" },
                    { date: "Jun 11", title: "FOMC Meeting", type: "FED", desc: "Federal Reserve interest rate decision" },
                    { date: "Jun 18", title: "Token Unlock - ARB", type: "UNLOCK", desc: "Arbitrum scheduled token unlock event" },
                    { date: "Jul 4", title: "Solana Breakpoint Conf.", type: "EVENT", desc: "Annual Solana developer conference" },
                  ].map((e, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 18px", border: "1px solid rgba(184,160,74,0.08)", marginBottom: 8, background: "rgba(14,13,11,0.6)" }}>
                      <div style={{ width: 60, textAlign: "center" }}>
                        <div style={{ fontFamily: FONTS.display, fontSize: 18, color: GOLD, fontWeight: 400 }}>{e.date}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: FONTS.body, fontSize: 14, color: CREAM, fontWeight: 500 }}>{e.title}</div>
                        <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, marginTop: 2 }}>{e.desc}</div>
                      </div>
                      <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: GOLD_DIM, padding: "3px 10px", border: "1px solid rgba(184,160,74,0.2)", letterSpacing: 1 }}>{e.type}</span>
                    </div>
                  ))}
                </div>
              ) : tab === "news" ? (
                <div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, marginBottom: 16, textTransform: "uppercase" }}>Crypto News Feed</div>
                  {news.length > 0 ? news.map((n, i) => (
                    <a key={i} href={n.url} target="_blank" rel="noopener noreferrer" style={{ display: "block", padding: "16px 20px", border: "1px solid rgba(184,160,74,0.08)", marginBottom: 8, background: "rgba(14,13,11,0.6)", textDecoration: "none", transition: "all 0.3s" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(184,160,74,0.25)"; e.currentTarget.style.background = "rgba(184,160,74,0.04)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(184,160,74,0.08)"; e.currentTarget.style.background = "rgba(14,13,11,0.6)"; }}>
                      <div style={{ fontFamily: FONTS.body, fontSize: 14, color: CREAM, fontWeight: 500, marginBottom: 4 }}>{n.title}</div>
                      <div style={{ fontFamily: FONTS.body, fontSize: 12, color: CREAM_DIM, marginBottom: 6 }}>{n.description?.slice(0, 140)}...</div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontFamily: FONTS.mono, fontSize: 9, color: GOLD_DIM, letterSpacing: 1 }}>
                        <span>{n.author || n.news_site || "Source"}</span>
                        <span>{n.updated_at ? new Date(n.updated_at).toLocaleDateString() : ""}</span>
                      </div>
                    </a>
                  )) : (<div style={{ textAlign: "center", padding: 40, fontFamily: FONTS.mono, fontSize: 11, color: CREAM_DIM }}>Loading news feed...</div>)}
                </div>
              ) : tab === "watchlist" ? (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, textTransform: "uppercase" }}>⭐ My Watchlist</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <input type="text" id="add-watch" placeholder="coin id (e.g. cardano)" style={{ background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, padding: "6px 12px", fontFamily: FONTS.mono, fontSize: 11, outline: "none" }} />
                      <button onClick={() => { const el = document.getElementById("add-watch"); if (el?.value) { addToWatchlist(el.value.toLowerCase().trim()); el.value = ""; } }} style={{ background: "rgba(184,160,74,0.1)", border: "1px solid rgba(184,160,74,0.3)", color: GOLD, cursor: "pointer", padding: "6px 14px", fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 1 }}>+ ADD</button>
                    </div>
                  </div>
                  {watchlistData.length > 0 ? watchlistData.map(c => (
                    <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 18px", border: "1px solid rgba(184,160,74,0.08)", marginBottom: 6, background: "rgba(14,13,11,0.6)" }}>
                      {c.image && <img src={c.image} alt="" style={{ width: 28, height: 28, borderRadius: "50%" }} />}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: FONTS.mono, fontSize: 13, color: CREAM, fontWeight: 500 }}>{c.name}</div>
                        <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM }}>{c.symbol?.toUpperCase()}</div>
                      </div>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 14, color: GOLD }}>{fmtPrice(c.current_price)}</div>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 12, color: c.price_change_percentage_24h >= 0 ? "#4caf50" : "#ef5350", minWidth: 70, textAlign: "right" }}>{c.price_change_percentage_24h >= 0 ? "+" : ""}{c.price_change_percentage_24h?.toFixed(2)}%</div>
                      <button onClick={() => removeFromWatchlist(c.id)} style={{ background: "none", border: "1px solid rgba(155,27,48,0.3)", color: RUBY, cursor: "pointer", padding: "4px 10px", fontFamily: FONTS.mono, fontSize: 9 }}>✕</button>
                    </div>
                  )) : watchlistLoading ? (
                    <div style={{ textAlign: "center", padding: 40 }}>
                      <div style={{ width: 20, height: 20, border: `2px solid ${GOLD_DIM}`, borderTop: `2px solid ${GOLD}`, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 12px" }} />
                      <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD_DIM, letterSpacing: 2 }}>FETCHING PRICES...</div>
                    </div>
                  ) : (<div style={{ textAlign: "center", padding: 40, fontFamily: FONTS.mono, fontSize: 11, color: CREAM_DIM }}>No coins in watchlist. Add a coin ID like "bitcoin" or "cardano" above.</div>)}
                  <div style={{ marginTop: 30 }}>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, marginBottom: 12, textTransform: "uppercase" }}>🔔 Price Alerts</div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                      <select id="alert-sym" style={{ background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, padding: "6px 12px", fontFamily: FONTS.mono, fontSize: 11 }}>
                        <option value="btc">BTC</option><option value="eth">ETH</option><option value="sol">SOL</option>
                      </select>
                      <select id="alert-dir" style={{ background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, padding: "6px 12px", fontFamily: FONTS.mono, fontSize: 11 }}>
                        <option value="above">Above</option><option value="below">Below</option>
                      </select>
                      <input type="number" id="alert-price" placeholder="Target price" style={{ background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, padding: "6px 12px", fontFamily: FONTS.mono, fontSize: 11, outline: "none", width: 130 }} />
                      <button onClick={() => {
                        const sym = document.getElementById("alert-sym").value;
                        const dir = document.getElementById("alert-dir").value;
                        const tgt = document.getElementById("alert-price").value;
                        if (tgt) { addAlert(sym, tgt, dir); document.getElementById("alert-price").value = ""; }
                      }} style={{ background: "rgba(184,160,74,0.1)", border: "1px solid rgba(184,160,74,0.3)", color: GOLD, cursor: "pointer", padding: "6px 14px", fontFamily: FONTS.mono, fontSize: 10 }}>+ ALERT</button>
                    </div>
                    {alerts.map(a => (
                      <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", border: `1px solid ${a.triggered ? "rgba(76,175,80,0.3)" : "rgba(184,160,74,0.08)"}`, marginBottom: 6, background: a.triggered ? "rgba(76,175,80,0.04)" : "rgba(14,13,11,0.6)" }}>
                        <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: CREAM }}>
                          {a.symbol.toUpperCase()} {a.direction} ${a.target.toLocaleString()}
                          {a.triggered && <span style={{ color: "#4caf50", marginLeft: 10 }}>✓ TRIGGERED</span>}
                        </span>
                        <button onClick={() => removeAlert(a.id)} style={{ background: "none", border: "1px solid rgba(155,27,48,0.3)", color: RUBY, cursor: "pointer", padding: "3px 8px", fontFamily: FONTS.mono, fontSize: 9 }}>✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : tab === "portfolio" ? (
                <div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: CREAM_DIM, marginBottom: 16, textTransform: "uppercase" }}>💼 Wallet Tracker</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
                    <input type="text" value={portfolioAddr} onChange={e => setPortfolioAddr(e.target.value)} placeholder="Paste ETH or SOL address..."
                      style={{ flex: 1, minWidth: 250, background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, padding: "10px 14px", fontFamily: FONTS.mono, fontSize: 12, outline: "none" }} />
                    <button onClick={fetchPortfolio} style={{ background: "rgba(184,160,74,0.1)", border: "1px solid rgba(184,160,74,0.3)", color: GOLD, cursor: "pointer", padding: "10px 22px", fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 2 }}>TRACK</button>
                  </div>
                  {portfolioData ? (
                    <div style={{ border: "1px solid rgba(184,160,74,0.15)", padding: "24px", background: "rgba(14,13,11,0.7)" }}>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, marginBottom: 4 }}>{portfolioData.chain?.toUpperCase()} • {portfolioData.address.slice(0, 12)}...{portfolioData.address.slice(-6)}</div>
                      <div style={{ fontFamily: FONTS.display, fontSize: 48, color: GOLD, fontWeight: 300 }}>
                        {portfolioData.balance?.toFixed(4)} {portfolioData.chain === "Solana" ? "SOL" : "ETH"}
                      </div>
                      {portfolioData.balanceUsd > 0 && (
                        <div style={{ fontFamily: FONTS.mono, fontSize: 14, color: "#4caf50", marginTop: 4 }}>${portfolioData.balanceUsd.toLocaleString("en-US", { maximumFractionDigits: 2 })}</div>
                      )}
                      {portfolioData.tokens?.length > 0 && (
                        <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid rgba(184,160,74,0.08)" }}>
                          <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, marginBottom: 12 }}>TOKENS ({portfolioData.tokens.length})</div>
                          {portfolioData.tokens.slice(0, 10).map((t, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontFamily: FONTS.mono, fontSize: 11 }}>
                              <span style={{ color: CREAM }}>{t.tokenInfo?.symbol || "?"}</span>
                              <span style={{ color: GOLD_DIM }}>{((t.balance || 0) / Math.pow(10, t.tokenInfo?.decimals || 18)).toFixed(4)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: 60, fontFamily: FONTS.mono, fontSize: 11, color: CREAM_DIM }}>Enter an address above to view balance and tokens</div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  CONTACT
 * ═══════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════
 *  LINKS PAGE
 * ═══════════════════════════════════════════════════════════════ */
function LinksPage({ onBack }) {
  const [hovLink, setHovLink] = useState(-1);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  const links = [
    { label: "Twitter / X", handle: "@NautX_", url: CONFIG.socials.x, icon: "\uD835\uDD4F", color: CREAM },
    { label: "Instagram", handle: "@e10.210", url: CONFIG.socials.ig, icon: "IG", color: "#E1306C" },
    { label: "YouTube", handle: "@NautXMedia", url: CONFIG.socials.yt, icon: "YT", color: "#FF0000" },
    { label: "Email", handle: CONFIG.socials.email, url: `mailto:${CONFIG.socials.email}`, icon: "@", color: GOLD },
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9000, background: BG,
      overflow: "auto", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", minHeight: "100vh",
    }}>
      {/* Ambient glow */}
      <div style={{
        position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)",
        width: "60vw", height: "50vh", borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(184,160,74,0.04) 0%, transparent 65%)",
      }} />
      <div style={{
        position: "absolute", bottom: "15%", right: "20%",
        width: "30vw", height: "30vw", borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle, rgba(155,27,48,0.03) 0%, transparent 55%)",
      }} />

      <div style={{
        maxWidth: 560, width: "100%", padding: "40px 24px", position: "relative", zIndex: 1,
      }}>
        {/* Back button */}
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 8, background: "none",
          border: "none", cursor: "pointer", marginBottom: 48,
          fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 2,
          color: GOLD_DIM, textTransform: "uppercase",
          opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(-10px)",
          transition: "all 0.6s cubic-bezier(0.16,1,0.3,1) 0.1s",
        }}>
          <span style={{ fontSize: 16 }}>&larr;</span> Back to site
        </button>

        {/* Header */}
        <div style={{
          textAlign: "center", marginBottom: 56,
          opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(20px)",
          transition: "all 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s",
        }}>
          <div style={{
            fontFamily: FONTS.display, fontSize: 36, fontWeight: 300,
            letterSpacing: 8, color: CREAM, marginBottom: 8,
          }}>
            NAUT<span style={{ color: RUBY, fontWeight: 600 }}>X</span>
          </div>
          <div style={{
            fontFamily: FONTS.display, fontSize: 16, fontStyle: "italic",
            letterSpacing: 6, color: GOLD_DIM, textTransform: "uppercase",
          }}>Media Group</div>
          <div style={{
            width: 40, height: 1, margin: "24px auto 0",
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
          }} />
        </div>

        {/* Links */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {links.map((link, i) => {
            const delay = 0.3 + i * 0.1;
            return (
              <a
                key={i}
                href={link.url}
                target={link.url.startsWith("mailto") ? undefined : "_blank"}
                rel="noopener noreferrer"
                onMouseEnter={() => setHovLink(i)}
                onMouseLeave={() => setHovLink(-1)}
                style={{
                  display: "flex", alignItems: "center", gap: 20,
                  padding: "22px 28px", textDecoration: "none",
                  border: `1px solid ${hovLink === i ? "rgba(184,160,74,0.3)" : "rgba(184,160,74,0.08)"}`,
                  background: hovLink === i ? "rgba(184,160,74,0.05)" : "rgba(14,13,11,0.7)",
                  backdropFilter: "blur(8px)",
                  transition: "all 0.4s cubic-bezier(0.16,1,0.3,1)",
                  transform: hovLink === i ? "translateY(-3px) scale(1.01)" : "none",
                  boxShadow: hovLink === i ? "0 8px 32px rgba(0,0,0,0.3)" : "none",
                  opacity: loaded ? 1 : 0,
                  animationDelay: `${delay}s`,
                }}
              >
                {/* Icon box */}
                <div style={{
                  width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center",
                  border: `1px solid ${hovLink === i ? link.color : "rgba(184,160,74,0.12)"}`,
                  fontFamily: FONTS.mono, fontSize: 15, fontWeight: 600,
                  color: hovLink === i ? link.color : CREAM_DIM,
                  transition: "all 0.4s", flexShrink: 0,
                  background: hovLink === i ? `${link.color}10` : "transparent",
                }}>{link.icon}</div>

                {/* Text */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontFamily: FONTS.display, fontSize: 22, fontWeight: 400,
                    color: hovLink === i ? CREAM : "rgba(212,205,180,0.7)",
                    transition: "color 0.4s",
                  }}>{link.label}</div>
                  <div style={{
                    fontFamily: FONTS.mono, fontSize: 11, color: CREAM_DIM,
                    letterSpacing: 1, marginTop: 3,
                  }}>{link.handle}</div>
                </div>

                {/* Arrow */}
                <div style={{
                  fontFamily: FONTS.display, fontSize: 22, color: hovLink === i ? link.color : GOLD_DIM,
                  transform: hovLink === i ? "translate(4px, -4px)" : "none",
                  opacity: hovLink === i ? 1 : 0.25,
                  transition: "all 0.4s",
                }}>&#8599;</div>
              </a>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          textAlign: "center", marginTop: 48,
          fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 3,
          color: "rgba(212,205,180,0.2)", textTransform: "uppercase",
          opacity: loaded ? 1 : 0, transition: "opacity 1s 0.8s",
        }}>
          &copy; {new Date().getFullYear()} Naut X Media Group
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  MEMBERS ONLY — Invite-gated Jupiter Terminal
 *  Invite Code: NAUTXINNERCIRCLE
 * ═══════════════════════════════════════════════════════════════ */
const INVITE_CODE = "NAUTXINNERCIRCLE";

// Build a compact sports/portfolio context from currently-loaded data
// This gets injected into chat requests so SportsAI has real data to analyze
function buildSportsContext(odds, scores, sport, portfolio) {
  const parts = [];

  // Portfolio context
  if (portfolio && portfolio.holdings && portfolio.holdings.length > 0) {
    parts.push(`USER PORTFOLIO (${portfolio.source}, total $${portfolio.total.toFixed(2)}):`);
    portfolio.holdings.slice(0, 12).forEach(h => {
      const change = h.change24h != null ? ` (${h.change24h >= 0 ? "+" : ""}${h.change24h.toFixed(2)}% 24h)` : "";
      const pl = h.plPct != null ? ` [P/L: ${h.plPct >= 0 ? "+" : ""}${h.plPct.toFixed(1)}%]` : "";
      parts.push(`  ${h.symbol}: $${h.value.toFixed(2)} (${h.allocation.toFixed(1)}%)${change}${pl}`);
    });
    parts.push("");
  }

  // Sports context
  if (odds && odds.length > 0) {
    const sportLabel = ({
      "americanfootball_nfl": "NFL", "americanfootball_ncaaf": "NCAAF",
      "basketball_nba": "NBA", "baseball_mlb": "MLB", "icehockey_nhl": "NHL",
      "soccer_epl": "EPL", "soccer_usa_mls": "MLS", "mma_mixed_martial_arts": "UFC",
    })[sport] || sport;

    parts.push(`CURRENT BOVADA LINES — ${sportLabel} (live data, refreshed every 60s):`);
    parts.push("");

    odds.slice(0, 10).forEach(game => {
      const bovada = game.bookmakers?.find(b => b.key === "bovada");
      if (!bovada) return;

      const h2h = bovada.markets?.find(m => m.key === "h2h");
      const spreads = bovada.markets?.find(m => m.key === "spreads");
      const totals = bovada.markets?.find(m => m.key === "totals");
      const commence = new Date(game.commence_time);
      const now = new Date();
      const scoreData = scores?.find(s => s.id === game.id);
      const isCompleted = scoreData?.completed;
      const isLive = !isCompleted && commence <= now;

      let status = commence.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
      if (isLive) status = "LIVE NOW";
      if (isCompleted) status = "FINAL";

      parts.push(`${game.away_team} @ ${game.home_team} — ${status}`);

      if (scoreData?.scores) {
        const away = scoreData.scores.find(s => s.name === game.away_team);
        const home = scoreData.scores.find(s => s.name === game.home_team);
        if (away && home) parts.push(`  Score: ${away.name} ${away.score} — ${home.name} ${home.score}`);
      }

      if (h2h) {
        const partsML = h2h.outcomes?.map(o => `${o.name} ${o.price > 0 ? "+" : ""}${o.price}`).join(" | ");
        parts.push(`  ML: ${partsML}`);
      }
      if (spreads) {
        const partsS = spreads.outcomes?.map(o => `${o.name} ${o.point > 0 ? "+" : ""}${o.point} (${o.price > 0 ? "+" : ""}${o.price})`).join(" | ");
        parts.push(`  Spread: ${partsS}`);
      }
      if (totals) {
        const partsT = totals.outcomes?.map(o => `${o.name} ${o.point} (${o.price > 0 ? "+" : ""}${o.price})`).join(" | ");
        parts.push(`  Total: ${partsT}`);
      }
      parts.push("");
    });
  }

  return parts.length > 0 ? parts.join("\n") : null;
}

// Lightweight markdown renderer for SportsAI chat
// Handles: **bold**, *italic*, `code`, ### headers, bullet lists, numbered lists, paragraphs
function MarkdownChat({ text }) {
  if (!text) return null;

  // Split into lines for block-level parsing
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Headers (### Header)
    const headerMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      blocks.push({ type: "header", level, text: headerMatch[2] });
      i++;
      continue;
    }

    // Horizontal rule
    if (trimmed === "---" || trimmed === "***") {
      blocks.push({ type: "hr" });
      i++;
      continue;
    }

    // Bullet list
    if (/^[-*•]\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^[-*•]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*•]\s+/, ""));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    // Empty line = paragraph break
    if (trimmed === "") {
      blocks.push({ type: "break" });
      i++;
      continue;
    }

    // Regular paragraph — collect consecutive non-empty, non-special lines
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^(#{1,3}\s+|---|\*\*\*$|[-*•]\s+|\d+\.\s+)/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push({ type: "p", text: paraLines.join(" ") });
  }

  // Inline formatter for **bold**, *italic*, `code`
  const renderInline = (str, keyPrefix) => {
    const parts = [];
    let remaining = str;
    let key = 0;

    while (remaining.length > 0) {
      // **bold**
      const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
      if (boldMatch) {
        parts.push(<strong key={`${keyPrefix}-${key++}`} style={{ color: GOLD, fontWeight: 600 }}>{boldMatch[1]}</strong>);
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }
      // `code`
      const codeMatch = remaining.match(/^`([^`]+)`/);
      if (codeMatch) {
        parts.push(<code key={`${keyPrefix}-${key++}`} style={{
          fontFamily: FONTS.mono, fontSize: "0.9em",
          background: "rgba(184,160,74,0.12)", padding: "1px 6px",
          borderRadius: 3, color: GOLD,
        }}>{codeMatch[1]}</code>);
        remaining = remaining.slice(codeMatch[0].length);
        continue;
      }
      // *italic* (single asterisk, not part of **)
      const italicMatch = remaining.match(/^\*([^*]+)\*/);
      if (italicMatch) {
        parts.push(<em key={`${keyPrefix}-${key++}`} style={{ color: "rgba(212,205,180,0.95)" }}>{italicMatch[1]}</em>);
        remaining = remaining.slice(italicMatch[0].length);
        continue;
      }
      // Plain text up to next special char
      const nextSpecial = remaining.search(/[\*`]/);
      if (nextSpecial === -1) {
        parts.push(<span key={`${keyPrefix}-${key++}`}>{remaining}</span>);
        break;
      }
      if (nextSpecial === 0) {
        // Stray asterisk — render as literal
        parts.push(<span key={`${keyPrefix}-${key++}`}>{remaining[0]}</span>);
        remaining = remaining.slice(1);
      } else {
        parts.push(<span key={`${keyPrefix}-${key++}`}>{remaining.slice(0, nextSpecial)}</span>);
        remaining = remaining.slice(nextSpecial);
      }
    }
    return parts;
  };

  return (
    <div>
      {blocks.map((b, idx) => {
        if (b.type === "header") {
          const sizes = { 1: 16, 2: 15, 3: 14 };
          return (
            <div key={idx} style={{
              fontFamily: FONTS.display, fontSize: sizes[b.level] || 14,
              fontWeight: 600, color: CREAM, margin: "10px 0 4px", letterSpacing: 0.3,
            }}>{renderInline(b.text, `h-${idx}`)}</div>
          );
        }
        if (b.type === "hr") {
          return <div key={idx} style={{ height: 1, background: "rgba(184,160,74,0.15)", margin: "10px 0" }} />;
        }
        if (b.type === "ul") {
          return (
            <ul key={idx} style={{ margin: "4px 0 4px 4px", paddingLeft: 14, listStyle: "none" }}>
              {b.items.map((item, ii) => (
                <li key={ii} style={{ position: "relative", padding: "2px 0 2px 14px" }}>
                  <span style={{ position: "absolute", left: 0, color: GOLD, fontSize: 10, top: 6 }}>◆</span>
                  {renderInline(item, `ul-${idx}-${ii}`)}
                </li>
              ))}
            </ul>
          );
        }
        if (b.type === "ol") {
          return (
            <ol key={idx} style={{ margin: "4px 0 4px 4px", paddingLeft: 20 }}>
              {b.items.map((item, ii) => (
                <li key={ii} style={{ padding: "2px 0", color: "rgba(212,205,180,0.9)" }}>
                  {renderInline(item, `ol-${idx}-${ii}`)}
                </li>
              ))}
            </ol>
          );
        }
        if (b.type === "break") {
          return <div key={idx} style={{ height: 6 }} />;
        }
        // Paragraph
        return (
          <div key={idx} style={{ margin: "2px 0" }}>
            {renderInline(b.text, `p-${idx}`)}
          </div>
        );
      })}
    </div>
  );
}

// Wallet add form for portfolio Wallets tab
function WalletAddForm({ onAdd }) {
  const [chain, setChain] = useState("ethereum");
  const [address, setAddress] = useState("");
  const [label, setLabel] = useState("");
  const CHAINS = [
    { id: "ethereum", label: "Ethereum / EVM" },
    { id: "bitcoin", label: "Bitcoin" },
    { id: "solana", label: "Solana" },
    { id: "polygon", label: "Polygon" },
    { id: "arbitrum", label: "Arbitrum" },
    { id: "optimism", label: "Optimism" },
    { id: "base", label: "Base" },
    { id: "bsc", label: "BNB Chain" },
    { id: "avalanche", label: "Avalanche" },
  ];
  const submit = () => {
    if (!address.trim()) return;
    onAdd(chain, address, label);
    setAddress(""); setLabel("");
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr auto", gap: 6 }}>
      <select value={chain} onChange={e => setChain(e.target.value)} style={{ padding: "8px 10px", background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, fontFamily: FONTS.mono, fontSize: 11, outline: "none" }}>
        {CHAINS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
      <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Wallet address"
        style={{ padding: "8px 10px", background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, fontFamily: FONTS.mono, fontSize: 11, outline: "none" }} />
      <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (optional)"
        style={{ padding: "8px 10px", background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, fontFamily: FONTS.mono, fontSize: 11, outline: "none" }} />
      <button onClick={submit} disabled={!address.trim()} style={{ padding: "8px 16px", background: address.trim() ? "rgba(184,160,74,0.15)" : "rgba(184,160,74,0.05)", border: "1px solid rgba(184,160,74,0.3)", color: GOLD, cursor: address.trim() ? "pointer" : "default", fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 1 }}>+ ADD</button>
    </div>
  );
}

// Manual add form for portfolio Manual tab
function ManualAddForm({ onAdd }) {
  const [symbol, setSymbol] = useState("");
  const [amount, setAmount] = useState("");
  const [avgPrice, setAvgPrice] = useState("");
  const submit = () => {
    if (!symbol.trim() || !amount) return;
    onAdd(symbol, parseFloat(amount), parseFloat(avgPrice) || 0);
    setSymbol(""); setAmount(""); setAvgPrice("");
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 6 }}>
      <input type="text" value={symbol} onChange={e => setSymbol(e.target.value)} placeholder="Symbol (BTC, ETH, SOL)"
        style={{ padding: "8px 10px", background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, fontFamily: FONTS.mono, fontSize: 11, outline: "none" }} />
      <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount"
        style={{ padding: "8px 10px", background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, fontFamily: FONTS.mono, fontSize: 11, outline: "none" }} />
      <input type="number" value={avgPrice} onChange={e => setAvgPrice(e.target.value)} placeholder="Avg cost (optional)"
        style={{ padding: "8px 10px", background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, fontFamily: FONTS.mono, fontSize: 11, outline: "none" }} />
      <button onClick={submit} disabled={!symbol.trim() || !amount} style={{ padding: "8px 16px", background: (symbol.trim() && amount) ? "rgba(184,160,74,0.15)" : "rgba(184,160,74,0.05)", border: "1px solid rgba(184,160,74,0.3)", color: GOLD, cursor: (symbol.trim() && amount) ? "pointer" : "default", fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 1 }}>+ ADD</button>
    </div>
  );
}

function CryptoCenterInner({ onBack }) {
  const { user } = useUser();
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(true);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState("");
  const [jupLoaded, setJupLoaded] = useState(false);

  const userEmail = user?.emailAddresses?.[0]?.emailAddress || "";
  const [chartData, setChartData] = useState({});
  const [chartRefreshing, setChartRefreshing] = useState(false);
  const [lastChartRefresh, setLastChartRefresh] = useState(null);

  // ═══ PORTFOLIO state ═══
  const [pfMode, setPfMode] = useState("none"); // 'coinstats' | 'wallets' | 'manual' | 'none'
  const [pfConfig, setPfConfig] = useState(() => {
    try {
      const saved = localStorage.getItem("nxmg-portfolio-config");
      return saved ? JSON.parse(saved) : {
        csApiKey: "", csShareToken: "",
        wallets: [], // [{ chain, address, label }]
        manual: [],  // [{ symbol, amount, avgPrice }]
      };
    } catch {
      return { csApiKey: "", csShareToken: "", wallets: [], manual: [] };
    }
  });
  const [pfData, setPfData] = useState(null); // { total, holdings: [{ symbol, name, value, amount, allocation, change24h }] }
  const [pfLoading, setPfLoading] = useState(false);
  const [pfError, setPfError] = useState("");
  const [pfOpen, setPfOpen] = useState(false); // collapsible
  const [pfSetupTab, setPfSetupTab] = useState("coinstats"); // which tab in setup

  // Persist config when it changes
  useEffect(() => {
    try { localStorage.setItem("nxmg-portfolio-config", JSON.stringify(pfConfig)); } catch {}
  }, [pfConfig]);

  // SportsAI Bot chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState(() => {
    try {
      const saved = localStorage.getItem("nxmg-sportsai-chat");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatScrollRef = useRef(null);

  // Persist chat history
  useEffect(() => {
    try { localStorage.setItem("nxmg-sportsai-chat", JSON.stringify(chatMessages.slice(-30))); } catch {}
  }, [chatMessages]);

  // Auto-scroll chat to bottom on new message
  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatMessages, chatLoading]);

  // Sports Odds + Scores state
  const [odds, setOdds] = useState([]);
  const [scores, setScores] = useState([]);
  const [oddsSport, setOddsSport] = useState("americanfootball_nfl");
  const [oddsLoading, setOddsLoading] = useState(false);
  const MO_ODDS_KEY = process.env.REACT_APP_ODDS_API_KEY || "c8086fee9c360fb521e7313b17006ad3";
  const MO_SPORTS = [
    { id: "americanfootball_nfl", label: "NFL" },
    { id: "americanfootball_ncaaf", label: "NCAAF" },
    { id: "basketball_nba", label: "NBA" },
    { id: "baseball_mlb", label: "MLB" },
    { id: "icehockey_nhl", label: "NHL" },
    { id: "soccer_epl", label: "EPL" },
    { id: "soccer_usa_mls", label: "MLS" },
    { id: "mma_mixed_martial_arts", label: "UFC" },
  ];

  // ═══ PORTFOLIO fetchers ═══
  const fetchPortfolio = useCallback(async () => {
    setPfLoading(true);
    setPfError("");
    try {
      if (pfMode === "coinstats") {
        if (!pfConfig.csApiKey || !pfConfig.csShareToken) {
          setPfError("Missing API key or ShareToken");
          setPfLoading(false);
          return;
        }
        const headers = { "X-API-KEY": pfConfig.csApiKey };
        const tokenParam = `shareToken=${encodeURIComponent(pfConfig.csShareToken)}`;

        // Try /portfolio/coins first (returns holdings array)
        let coinsRes = await fetch(
          `https://openapiv1.coinstats.app/portfolio/coins?${tokenParam}`,
          { headers }
        );

        // If that fails, try a few common alternates
        if (coinsRes.status === 404) {
          coinsRes = await fetch(
            `https://openapiv1.coinstats.app/portfolio/holdings?${tokenParam}`,
            { headers }
          );
        }

        if (!coinsRes.ok) {
          if (coinsRes.status === 401) setPfError("Invalid API key");
          else if (coinsRes.status === 403) setPfError("ShareToken invalid or requires Degen plan subscription");
          else if (coinsRes.status === 404) setPfError("Portfolio endpoint not found — verify your ShareToken is valid and your plan supports portfolio API access");
          else setPfError(`CoinStats error: ${coinsRes.status}`);
          setPfLoading(false);
          return;
        }
        const data = await coinsRes.json();

        // Handle multiple possible response shapes
        const rawCoins = data.coins || data.holdings || data.result || data.data || (Array.isArray(data) ? data : []);
        const holdings = rawCoins.map(c => ({
          symbol: (c.symbol || c.s || c.coin?.symbol || "?").toUpperCase(),
          name: c.name || c.n || c.coin?.name || c.symbol,
          value: parseFloat(c.value || c.totalValue || c.amount?.value || (c.count * (c.price || 0)) || 0),
          amount: parseFloat(c.count || c.amount?.count || c.amount || 0),
          price: parseFloat(c.price || c.coin?.price || 0),
          change24h: parseFloat(c.priceChange1d || c.change24h || c.coin?.priceChange1d || 0),
        })).filter(h => h.value > 0).sort((a, b) => b.value - a.value);

        if (holdings.length === 0) {
          setPfError("No holdings returned. Verify your ShareToken is from a portfolio with holdings, and that you're on a CoinStats Degen plan.");
          setPfLoading(false);
          return;
        }

        const total = holdings.reduce((sum, h) => sum + h.value, 0);
        const withAllocation = holdings.map(h => ({ ...h, allocation: total > 0 ? (h.value / total) * 100 : 0 }));
        setPfData({ source: "CoinStats", total, holdings: withAllocation });
      } else if (pfMode === "wallets") {
        if (!pfConfig.csApiKey) {
          setPfError("CoinStats API key needed for wallet lookups");
          setPfLoading(false);
          return;
        }
        if (!pfConfig.wallets.length) {
          setPfError("Add at least one wallet");
          setPfLoading(false);
          return;
        }
        const headers = { "X-API-KEY": pfConfig.csApiKey };
        const allHoldings = [];
        for (const w of pfConfig.wallets) {
          try {
            const r = await fetch(
              `https://openapiv1.coinstats.app/wallet/balance?address=${encodeURIComponent(w.address)}&connectionId=${w.chain}`,
              { headers }
            );
            if (!r.ok) continue;
            const d = await r.json();
            const coins = d.balances || d.coins || (Array.isArray(d) ? d : []);
            coins.forEach(c => {
              if ((c.value || c.totalValue || 0) <= 0.01) return;
              allHoldings.push({
                symbol: (c.symbol || c.s || "?").toUpperCase(),
                name: c.name || c.symbol,
                value: parseFloat(c.value || c.totalValue || 0),
                amount: parseFloat(c.count || c.amount || 0),
                price: parseFloat(c.price || 0),
                change24h: parseFloat(c.priceChange1d || 0),
                source: w.label || w.chain,
              });
            });
          } catch (e) { /* skip failed wallet */ }
        }
        // Aggregate same symbols across wallets
        const merged = {};
        allHoldings.forEach(h => {
          if (!merged[h.symbol]) merged[h.symbol] = { ...h, sources: [h.source] };
          else {
            merged[h.symbol].value += h.value;
            merged[h.symbol].amount += h.amount;
            if (!merged[h.symbol].sources.includes(h.source)) merged[h.symbol].sources.push(h.source);
          }
        });
        const holdings = Object.values(merged).sort((a, b) => b.value - a.value);
        const total = holdings.reduce((sum, h) => sum + h.value, 0);
        const withAllocation = holdings.map(h => ({ ...h, allocation: total > 0 ? (h.value / total) * 100 : 0 }));
        setPfData({ source: `${pfConfig.wallets.length} wallet${pfConfig.wallets.length > 1 ? "s" : ""}`, total, holdings: withAllocation });
      } else if (pfMode === "manual") {
        if (!pfConfig.manual.length) {
          setPfError("Add holdings below");
          setPfLoading(false);
          return;
        }
        // Fetch live prices via CoinGecko (no key needed)
        const symbols = pfConfig.manual.map(h => h.symbol.toLowerCase()).join(",");
        const cgMap = {
          btc: "bitcoin", eth: "ethereum", sol: "solana", xrp: "ripple",
          doge: "dogecoin", ada: "cardano", avax: "avalanche-2", link: "chainlink",
          matic: "matic-network", dot: "polkadot", ltc: "litecoin", bch: "bitcoin-cash",
          uni: "uniswap", atom: "cosmos", near: "near", apt: "aptos", hype: "hyperliquid",
        };
        const ids = pfConfig.manual.map(h => cgMap[h.symbol.toLowerCase()] || h.symbol.toLowerCase()).join(",");
        const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true`);
        if (!res.ok) {
          setPfError("Price fetch failed");
          setPfLoading(false);
          return;
        }
        const prices = await res.json();
        const holdings = pfConfig.manual.map(h => {
          const cgId = cgMap[h.symbol.toLowerCase()] || h.symbol.toLowerCase();
          const price = prices[cgId]?.usd || 0;
          const change24h = prices[cgId]?.usd_24h_change || 0;
          const value = price * parseFloat(h.amount || 0);
          const cost = parseFloat(h.avgPrice || 0) * parseFloat(h.amount || 0);
          const pl = value - cost;
          const plPct = cost > 0 ? (pl / cost) * 100 : 0;
          return {
            symbol: h.symbol.toUpperCase(), name: h.symbol.toUpperCase(),
            amount: parseFloat(h.amount || 0), price, value, change24h,
            avgPrice: parseFloat(h.avgPrice || 0), pl, plPct,
          };
        }).filter(h => h.value > 0).sort((a, b) => b.value - a.value);
        const total = holdings.reduce((sum, h) => sum + h.value, 0);
        const withAllocation = holdings.map(h => ({ ...h, allocation: total > 0 ? (h.value / total) * 100 : 0 }));
        setPfData({ source: "Manual", total, holdings: withAllocation });
      }
    } catch (e) {
      setPfError("Connection failed: " + (e.message || "unknown"));
    }
    setPfLoading(false);
  }, [pfMode, pfConfig]);

  // Auto-refresh portfolio every 60s when active
  useEffect(() => {
    if (pfMode === "none" || !verified) return;
    fetchPortfolio();
    const interval = setInterval(fetchPortfolio, 60000);
    return () => clearInterval(interval);
  }, [pfMode, verified, fetchPortfolio]);

  // Wallet/holding management helpers
  const addWallet = (chain, address, label) => {
    if (!address.trim()) return;
    setPfConfig(c => ({ ...c, wallets: [...c.wallets, { chain, address: address.trim(), label: label.trim() || chain }] }));
  };
  const removeWallet = (idx) => {
    setPfConfig(c => ({ ...c, wallets: c.wallets.filter((_, i) => i !== idx) }));
  };
  const addManual = (symbol, amount, avgPrice) => {
    if (!symbol.trim() || !amount) return;
    setPfConfig(c => ({ ...c, manual: [...c.manual, { symbol: symbol.trim().toUpperCase(), amount, avgPrice: avgPrice || 0 }] }));
  };
  const removeManual = (idx) => {
    setPfConfig(c => ({ ...c, manual: c.manual.filter((_, i) => i !== idx) }));
  };
  const disconnectPortfolio = () => {
    if (!window.confirm("Disconnect portfolio? Your config stays saved locally.")) return;
    setPfMode("none");
    setPfData(null);
  };
  const clearPortfolioConfig = () => {
    if (!window.confirm("Clear all portfolio data? This wipes saved keys, wallets, and manual entries.")) return;
    setPfMode("none");
    setPfData(null);
    setPfConfig({ csApiKey: "", csShareToken: "", wallets: [], manual: [] });
    try { localStorage.removeItem("nxmg-portfolio-config"); } catch {}
  };

  const sendChatMessage = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    const newUserMsg = { role: "user", content: text };
    const updated = [...chatMessages, newUserMsg];
    setChatMessages(updated);
    setChatInput("");
    setChatLoading(true);

    // Build live context from the data already loaded in Members Only
    const context = buildSportsContext(odds, scores, oddsSport, pfData);

    try {
      const res = await fetch("/api/sportsai-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: updated.slice(-20), context }),
      });
      if (!res.ok) throw new Error("Chat failed");
      const data = await res.json();
      setChatMessages(m => [...m, { role: "assistant", content: data.reply || "No response" }]);
    } catch (e) {
      setChatMessages(m => [...m, { role: "assistant", content: "⚠️ Connection failed. Try again in a moment." }]);
    }
    setChatLoading(false);
  }, [chatInput, chatMessages, chatLoading, odds, scores, oddsSport, pfData]);

  const clearChat = () => {
    setChatMessages([]);
    try { localStorage.removeItem("nxmg-sportsai-chat"); } catch {}
  };

  const fetchOddsMO = useCallback(async (sport) => {
    if (!MO_ODDS_KEY) return;
    setOddsLoading(true);
    try {
      // Fetch odds + scores in parallel
      const [oddsRes, scoresRes] = await Promise.all([
        fetch(`https://api.the-odds-api.com/v4/sports/${sport}/odds/?apiKey=${MO_ODDS_KEY}&regions=us&markets=h2h,spreads,totals&oddsFormat=american&bookmakers=bovada`),
        fetch(`https://api.the-odds-api.com/v4/sports/${sport}/scores/?apiKey=${MO_ODDS_KEY}&daysFrom=3`),
      ]);
      if (oddsRes.ok) {
        const oddsData = await oddsRes.json();
        setOdds(oddsData.slice(0, 15));
      }
      if (scoresRes.ok) {
        const scoresData = await scoresRes.json();
        setScores(scoresData);
      }
    } catch { setOdds([]); setScores([]); }
    setOddsLoading(false);
  }, [MO_ODDS_KEY]);

  // Helper: merge score data into a game
  const getGameScore = useCallback((gameId) => {
    return scores.find(s => s.id === gameId) || null;
  }, [scores]);

  useEffect(() => {
    if (!verified || !MO_ODDS_KEY) return;
    fetchOddsMO(oddsSport);
    const interval = setInterval(() => fetchOddsMO(oddsSport), 60000);
    return () => clearInterval(interval);
  }, [verified, oddsSport, fetchOddsMO, MO_ODDS_KEY]);

  // Build combined list: completed games (from scores) + upcoming (from odds)
  const mergedGames = useMemo(() => {
    // Start with odds games
    const oddsMap = new Map(odds.map(g => [g.id, g]));
    // Add completed games from scores that aren't in odds
    const completedFromScores = scores
      .filter(s => s.completed && !oddsMap.has(s.id))
      .map(s => ({ ...s, _scoresOnly: true }));
    // Combine: completed first, then upcoming/live
    const combined = [...completedFromScores, ...odds];
    // Sort: live first, then upcoming (soonest first), then completed (most recent first)
    return combined.sort((a, b) => {
      const aScore = getGameScore(a.id);
      const bScore = getGameScore(b.id);
      const aCompleted = aScore?.completed || a.completed;
      const bCompleted = bScore?.completed || b.completed;
      const now = new Date();
      const aLive = !aCompleted && new Date(a.commence_time) <= now;
      const bLive = !bCompleted && new Date(b.commence_time) <= now;
      // Live games first
      if (aLive && !bLive) return -1;
      if (!aLive && bLive) return 1;
      // Upcoming before completed
      if (!aCompleted && bCompleted) return -1;
      if (aCompleted && !bCompleted) return 1;
      // Within upcoming: soonest game first (ascending)
      if (!aCompleted && !bCompleted) return new Date(a.commence_time) - new Date(b.commence_time);
      // Within completed: most recent first (descending)
      return new Date(b.commence_time) - new Date(a.commence_time);
    }).slice(0, 15);
  }, [odds, scores, getGameScore]);

  // Fetch 7-day price charts
  const chartCoins = [
    { id: "bitcoin", symbol: "BTC", color: "#f7931a" },
    { id: "ethereum", symbol: "ETH", color: "#627eea" },
    { id: "solana", symbol: "SOL", color: "#9945ff" },
    { id: "hyperliquid", symbol: "HYPE", color: "#4caf50" },
  ];

  const fetchCharts = useCallback(async () => {
    setChartRefreshing(true);
    const data = {};
    await Promise.all(chartCoins.map(async (coin) => {
      try {
        const res = await fetch(`https://api.coingecko.com/api/v3/coins/${coin.id}/market_chart?vs_currency=usd&days=7`);
        const json = await res.json();
        if (json?.prices) {
          const prices = json.prices.map(p => p[1]);
          const current = prices[prices.length - 1];
          const prev = prices[0];
          const change = ((current - prev) / prev) * 100;
          data[coin.symbol] = { prices, current, change, color: coin.color, name: coin.id.charAt(0).toUpperCase() + coin.id.slice(1) };
        }
      } catch {}
    }));
    setChartData(data);
    setLastChartRefresh(new Date());
    setChartRefreshing(false);
  }, []);

  useEffect(() => {
    if (!verified) return;
    fetchCharts();
    const interval = setInterval(fetchCharts, 60000);
    return () => clearInterval(interval);
  }, [verified, fetchCharts]);

  // Check if user already verified
  useEffect(() => {
    const check = async () => {
      // Check localStorage first (instant)
      if (localStorage.getItem("nxmg-verified") === "true") {
        setVerified(true);
        setChecking(false);
        return;
      }
      // Then check Supabase (if signed in)
      if (userEmail) {
        try {
          const { data, error } = await supabase
            .from("invite_members")
            .select("id")
            .eq("email", userEmail)
            .maybeSingle();
          if (!error && data) {
            localStorage.setItem("nxmg-verified", "true");
            setVerified(true);
          }
        } catch {}
      }
      setChecking(false);
    };
    const timeout = setTimeout(() => setChecking(false), 3000);
    check().finally(() => clearTimeout(timeout));
  }, [userEmail]);

  // Load Jupiter Plugin script when verified (RPC-less - no endpoint needed)
  useEffect(() => {
    if (!verified) return;
    if (document.getElementById("jup-script")) { setJupLoaded(true); return; }
    const script = document.createElement("script");
    script.id = "jup-script";
    script.src = "https://plugin.jup.ag/main.js";
    script.setAttribute("data-preload", "");
    script.onload = () => setJupLoaded(true);
    document.head.appendChild(script);

    // Inject dark theme override styles for Jupiter
    const style = document.createElement("style");
    style.textContent = `
      #jupiter-terminal-embed .jupiter-terminal,
      #jupiter-terminal-embed [class*="bg-"] {
        background: #0e0e0c !important;
        color: #d4cdb4 !important;
      }
      .wallet-adapter-modal-wrapper {
        background: #0e0e0c !important;
        border: 1px solid rgba(184,160,74,0.2) !important;
        border-radius: 0 !important;
      }
      .wallet-adapter-modal-title {
        color: #d4cdb4 !important;
        font-family: 'Cormorant Garamond', serif !important;
        letter-spacing: 3px !important;
      }
      .wallet-adapter-modal-list .wallet-adapter-button {
        background: rgba(14,13,11,0.8) !important;
        border: 1px solid rgba(184,160,74,0.12) !important;
        border-radius: 0 !important;
        color: #d4cdb4 !important;
        transition: all 0.3s !important;
      }
      .wallet-adapter-modal-list .wallet-adapter-button:hover {
        background: rgba(184,160,74,0.08) !important;
        border-color: rgba(184,160,74,0.3) !important;
      }
      .wallet-adapter-modal-button-close {
        background: rgba(184,160,74,0.1) !important;
        color: #b8a04a !important;
      }
      .wallet-adapter-modal-overlay {
        background: rgba(6,6,4,0.85) !important;
        backdrop-filter: blur(12px) !important;
      }
      .wallet-adapter-modal-container {
        font-family: 'Outfit', sans-serif !important;
      }
    `;
    document.head.appendChild(style);
  }, [verified]);

  // Initialize Jupiter Plugin when script loads
  useEffect(() => {
    if (!jupLoaded || !window.Jupiter) return;
    const timer = setTimeout(() => {
      try {
        window.Jupiter.init({
          displayMode: "integrated",
          integratedTargetId: "jupiter-terminal-embed",
          containerStyles: {
            maxHeight: "90vh",
            width: "100%",
            borderRadius: "0px",
          },
          containerClassName: "",
          formProps: {
            initialInputMint: "So11111111111111111111111111111111111111112",
            initialOutputMint: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
          },
        });
      } catch (e) { console.log("Jupiter init:", e); }
    }, 800);
    return () => clearTimeout(timer);
  }, [jupLoaded]);

  // Submit invite code
  const submitCode = async () => {
    if (codeInput.trim().toUpperCase() !== INVITE_CODE) {
      setCodeError("Invalid invite code");
      return;
    }
    // Code is correct — grant access immediately
    localStorage.setItem("nxmg-verified", "true");
    setVerified(true);
    setCodeError("");
    // Try to persist in Supabase (non-blocking, best-effort)
    if (userEmail) {
      try {
        await supabase.from("invite_members").insert({
          email: userEmail,
          clerk_id: user?.id || null,
          verified_at: new Date().toISOString(),
        });
      } catch {}
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9500, background: BG, overflow: "auto",
    }}>
      <div style={{
        position: "absolute", top: "10%", right: "10%", width: "40vw", height: "40vw",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle, rgba(74,90,43,0.06) 0%, transparent 60%)",
      }} />

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 24px", position: "relative", zIndex: 1 }}>
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 8, background: "none",
          border: "none", cursor: "pointer", marginBottom: 40,
          fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 2, color: GOLD_DIM, textTransform: "uppercase",
        }}>
          <span style={{ fontSize: 16 }}>&larr;</span> Back to site
        </button>

        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontFamily: FONTS.display, fontSize: 32, fontWeight: 300, letterSpacing: 6, color: CREAM, marginBottom: 8 }}>
            NAUT<span style={{ color: RUBY, fontWeight: 600 }}>X</span>
          </div>
          <div style={{ fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 4, color: GOLD, textTransform: "uppercase" }}>Members Only</div>
          <div style={{ width: 40, height: 1, margin: "20px auto 0", background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
        </div>

        {checking ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <div style={{ width: 24, height: 24, border: `2px solid ${GOLD_DIM}`, borderTop: `2px solid ${GOLD}`, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
            <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD_DIM, letterSpacing: 3 }}>VERIFYING ACCESS...</div>
          </div>
        ) : !verified ? (
          <div style={{ maxWidth: 420, margin: "0 auto", textAlign: "center" }}>
            <div style={{ border: "1px solid rgba(184,160,74,0.15)", padding: "48px 36px", background: "rgba(14,13,11,0.8)", backdropFilter: "blur(12px)" }}>
              <div style={{ width: 56, height: 56, margin: "0 auto 24px", border: "1px solid rgba(184,160,74,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONTS.mono, fontSize: 22, color: GOLD }}>&#9919;</div>
              <div style={{ fontFamily: FONTS.display, fontSize: 24, color: CREAM, fontWeight: 300, marginBottom: 8 }}>Exclusive Access</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 14, color: CREAM_DIM, marginBottom: 32, lineHeight: 1.7 }}>This section is invite-only. Enter your access code to unlock.</div>
              <input type="text" value={codeInput} onChange={e => { setCodeInput(e.target.value); setCodeError(""); }}
                onKeyDown={e => e.key === "Enter" && submitCode()} placeholder="Enter invite code..."
                style={{ width: "100%", padding: "14px 18px", textAlign: "center", background: "rgba(20,20,16,0.9)", border: `1px solid ${codeError ? "rgba(155,27,48,0.5)" : "rgba(184,160,74,0.2)"}`, color: CREAM, fontFamily: FONTS.mono, fontSize: 14, letterSpacing: 4, textTransform: "uppercase", outline: "none" }} />
              {codeError && <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: RUBY, marginTop: 10 }}>{codeError}</div>}
              <button onClick={submitCode} style={{ width: "100%", marginTop: 16, padding: "14px", border: "1px solid rgba(184,160,74,0.3)", background: "rgba(184,160,74,0.08)", color: GOLD, cursor: "pointer", fontFamily: FONTS.mono, fontSize: 12, letterSpacing: 3, textTransform: "uppercase" }}>Unlock Access</button>
            </div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: "rgba(212,205,180,0.25)", marginTop: 20, letterSpacing: 2 }}>POWERED BY JUPITER PROTOCOL</div>
          </div>
        ) : (
          <div>
            {/* Price Charts */}
            {Object.keys(chartData).length > 0 && (
              <div style={{ marginBottom: 24 }}>
                {/* Chart header with refresh */}
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  marginBottom: 14,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 6, height: 6, borderRadius: "50%",
                      background: chartRefreshing ? "#fff" : "#4caf50",
                      boxShadow: chartRefreshing ? "0 0 10px #4caf50" : "none",
                      animation: "livePulse 2s infinite", transition: "all 0.3s",
                    }} />
                    <span style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 2, color: chartRefreshing ? "#4caf50" : CREAM_DIM }}>
                      {chartRefreshing ? "REFRESHING..." : "7D PRICE CHARTS"}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {lastChartRefresh && (
                      <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: "rgba(212,205,180,0.35)", letterSpacing: 2 }}>
                        {lastChartRefresh.toLocaleTimeString([], { hour12: false })}
                      </span>
                    )}
                    <button onClick={fetchCharts} disabled={chartRefreshing} style={{
                      background: "none", border: "1px solid rgba(184,160,74,0.2)",
                      color: GOLD_DIM, cursor: chartRefreshing ? "default" : "pointer",
                      fontFamily: FONTS.mono, fontSize: 9, padding: "5px 12px",
                      letterSpacing: 2, transition: "all 0.3s",
                      opacity: chartRefreshing ? 0.4 : 1,
                    }}>
                      {chartRefreshing ? "..." : "REFRESH"}
                    </button>
                  </div>
                </div>

                <div className="crypto-charts-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
                {Object.entries(chartData).map(([symbol, coin]) => {
                  const prices = coin.prices;
                  const min = Math.min(...prices);
                  const max = Math.max(...prices);
                  const range = max - min || 1;
                  const w = 260, h = 60;
                  const step = w / (prices.length - 1);
                  const points = prices.map((p, i) => `${i * step},${h - ((p - min) / range) * h}`).join(" ");
                  const isUp = coin.change >= 0;

                  return (
                    <div key={symbol} style={{
                      border: "1px solid rgba(184,160,74,0.12)", padding: "20px",
                      background: "rgba(14,13,11,0.8)",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <div>
                          <span style={{ fontFamily: FONTS.mono, fontSize: 14, color: CREAM, fontWeight: 500 }}>{symbol}</span>
                          <span style={{ fontFamily: FONTS.body, fontSize: 11, color: CREAM_DIM, marginLeft: 8 }}>{coin.name}</span>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontFamily: FONTS.mono, fontSize: 16, color: GOLD, fontWeight: 500 }}>
                            ${coin.current >= 1000 ? coin.current.toLocaleString("en-US", { maximumFractionDigits: 0 }) : coin.current?.toFixed(2)}
                          </div>
                          <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: isUp ? "#4caf50" : "#ef5350" }}>
                            {isUp ? "+" : ""}{coin.change?.toFixed(2)}% 7d
                          </div>
                        </div>
                      </div>
                      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: 60 }}>
                        <defs>
                          <linearGradient id={`grad-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={coin.color} stopOpacity="0.15" />
                            <stop offset="100%" stopColor={coin.color} stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        <polygon points={`0,${h} ${points} ${w},${h}`} fill={`url(#grad-${symbol})`} />
                        <polyline points={points} fill="none" stroke={coin.color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                      </svg>
                    </div>
                  );
                })}
              </div>
              </div>
            )}

            <div style={{ border: "1px solid rgba(184,160,74,0.15)", overflow: "hidden", background: "rgba(14,13,11,0.8)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderBottom: "1px solid rgba(184,160,74,0.1)", background: "rgba(10,10,8,0.6)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#4caf50", animation: "livePulse 2s infinite" }} />
                  <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD, letterSpacing: 2 }}>JUPITER SWAP</span>
                </div>
                <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2 }}>SOLANA MAINNET</span>
              </div>
              <div id="jupiter-terminal-embed" style={{ minHeight: 620, padding: "16px" }}>
                {!jupLoaded && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 620 }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ width: 24, height: 24, border: `2px solid ${GOLD_DIM}`, borderTop: `2px solid ${GOLD}`, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
                      <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD_DIM, letterSpacing: 3 }}>LOADING JUPITER...</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, flexWrap: "wrap", gap: 10 }}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2 }}>Signed in as {userEmail}</div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: "rgba(212,205,180,0.25)", letterSpacing: 2 }}>TRADES EXECUTE ON-CHAIN VIA JUPITER</div>
            </div>

            {/* ═══ PORTFOLIO TRACKER SECTION ═══ */}
            <div style={{ marginTop: 32 }}>
              <div style={{ border: "1px solid rgba(184,160,74,0.15)", overflow: "hidden", background: "rgba(14,13,11,0.8)" }}>
                {/* Header */}
                <div onClick={() => setPfOpen(!pfOpen)} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "14px 20px", borderBottom: pfOpen ? "1px solid rgba(184,160,74,0.1)" : "none",
                  background: "rgba(10,10,8,0.6)", cursor: "pointer", userSelect: "none",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 18 }}>📊</span>
                    <div>
                      <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: GOLD, letterSpacing: 2 }}>MY PORTFOLIO</div>
                      {pfData && (
                        <div style={{ fontFamily: FONTS.display, fontSize: 18, color: CREAM, fontWeight: 500, marginTop: 2 }}>
                          ${pfData.total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {pfMode !== "none" && pfData && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4caf50", animation: "livePulse 2s infinite" }} />
                        <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: "rgba(76,175,80,0.7)", letterSpacing: 2 }}>LIVE • {pfData.source}</span>
                      </div>
                    )}
                    <span style={{ color: GOLD_DIM, fontSize: 14, transform: pfOpen ? "rotate(180deg)" : "none", transition: "transform 0.3s" }}>▼</span>
                  </div>
                </div>

                {/* Body */}
                {pfOpen && (
                  <div style={{ padding: "20px" }}>
                    {/* Mode selector tabs */}
                    {pfMode === "none" ? (
                      <>
                        <div style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM_DIM, marginBottom: 18, lineHeight: 1.6 }}>
                          Connect your portfolio to track holdings live, see allocation breakdown, and let SportsAI analyze your positions in chat.
                        </div>

                        {/* Setup tabs */}
                        <div style={{ display: "flex", borderBottom: "1px solid rgba(184,160,74,0.12)", marginBottom: 18 }}>
                          {[
                            { id: "coinstats", label: "CoinStats Connect" },
                            { id: "wallets", label: "Wallets" },
                            { id: "manual", label: "Manual" },
                          ].map(t => (
                            <button key={t.id} onClick={() => setPfSetupTab(t.id)} style={{
                              padding: "10px 18px", background: "none",
                              border: "none", borderBottom: pfSetupTab === t.id ? `2px solid ${GOLD}` : "2px solid transparent",
                              color: pfSetupTab === t.id ? GOLD : CREAM_DIM, cursor: "pointer",
                              fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
                              transition: "all 0.2s",
                            }}>{t.label}</button>
                          ))}
                        </div>

                        {/* CoinStats setup */}
                        {pfSetupTab === "coinstats" && (
                          <div>
                            <div style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM_DIM, marginBottom: 14, lineHeight: 1.7 }}>
                              Pulls your <strong style={{ color: GOLD }}>full portfolio</strong> from CoinStats — total value, holdings, P/L, allocation. Requires CoinStats Degen plan for ShareToken access.
                            </div>
                            <div style={{ marginBottom: 12 }}>
                              <label style={{ fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2, color: CREAM_DIM, textTransform: "uppercase" }}>API Key</label>
                              <input type="password" value={pfConfig.csApiKey} onChange={e => setPfConfig(c => ({ ...c, csApiKey: e.target.value }))}
                                placeholder="From openapi.coinstats.app"
                                style={{ width: "100%", padding: "10px 12px", marginTop: 4, background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, fontFamily: FONTS.mono, fontSize: 12, outline: "none" }} />
                            </div>
                            <div style={{ marginBottom: 14 }}>
                              <label style={{ fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2, color: CREAM_DIM, textTransform: "uppercase" }}>ShareToken</label>
                              <input type="password" value={pfConfig.csShareToken} onChange={e => setPfConfig(c => ({ ...c, csShareToken: e.target.value }))}
                                placeholder="From your CoinStats portfolio Share link"
                                style={{ width: "100%", padding: "10px 12px", marginTop: 4, background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, fontFamily: FONTS.mono, fontSize: 12, outline: "none" }} />
                            </div>
                            <button onClick={() => { setPfMode("coinstats"); }} disabled={!pfConfig.csApiKey || !pfConfig.csShareToken}
                              style={{ padding: "10px 24px", background: (pfConfig.csApiKey && pfConfig.csShareToken) ? GOLD : "rgba(184,160,74,0.15)", color: (pfConfig.csApiKey && pfConfig.csShareToken) ? BG : GOLD_DIM, border: "none", cursor: (pfConfig.csApiKey && pfConfig.csShareToken) ? "pointer" : "default", fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 2, fontWeight: 600 }}>
                              CONNECT COINSTATS
                            </button>
                            <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: "rgba(212,205,180,0.4)", marginTop: 10, lineHeight: 1.6 }}>
                              ⓘ Read-only access. Keys stored in your browser only — never sent to our server.
                            </div>
                          </div>
                        )}

                        {/* Wallets setup */}
                        {pfSetupTab === "wallets" && (
                          <div>
                            <div style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM_DIM, marginBottom: 14, lineHeight: 1.7 }}>
                              Track <strong style={{ color: GOLD }}>any wallet address</strong> — BTC, ETH, SOL, and more. Aggregates balances across all your wallets. Free CoinStats API key required.
                            </div>
                            <div style={{ marginBottom: 14 }}>
                              <label style={{ fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2, color: CREAM_DIM, textTransform: "uppercase" }}>CoinStats API Key (Free)</label>
                              <input type="password" value={pfConfig.csApiKey} onChange={e => setPfConfig(c => ({ ...c, csApiKey: e.target.value }))}
                                placeholder="From openapi.coinstats.app"
                                style={{ width: "100%", padding: "10px 12px", marginTop: 4, background: "rgba(20,20,16,0.9)", border: "1px solid rgba(184,160,74,0.2)", color: CREAM, fontFamily: FONTS.mono, fontSize: 12, outline: "none" }} />
                            </div>

                            {/* Saved wallets */}
                            {pfConfig.wallets.length > 0 && (
                              <div style={{ marginBottom: 14, border: "1px solid rgba(184,160,74,0.1)", borderRadius: 4 }}>
                                {pfConfig.wallets.map((w, i) => (
                                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderBottom: i < pfConfig.wallets.length - 1 ? "1px solid rgba(184,160,74,0.06)" : "none" }}>
                                    <div>
                                      <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: CREAM }}>{w.label} <span style={{ color: GOLD_DIM, fontSize: 9, marginLeft: 6 }}>[{w.chain.toUpperCase()}]</span></div>
                                      <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, marginTop: 2 }}>{w.address.slice(0, 12)}...{w.address.slice(-8)}</div>
                                    </div>
                                    <button onClick={() => removeWallet(i)} style={{ background: "none", border: "1px solid rgba(155,27,48,0.3)", color: RUBY, padding: "4px 10px", cursor: "pointer", fontFamily: FONTS.mono, fontSize: 9 }}>REMOVE</button>
                                  </div>
                                ))}
                              </div>
                            )}

                            <WalletAddForm onAdd={addWallet} />

                            <button onClick={() => { setPfMode("wallets"); }} disabled={!pfConfig.csApiKey || pfConfig.wallets.length === 0}
                              style={{ padding: "10px 24px", marginTop: 12, background: (pfConfig.csApiKey && pfConfig.wallets.length) ? GOLD : "rgba(184,160,74,0.15)", color: (pfConfig.csApiKey && pfConfig.wallets.length) ? BG : GOLD_DIM, border: "none", cursor: (pfConfig.csApiKey && pfConfig.wallets.length) ? "pointer" : "default", fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 2, fontWeight: 600 }}>
                              TRACK WALLETS
                            </button>
                          </div>
                        )}

                        {/* Manual setup */}
                        {pfSetupTab === "manual" && (
                          <div>
                            <div style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM_DIM, marginBottom: 14, lineHeight: 1.7 }}>
                              Manually enter holdings with cost basis. Live prices via CoinGecko. <strong style={{ color: GOLD }}>P/L tracking included</strong> — no API keys needed.
                            </div>

                            {pfConfig.manual.length > 0 && (
                              <div style={{ marginBottom: 14, border: "1px solid rgba(184,160,74,0.1)", borderRadius: 4 }}>
                                {pfConfig.manual.map((h, i) => (
                                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderBottom: i < pfConfig.manual.length - 1 ? "1px solid rgba(184,160,74,0.06)" : "none" }}>
                                    <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: CREAM }}>
                                      {h.symbol} <span style={{ color: GOLD_DIM, marginLeft: 8 }}>{h.amount}</span>
                                      {h.avgPrice > 0 && <span style={{ color: CREAM_DIM, fontSize: 10, marginLeft: 8 }}>@ ${h.avgPrice}</span>}
                                    </div>
                                    <button onClick={() => removeManual(i)} style={{ background: "none", border: "1px solid rgba(155,27,48,0.3)", color: RUBY, padding: "4px 10px", cursor: "pointer", fontFamily: FONTS.mono, fontSize: 9 }}>REMOVE</button>
                                  </div>
                                ))}
                              </div>
                            )}

                            <ManualAddForm onAdd={addManual} />

                            <button onClick={() => { setPfMode("manual"); }} disabled={pfConfig.manual.length === 0}
                              style={{ padding: "10px 24px", marginTop: 12, background: pfConfig.manual.length ? GOLD : "rgba(184,160,74,0.15)", color: pfConfig.manual.length ? BG : GOLD_DIM, border: "none", cursor: pfConfig.manual.length ? "pointer" : "default", fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 2, fontWeight: 600 }}>
                              TRACK PORTFOLIO
                            </button>
                          </div>
                        )}
                      </>
                    ) : pfLoading && !pfData ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 40, flexDirection: "column", gap: 10 }}>
                        <div style={{ width: 22, height: 22, border: `2px solid ${GOLD_DIM}`, borderTop: `2px solid ${GOLD}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                        <span style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: GOLD_DIM }}>LOADING PORTFOLIO...</span>
                      </div>
                    ) : pfError ? (
                      <div>
                        <div style={{ padding: 20, background: "rgba(155,27,48,0.1)", border: "1px solid rgba(155,27,48,0.3)", marginBottom: 14 }}>
                          <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: RUBY, marginBottom: 6 }}>⚠ ERROR</div>
                          <div style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM_DIM }}>{pfError}</div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={fetchPortfolio} style={{ padding: "8px 16px", background: "rgba(184,160,74,0.1)", border: "1px solid rgba(184,160,74,0.3)", color: GOLD, cursor: "pointer", fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 2 }}>RETRY</button>
                          <button onClick={disconnectPortfolio} style={{ padding: "8px 16px", background: "none", border: "1px solid rgba(184,160,74,0.15)", color: GOLD_DIM, cursor: "pointer", fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 2 }}>RECONFIGURE</button>
                        </div>
                      </div>
                    ) : pfData ? (
                      <div>
                        {/* Holdings table */}
                        <div style={{ overflowX: "auto", marginBottom: 14 }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONTS.mono, fontSize: 12 }}>
                            <thead>
                              <tr style={{ borderBottom: "1px solid rgba(184,160,74,0.15)" }}>
                                {["Asset", "Amount", "Price", "Value", "Allocation", pfMode === "manual" ? "P/L" : "24h"].map(h => (
                                  <th key={h} style={{ padding: "10px 12px", textAlign: h === "Asset" ? "left" : "right", fontSize: 10, letterSpacing: 2, color: CREAM_DIM, textTransform: "uppercase", fontWeight: 500 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {pfData.holdings.slice(0, 20).map((h, i) => (
                                <tr key={i} style={{ borderBottom: "1px solid rgba(184,160,74,0.06)" }}>
                                  <td style={{ padding: "12px", color: CREAM, fontWeight: 500 }}>{h.symbol}</td>
                                  <td style={{ padding: "12px", textAlign: "right", color: CREAM_DIM }}>{h.amount.toLocaleString("en-US", { maximumFractionDigits: 4 })}</td>
                                  <td style={{ padding: "12px", textAlign: "right", color: CREAM_DIM }}>${h.price < 1 ? h.price.toFixed(4) : h.price.toFixed(2)}</td>
                                  <td style={{ padding: "12px", textAlign: "right", color: CREAM, fontWeight: 500 }}>${h.value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td style={{ padding: "12px", textAlign: "right", color: GOLD_DIM }}>
                                    {h.allocation.toFixed(1)}%
                                    <div style={{ width: "100%", height: 2, background: "rgba(184,160,74,0.1)", marginTop: 4 }}>
                                      <div style={{ width: `${h.allocation}%`, height: "100%", background: GOLD }} />
                                    </div>
                                  </td>
                                  <td style={{ padding: "12px", textAlign: "right", color: pfMode === "manual" ? (h.plPct >= 0 ? "#4caf50" : RUBY) : (h.change24h >= 0 ? "#4caf50" : RUBY) }}>
                                    {pfMode === "manual"
                                      ? (h.plPct != null ? `${h.plPct >= 0 ? "+" : ""}${h.plPct.toFixed(1)}%` : "—")
                                      : (h.change24h != null ? `${h.change24h >= 0 ? "+" : ""}${h.change24h.toFixed(2)}%` : "—")}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Footer actions */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: "1px solid rgba(184,160,74,0.08)", flexWrap: "wrap", gap: 10 }}>
                          <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 1 }}>
                            {pfData.holdings.length} {pfData.holdings.length === 1 ? "asset" : "assets"} • Auto-refresh 60s • Visible to SportsAI in chat
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={fetchPortfolio} disabled={pfLoading} style={{ padding: "5px 12px", background: "none", border: "1px solid rgba(184,160,74,0.15)", color: GOLD_DIM, cursor: pfLoading ? "default" : "pointer", fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2 }}>
                              {pfLoading ? "..." : "REFRESH"}
                            </button>
                            <button onClick={disconnectPortfolio} style={{ padding: "5px 12px", background: "none", border: "1px solid rgba(184,160,74,0.15)", color: GOLD_DIM, cursor: "pointer", fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2 }}>DISCONNECT</button>
                            <button onClick={clearPortfolioConfig} style={{ padding: "5px 12px", background: "none", border: "1px solid rgba(155,27,48,0.2)", color: RUBY, cursor: "pointer", fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2 }}>CLEAR ALL</button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            {/* ═══ BOVADA ODDS SECTION ═══ */}
            <div style={{ marginTop: 32 }}>
              <div style={{ border: "1px solid rgba(184,160,74,0.15)", overflow: "hidden", background: "rgba(14,13,11,0.8)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderBottom: "1px solid rgba(184,160,74,0.1)", background: "rgba(10,10,8,0.6)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#4caf50", animation: "livePulse 2s infinite" }} />
                    <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD, letterSpacing: 2 }}>BOVADA LINES & SCORES</span>
                  </div>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2 }}>AUTO-REFRESH 60S</span>
                </div>

                <div style={{ padding: "20px" }}>
                  {/* Sport selector */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
                    {MO_SPORTS.map((s) => (
                      <button key={s.id} onClick={() => setOddsSport(s.id)} style={{
                        background: oddsSport === s.id ? "rgba(184,160,74,0.15)" : "rgba(20,20,16,0.6)",
                        border: `1px solid ${oddsSport === s.id ? GOLD : "rgba(184,160,74,0.12)"}`,
                        color: oddsSport === s.id ? GOLD : CREAM_DIM,
                        padding: "6px 14px", cursor: "pointer",
                        fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 2, transition: "all 0.3s",
                      }}>{s.label}</button>
                    ))}
                  </div>

                  {oddsLoading ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 160, flexDirection: "column", gap: 12 }}>
                      <div style={{ width: 20, height: 20, border: `2px solid ${GOLD_DIM}`, borderTop: `2px solid ${GOLD}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                      <span style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: GOLD_DIM }}>LOADING LINES...</span>
                    </div>
                  ) : mergedGames.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 40, fontFamily: FONTS.mono, fontSize: 12, color: CREAM_DIM }}>
                      No events for {MO_SPORTS.find(s => s.id === oddsSport)?.label || oddsSport}
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {mergedGames.map((game, gi) => {
                        const bovada = game.bookmakers?.find(b => b.key === "bovada");
                        const h2h = bovada?.markets?.find(m => m.key === "h2h");
                        const spreads = bovada?.markets?.find(m => m.key === "spreads");
                        const totals = bovada?.markets?.find(m => m.key === "totals");
                        const gameDate = new Date(game.commence_time);

                        // Score data
                        const scoreData = getGameScore(game.id);
                        const isCompleted = scoreData?.completed || game.completed;
                        const isLive = !isCompleted && gameDate <= new Date();
                        const isUpcoming = !isCompleted && !isLive;
                        const gameScores = scoreData?.scores || game.scores || null;

                        // Determine winner
                        let homeScore = null, awayScore = null, winner = null;
                        if (gameScores && Array.isArray(gameScores)) {
                          const homeEntry = gameScores.find(s => s.name === game.home_team);
                          const awayEntry = gameScores.find(s => s.name === game.away_team);
                          homeScore = homeEntry ? parseInt(homeEntry.score) : null;
                          awayScore = awayEntry ? parseInt(awayEntry.score) : null;
                          if (isCompleted && homeScore != null && awayScore != null) {
                            winner = homeScore > awayScore ? "home" : awayScore > homeScore ? "away" : "tie";
                          }
                        }

                        const statusColor = isCompleted ? "rgba(184,160,74,0.5)" : isLive ? "#ef5350" : "rgba(184,160,74,0.15)";
                        const statusText = isCompleted ? "FINAL" : isLive ? "LIVE" : gameDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                        const cardBorder = isCompleted ? "rgba(184,160,74,0.2)" : isLive ? "rgba(239,83,80,0.2)" : "rgba(184,160,74,0.1)";

                        return (
                          <div key={gi} style={{
                            border: `1px solid ${cardBorder}`, padding: "16px 18px",
                            background: isCompleted ? "rgba(14,13,11,0.4)" : "rgba(14,13,11,0.6)",
                            transition: "border-color 0.3s",
                            opacity: isCompleted ? 0.85 : 1,
                          }}
                            onMouseEnter={(e) => e.currentTarget.style.borderColor = "rgba(184,160,74,0.3)"}
                            onMouseLeave={(e) => e.currentTarget.style.borderColor = cardBorder}
                          >
                            {/* Header — teams + scores + status */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: isCompleted && !bovada ? 0 : 12 }}>
                              <div style={{ flex: 1 }}>
                                {/* Away team */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                                  <div style={{
                                    fontFamily: FONTS.body, fontSize: 13, fontWeight: 500,
                                    color: isCompleted ? (winner === "away" ? GOLD : "rgba(212,205,180,0.35)") : CREAM,
                                  }}>
                                    {winner === "away" && <span style={{ marginRight: 6 }}>👑</span>}
                                    {game.away_team}
                                  </div>
                                  {awayScore != null && (
                                    <span style={{
                                      fontFamily: FONTS.mono, fontSize: 18, fontWeight: 600, marginLeft: 12, minWidth: 32, textAlign: "right",
                                      color: isCompleted ? (winner === "away" ? GOLD : "rgba(212,205,180,0.35)") : isLive ? "#ef5350" : CREAM,
                                    }}>{awayScore}</span>
                                  )}
                                </div>
                                {/* Home team */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <div style={{
                                    fontFamily: FONTS.body, fontSize: 13, fontWeight: 500,
                                    color: isCompleted ? (winner === "home" ? GOLD : "rgba(212,205,180,0.35)") : CREAM,
                                  }}>
                                    {winner === "home" && <span style={{ marginRight: 6 }}>👑</span>}
                                    {game.home_team}
                                  </div>
                                  {homeScore != null && (
                                    <span style={{
                                      fontFamily: FONTS.mono, fontSize: 18, fontWeight: 600, marginLeft: 12, minWidth: 32, textAlign: "right",
                                      color: isCompleted ? (winner === "home" ? GOLD : "rgba(212,205,180,0.35)") : isLive ? "#ef5350" : CREAM,
                                    }}>{homeScore}</span>
                                  )}
                                </div>
                              </div>

                              {/* Status badge */}
                              <div style={{ textAlign: "right", marginLeft: 16, flexShrink: 0 }}>
                                <div style={{
                                  display: "inline-flex", alignItems: "center", gap: 5,
                                  padding: "3px 8px", border: `1px solid ${statusColor}`,
                                  fontSize: 9, fontFamily: FONTS.mono, letterSpacing: 2,
                                  color: isCompleted ? GOLD_DIM : isLive ? "#ef5350" : CREAM_DIM,
                                }}>
                                  {isLive && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#ef5350", animation: "livePulse 2s infinite" }} />}
                                  {statusText}
                                </div>
                                {isUpcoming && (
                                  <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, marginTop: 3, letterSpacing: 1 }}>
                                    {gameDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Odds grid — only show for upcoming/live games with Bovada lines */}
                            {!isCompleted && bovada && (
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                                <div style={{ background: "rgba(20,20,16,0.8)", padding: "8px 10px", border: "1px solid rgba(184,160,74,0.06)" }}>
                                  <div style={{ fontFamily: FONTS.mono, fontSize: 8, letterSpacing: 2, color: CREAM_DIM, marginBottom: 5, textTransform: "uppercase" }}>Moneyline</div>
                                  {h2h?.outcomes?.map((o, oi) => (
                                    <div key={oi} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                                      <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM }}>{o.name?.length > 10 ? o.name.slice(0, 10) + "…" : o.name}</span>
                                      <span style={{ fontFamily: FONTS.mono, fontSize: 11, fontWeight: 500, color: o.price > 0 ? "#4caf50" : RUBY }}>{o.price > 0 ? "+" : ""}{o.price}</span>
                                    </div>
                                  ))}
                                </div>
                                <div style={{ background: "rgba(20,20,16,0.8)", padding: "8px 10px", border: "1px solid rgba(184,160,74,0.06)" }}>
                                  <div style={{ fontFamily: FONTS.mono, fontSize: 8, letterSpacing: 2, color: CREAM_DIM, marginBottom: 5, textTransform: "uppercase" }}>Spread</div>
                                  {spreads?.outcomes?.map((o, oi) => (
                                    <div key={oi} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                                      <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD_DIM }}>{o.point > 0 ? "+" : ""}{o.point}</span>
                                      <span style={{ fontFamily: FONTS.mono, fontSize: 11, fontWeight: 500, color: o.price > 0 ? "#4caf50" : RUBY }}>{o.price > 0 ? "+" : ""}{o.price}</span>
                                    </div>
                                  )) || <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: "rgba(212,205,180,0.3)" }}>—</div>}
                                </div>
                                <div style={{ background: "rgba(20,20,16,0.8)", padding: "8px 10px", border: "1px solid rgba(184,160,74,0.06)" }}>
                                  <div style={{ fontFamily: FONTS.mono, fontSize: 8, letterSpacing: 2, color: CREAM_DIM, marginBottom: 5, textTransform: "uppercase" }}>Total</div>
                                  {totals?.outcomes?.map((o, oi) => (
                                    <div key={oi} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                                      <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD_DIM }}>{o.name === "Over" ? "O" : "U"} {o.point}</span>
                                      <span style={{ fontFamily: FONTS.mono, fontSize: 11, fontWeight: 500, color: o.price > 0 ? "#4caf50" : RUBY }}>{o.price > 0 ? "+" : ""}{o.price}</span>
                                    </div>
                                  )) || <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: "rgba(212,205,180,0.3)" }}>—</div>}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                    <button onClick={() => fetchOddsMO(oddsSport)} style={{
                      background: "none", border: "1px solid rgba(184,160,74,0.15)", color: GOLD_DIM,
                      padding: "5px 14px", cursor: "pointer", fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2,
                    }}>REFRESH ODDS & SCORES</button>
                  </div>
                </div>
              </div>
            </div>

            {/* ═══ SPORTSAI CHAT BUBBLE ═══ */}
            <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9600 }}>
              {/* Floating trigger button */}
              {!chatOpen && (
                <button onClick={() => setChatOpen(true)} style={{
                  width: 60, height: 60, borderRadius: "50%", cursor: "pointer",
                  background: `linear-gradient(135deg, ${GOLD}, #8b7830)`,
                  border: "2px solid rgba(212,205,180,0.3)",
                  boxShadow: "0 8px 24px rgba(184,160,74,0.4), 0 0 0 0 rgba(184,160,74,0.4)",
                  animation: "chatPulse 2.5s infinite",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 26,
                }} title="Chat with NautX SportsAI">🏆</button>
              )}

              {/* Chat panel */}
              {chatOpen && (
                <div style={{
                  width: "min(420px, calc(100vw - 48px))",
                  height: "min(600px, calc(100vh - 100px))",
                  background: "rgba(10,10,8,0.98)", backdropFilter: "blur(16px)",
                  border: `1px solid ${GOLD}`, borderRadius: 12, overflow: "hidden",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(184,160,74,0.15)",
                  display: "flex", flexDirection: "column",
                  animation: "chatSlideUp 0.3s cubic-bezier(0.16,1,0.3,1)",
                }}>
                  {/* Chat header */}
                  <div style={{
                    padding: "14px 18px", borderBottom: `1px solid rgba(184,160,74,0.15)`,
                    background: "rgba(14,13,11,0.8)",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: "50%",
                        background: `linear-gradient(135deg, ${GOLD}, #8b7830)`,
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                      }}>🏆</div>
                      <div>
                        <div style={{ fontFamily: FONTS.display, fontSize: 15, color: CREAM, fontWeight: 500 }}>NautX SportsAI</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 1 }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#4caf50", animation: "livePulse 2s infinite" }} />
                          <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: "rgba(76,175,80,0.8)", letterSpacing: 1 }}>ONLINE • CLAUDE OPUS 4.7</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {chatMessages.length > 0 && (
                        <button onClick={clearChat} title="Clear chat" style={{
                          background: "none", border: "1px solid rgba(184,160,74,0.2)",
                          color: GOLD_DIM, cursor: "pointer", width: 28, height: 28, borderRadius: 4,
                          fontFamily: FONTS.mono, fontSize: 12,
                        }}>✕</button>
                      )}
                      <button onClick={() => setChatOpen(false)} title="Minimize" style={{
                        background: "none", border: "1px solid rgba(184,160,74,0.2)",
                        color: GOLD_DIM, cursor: "pointer", width: 28, height: 28, borderRadius: 4,
                        fontFamily: FONTS.mono, fontSize: 14,
                      }}>–</button>
                    </div>
                  </div>

                  {/* Messages area */}
                  <div ref={chatScrollRef} style={{
                    flex: 1, overflowY: "auto", padding: "18px",
                    display: "flex", flexDirection: "column", gap: 12,
                  }}>
                    {chatMessages.length === 0 && (
                      <div style={{ textAlign: "center", padding: "20px 8px", color: CREAM_DIM }}>
                        <div style={{ fontSize: 36, marginBottom: 10 }}>🏆</div>
                        <div style={{ fontFamily: FONTS.display, fontSize: 17, color: CREAM, fontWeight: 500, marginBottom: 6 }}>Welcome to SportsAI</div>
                        <div style={{ fontFamily: FONTS.body, fontSize: 12, lineHeight: 1.6, maxWidth: 280, margin: "0 auto", color: CREAM_DIM }}>
                          Ask about picks, parlays, injury reports, pitcher matchups, bankroll strategy, or anything sports betting related.
                        </div>
                        <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 6 }}>
                          {["Best MLB pick today?", "Analyze a parlay for me", "What's the smart bankroll strategy?"].map(q => (
                            <button key={q} onClick={() => { setChatInput(q); }} style={{
                              background: "rgba(184,160,74,0.05)", border: "1px solid rgba(184,160,74,0.15)",
                              color: GOLD_DIM, padding: "8px 12px", cursor: "pointer",
                              fontFamily: FONTS.mono, fontSize: 11, textAlign: "left", borderRadius: 4,
                              transition: "all 0.2s",
                            }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(184,160,74,0.1)"; e.currentTarget.style.color = GOLD; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(184,160,74,0.05)"; e.currentTarget.style.color = GOLD_DIM; }}
                            >&rarr; {q}</button>
                          ))}
                        </div>
                      </div>
                    )}

                    {chatMessages.map((m, i) => (
                      <div key={i} style={{
                        display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                      }}>
                        <div style={{
                          maxWidth: "82%", padding: "10px 14px",
                          background: m.role === "user" ? `rgba(184,160,74,0.15)` : "rgba(20,20,16,0.8)",
                          border: `1px solid ${m.role === "user" ? "rgba(184,160,74,0.3)" : "rgba(184,160,74,0.1)"}`,
                          borderRadius: 8, fontFamily: FONTS.body, fontSize: 13, lineHeight: 1.6,
                          color: m.role === "user" ? CREAM : "rgba(212,205,180,0.9)",
                          wordBreak: "break-word",
                        }}>
                          {m.role === "user" ? (
                            <span style={{ whiteSpace: "pre-wrap" }}>{m.content}</span>
                          ) : (
                            <MarkdownChat text={m.content} />
                          )}
                        </div>
                      </div>
                    ))}

                    {chatLoading && (
                      <div style={{ display: "flex", justifyContent: "flex-start" }}>
                        <div style={{
                          padding: "12px 16px", background: "rgba(20,20,16,0.8)",
                          border: "1px solid rgba(184,160,74,0.1)", borderRadius: 8,
                          display: "flex", gap: 4,
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: GOLD, animation: "typingDot 1.4s infinite 0s" }} />
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: GOLD, animation: "typingDot 1.4s infinite 0.2s" }} />
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: GOLD, animation: "typingDot 1.4s infinite 0.4s" }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Input area */}
                  <div style={{
                    padding: "12px 14px", borderTop: "1px solid rgba(184,160,74,0.12)",
                    background: "rgba(14,13,11,0.8)",
                  }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendChatMessage())}
                        placeholder="Ask about picks, parlays, injuries..."
                        disabled={chatLoading}
                        style={{
                          flex: 1, padding: "10px 14px", background: "rgba(20,20,16,0.9)",
                          border: "1px solid rgba(184,160,74,0.2)", borderRadius: 6,
                          color: CREAM, fontFamily: FONTS.body, fontSize: 13, outline: "none",
                        }} />
                      <button onClick={sendChatMessage} disabled={chatLoading || !chatInput.trim()} style={{
                        padding: "10px 16px", background: chatInput.trim() && !chatLoading ? GOLD : "rgba(184,160,74,0.15)",
                        border: "none", color: chatInput.trim() && !chatLoading ? BG : GOLD_DIM,
                        cursor: chatInput.trim() && !chatLoading ? "pointer" : "default", borderRadius: 6,
                        fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 1, fontWeight: 600,
                        transition: "all 0.2s",
                      }}>{chatLoading ? "..." : "SEND"}</button>
                    </div>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 8, color: "rgba(212,205,180,0.3)", marginTop: 6, letterSpacing: 1, textAlign: "center" }}>
                      POWERED BY CLAUDE • PICKS LOG TO GOOGLE SHEETS VIA TELEGRAM
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CryptoCenter({ onBack }) {
  return (
    <>
      <SignedIn><CryptoCenterInner onBack={onBack} /></SignedIn>
      <SignedOut>
        <div style={{ position: "fixed", inset: 0, zIndex: 9500, background: "rgba(6,6,4,0.97)", backdropFilter: "blur(20px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontFamily: FONTS.display, fontSize: 24, color: CREAM, letterSpacing: 6, marginBottom: 8 }}>NAUT<span style={{ color: RUBY }}>X</span> MEMBERS ONLY</div>
          <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 3, marginBottom: 32 }}>SIGN IN TO CONTINUE</div>
          <SignIn appearance={{ variables: { colorPrimary: "#b8a04a", colorBackground: "#0e0e0c", colorText: "#d4cdb4", colorInputBackground: "#141410", colorInputText: "#d4cdb4" } }} />
          <button onClick={onBack} style={{ marginTop: 24, background: "none", border: "1px solid rgba(184,160,74,0.2)", color: GOLD_DIM, cursor: "pointer", fontFamily: FONTS.mono, fontSize: 10, padding: "8px 20px", letterSpacing: 2 }}>Cancel</button>
        </div>
      </SignedOut>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  CONTACT
 * ═══════════════════════════════════════════════════════════════ */
function Contact({ onLinks }) {
  return (
    <section id="contact" style={{ padding: "120px 36px 80px" }}>
      <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
        <Reveal><SectionLabel text="Contact" num="05" /></Reveal>

        <Reveal delay={0.1}>
          <h2 style={{
            fontFamily: FONTS.display, fontSize: "clamp(34px, 5vw, 60px)",
            fontWeight: 300, color: CREAM, margin: "0 0 12px", lineHeight: 1.15,
          }}>
            Got a vision?
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <h2 style={{
            fontFamily: FONTS.display, fontSize: "clamp(34px, 5vw, 60px)",
            fontWeight: 300, fontStyle: "italic", color: GOLD, margin: "0 0 28px", lineHeight: 1.15,
          }}>
            Let's make it real.
          </h2>
        </Reveal>
        <Reveal delay={0.3}>
          <p style={{
            fontFamily: FONTS.body, fontSize: 15, lineHeight: 1.85,
            color: CREAM_DIM, marginBottom: 52, maxWidth: 540, margin: "0 auto 52px",
          }}>
            Whether it's a full platform build, a brand overhaul, or a Web3 project —
            if you're serious about it, I'm in. Serious inquiries only.
          </p>
        </Reveal>
        <Reveal delay={0.4}>
          <MagBtn variant="fill" onClick={onLinks}>
            Connect With Me <span style={{ fontSize: 16, lineHeight: 1 }}>&#8599;</span>
          </MagBtn>
        </Reveal>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  FOOTER
 * ═══════════════════════════════════════════════════════════════ */
function Footer() {
  return (
    <footer style={{
      padding: "36px 36px", borderTop: "1px solid rgba(184,160,74,0.12)",
      display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
      background: "rgba(8,8,10,0.92)", position: "relative", zIndex: 2,
    }}>
      <span style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 2, color: "rgba(212,205,180,0.4)" }}>
        &copy; {new Date().getFullYear()} NAUT X MEDIA GROUP
      </span>
      <span style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 2, color: "rgba(212,205,180,0.3)" }}>
        BUILT FROM SCRATCH. NO TEMPLATES.
      </span>
    </footer>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  ADMIN PANEL
 *  Access: press Ctrl+Shift+A or click the hidden dot in footer
 *  Auth: Clerk sign-in, then checks against ADMIN_EMAIL
 * ═══════════════════════════════════════════════════════════════ */

const inputStyle = {
  width: "100%", padding: "10px 14px", background: "rgba(20,20,16,0.9)",
  border: "1px solid rgba(184,160,74,0.2)", color: CREAM,
  fontFamily: FONTS.body, fontSize: 14, outline: "none",
  transition: "border-color 0.3s",
};
const textareaStyle = { ...inputStyle, minHeight: 80, resize: "vertical" };
const adminBtnStyle = {
  padding: "10px 24px", border: "1px solid rgba(184,160,74,0.3)",
  background: "rgba(184,160,74,0.08)", color: GOLD, cursor: "pointer",
  fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 2,
  textTransform: "uppercase", transition: "all 0.3s",
};
const dangerBtnStyle = { ...adminBtnStyle, borderColor: "rgba(155,27,48,0.4)", color: RUBY, background: "rgba(155,27,48,0.08)" };
const saveBtnStyle = { ...adminBtnStyle, borderColor: "rgba(76,175,80,0.4)", color: "#4caf50", background: "rgba(76,175,80,0.08)" };

function AdminInner({ onClose }) {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState([]);
  const [projects, setProjects] = useState([]);
  const [content, setContent] = useState([]);
  const [socialData, setSocialData] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // 🔒 Admin check — verifies email against ADMIN_EMAILS whitelist.
  // This is UX gating only. The REAL security is Supabase RLS (admin-schema.sql)
  // which checks the Clerk JWT email server-side on every write.
  const isAdmin = user?.emailAddresses?.some(e =>
    ADMIN_EMAILS.includes(e.emailAddress?.toLowerCase())
  ) ?? false;

  // 🔒 Returns a Supabase client authenticated with the user's Clerk JWT.
  // RLS policies can then verify auth.jwt()->>'email' matches an admin.
  // Setup: in Clerk dashboard → JWT Templates → create "supabase" template.
  // Then in Supabase → Settings → Auth → JWT Settings → paste Clerk's JWKS URL.
  const getAuthedSupabase = useCallback(async () => {
    // Use anon client — admin access is gated by ADMIN_EMAILS check above,
    // and Supabase RLS policies should allow public writes on admin tables
    // OR you can set up service role via edge function for production hardening.
    return supabase;
  }, []);

  // 🔒 Input validators — defense in depth alongside RLS
  const validateInt = (v, max = 999999999) => {
    const n = parseInt(v);
    if (isNaN(n) || n < 0 || n > max) return null;
    return n;
  };
  const validateText = (v, maxLen = 5000) => {
    if (typeof v !== "string") return null;
    const trimmed = v.trim();
    if (trimmed.length > maxLen) return null;
    return trimmed;
  };
  const validateEmail = (v) => {
    if (typeof v !== "string") return null;
    const trimmed = v.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return null;
    if (trimmed.length > 254) return null;
    return trimmed;
  };
  const validateColor = (v) => {
    if (typeof v !== "string") return null;
    if (!/^#[0-9a-fA-F]{6}$/.test(v.trim())) return null;
    return v.trim();
  };
  const ALLOWED_ROLES = ["admin", "moderator", "vip", "member", "banned"];
  const ALLOWED_STATUSES = ["active", "suspended", "banned"];

  // Load all data from Supabase
  useEffect(() => {
    const load = async () => {
      const [s, p, c, soc, mem] = await Promise.all([
        supabase.from("site_stats").select("*").order("key"),
        supabase.from("projects").select("*").order("sort_order"),
        supabase.from("site_content").select("*").order("key"),
        supabase.from("social_stats").select("*").order("platform"),
        supabase.from("members").select("*").order("created_at", { ascending: false }),
      ]);
      if (s.data) setStats(s.data);
      if (p.data) setProjects(p.data);
      if (c.data) setContent(c.data);
      if (soc.data) setSocialData(soc.data);
      if (mem.data) setMembers(mem.data);
    };
    load();
  }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2500); };

  // --- STATS ---
  const updateStat = async (id, value) => {
    const num = validateInt(value);
    if (num === null) { flash("Invalid number"); return; }
    setSaving(true);
    const sb = await getAuthedSupabase();
    const { error } = await sb.from("site_stats")
      .update({ value: num, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) { flash("Save failed: " + error.message); setSaving(false); return; }
    setStats(prev => prev.map(s => s.id === id ? { ...s, value: num } : s));
    setSaving(false);
    flash("Stat updated");
  };

  // --- SOCIAL STATS ---
  const updateSocial = async (id, field, value) => {
    const numVal = field === "engagement_rate" ? (parseFloat(value) || 0) : validateInt(value);
    if (numVal === null) return;
    const sb = await getAuthedSupabase();
    const { error } = await sb.from("social_stats")
      .update({ [field]: numVal, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) { flash("Save failed"); return; }
    setSocialData(prev => prev.map(s => s.id === id ? { ...s, [field]: numVal } : s));
  };

  const saveSocial = async (id) => {
    setSaving(true);
    const row = socialData.find(s => s.id === id);
    if (row) {
      const { id: rid, ...rest } = row;
      const sb = await getAuthedSupabase();
      const { error } = await sb.from("social_stats")
        .update({ ...rest, updated_at: new Date().toISOString() })
        .eq("id", rid);
      if (error) { flash("Save failed"); setSaving(false); return; }
    }
    setSaving(false);
    flash("Social stats saved");
  };

  // --- MEMBERS ---
  const updateMember = async (id, field, value) => {
    // Validate based on field
    let cleanValue = value;
    if (field === "email") {
      cleanValue = validateEmail(value);
      if (!cleanValue) { flash("Invalid email"); return; }
    } else if (field === "role") {
      if (!ALLOWED_ROLES.includes(value)) { flash("Invalid role"); return; }
    } else if (field === "status") {
      if (!ALLOWED_STATUSES.includes(value)) { flash("Invalid status"); return; }
    } else if (typeof value === "string") {
      cleanValue = validateText(value, 1000);
      if (cleanValue === null) { flash("Invalid input"); return; }
    }
    const sb = await getAuthedSupabase();
    const { error } = await sb.from("members")
      .update({ [field]: cleanValue, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) { flash("Save failed"); return; }
    setMembers(prev => prev.map(m => m.id === id ? { ...m, [field]: cleanValue } : m));
    flash(`Member ${field} updated`);
  };

  const banMember = async (id, banned) => {
    const sb = await getAuthedSupabase();
    const { error } = await sb.from("members").update({
      status: banned ? "banned" : "active",
      role: banned ? "banned" : "member",
      updated_at: new Date().toISOString(),
    }).eq("id", id);
    if (error) { flash("Action failed"); return; }
    setMembers(prev => prev.map(m => m.id === id ? { ...m, status: banned ? "banned" : "active", role: banned ? "banned" : "member" } : m));
    flash(banned ? "Member banned" : "Member unbanned");
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Delete this member permanently? This cannot be undone.")) return;
    const sb = await getAuthedSupabase();
    const { error } = await sb.from("members").delete().eq("id", id);
    if (error) { flash("Delete failed"); return; }
    setMembers(prev => prev.filter(m => m.id !== id));
    flash("Member removed");
  };

  const filteredMembers = members.filter(m => {
    if (!memberSearch) return true;
    const q = memberSearch.toLowerCase();
    return (m.email || "").toLowerCase().includes(q) ||
           (m.username || "").toLowerCase().includes(q) ||
           (m.role || "").toLowerCase().includes(q) ||
           (m.status || "").toLowerCase().includes(q);
  });

  // --- PROJECTS ---
  const addProject = async () => {
    const sb = await getAuthedSupabase();
    const { data, error } = await sb.from("projects").insert({
      title: "New Project", type: "PROJECT TYPE", description: "Description here",
      tech: "React, Supabase", accent_color: "#b8a04a", year: new Date().getFullYear().toString(),
      status: "IN PROGRESS", sort_order: projects.length,
    }).select();
    if (error) { flash("Add failed: " + error.message); return; }
    if (data) setProjects([...projects, data[0]]);
    flash("Project added");
  };

  const updateProject = async (id, field, value) => {
    let cleanValue = value;
    if (field === "accent_color") {
      cleanValue = validateColor(value);
      if (!cleanValue) { flash("Invalid hex color"); return; }
    } else if (field === "sort_order") {
      cleanValue = validateInt(value, 9999);
      if (cleanValue === null) { flash("Invalid sort order"); return; }
    } else if (typeof value === "string") {
      const maxLen = field === "description" ? 5000 : 500;
      cleanValue = validateText(value, maxLen);
      if (cleanValue === null) { flash(`Too long (max ${maxLen} chars)`); return; }
    }
    const sb = await getAuthedSupabase();
    const { error } = await sb.from("projects").update({ [field]: cleanValue }).eq("id", id);
    if (error) { flash("Save failed: " + error.message); return; }
    setProjects(prev => prev.map(p => p.id === id ? { ...p, [field]: cleanValue } : p));
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Delete this project permanently?")) return;
    const sb = await getAuthedSupabase();
    const { error } = await sb.from("projects").delete().eq("id", id);
    if (error) { flash("Delete failed: " + error.message); return; }
    setProjects(prev => prev.filter(p => p.id !== id));
    flash("Project deleted");
  };

  // --- CONTENT ---
  const updateContent = async (id, value) => {
    const clean = validateText(value, 10000);
    if (clean === null) { flash("Content too long (max 10K chars)"); return; }
    const sb = await getAuthedSupabase();
    const { error } = await sb.from("site_content")
      .update({ value: clean, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) { flash("Save failed"); return; }
    setContent(prev => prev.map(c => c.id === id ? { ...c, value: clean } : c));
    flash("Content saved");
  };

  const addContent = async (key, value) => {
    const cleanKey = validateText(key, 100);
    const cleanValue = validateText(value, 10000);
    if (!cleanKey || cleanValue === null) { flash("Invalid input"); return; }
    if (!/^[a-z0-9_]+$/.test(cleanKey)) { flash("Key must be lowercase letters, numbers, underscores"); return; }
    const sb = await getAuthedSupabase();
    const { data, error } = await sb.from("site_content").insert({ key: cleanKey, value: cleanValue }).select();
    if (error) { flash("Add failed: " + error.message); return; }
    if (data) setContent([...content, data[0]]);
    flash("Content added");
  };

  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "members", label: "Members" },
    { id: "projects", label: "Projects" },
    { id: "stats", label: "Stats" },
    { id: "content", label: "Content" },
    { id: "tracking", label: "Tracking" },
    { id: "social", label: "Social Stats" },
    { id: "sportsai", label: "SportsAI Bot" },
  ];

  if (!isAdmin) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <div style={{ fontFamily: FONTS.mono, fontSize: 14, color: RUBY, marginBottom: 12 }}>ACCESS DENIED</div>
        <div style={{ fontFamily: FONTS.body, fontSize: 14, color: CREAM_DIM }}>This email is not authorized as admin.</div>
      </div>
    );
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10000,
      background: "rgba(6,6,4,0.97)", backdropFilter: "blur(20px)",
      overflow: "auto",
    }}>
      {/* Top bar */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10001,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 28px", background: "rgba(10,10,8,0.95)",
        borderBottom: "1px solid rgba(184,160,74,0.12)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontFamily: FONTS.display, fontSize: 18, color: CREAM, letterSpacing: 4 }}>
            NAUT<span style={{ color: RUBY }}>X</span>
          </span>
          <span style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 3, color: GOLD, padding: "4px 12px", border: "1px solid rgba(184,160,74,0.3)" }}>
            ADMIN
          </span>
          {msg && <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: "#4caf50", letterSpacing: 1, animation: "fadeIn 0.3s" }}>{msg}</span>}
        </div>
        <button onClick={onClose} style={{
          ...adminBtnStyle, padding: "8px 20px", fontSize: 10,
        }}>Close Panel</button>
      </div>

      <div className="admin-layout" style={{ display: "flex", minHeight: "calc(100vh - 56px)" }}>
        {/* Sidebar */}
        <div className="admin-sidebar" style={{
          width: 220, padding: "24px 0", borderRight: "1px solid rgba(184,160,74,0.08)",
          background: "rgba(10,10,8,0.6)", flexShrink: 0,
        }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              display: "block", width: "100%", padding: "12px 24px", border: "none",
              background: tab === t.id ? "rgba(184,160,74,0.08)" : "transparent",
              borderLeft: tab === t.id ? `2px solid ${GOLD}` : "2px solid transparent",
              color: tab === t.id ? GOLD : CREAM_DIM, cursor: "pointer",
              fontFamily: FONTS.mono, fontSize: 11, letterSpacing: 2,
              textTransform: "uppercase", textAlign: "left", transition: "all 0.2s",
            }}>{t.label}</button>
          ))}
          <div className="admin-sidebar-user" style={{ padding: "20px 24px", marginTop: 20, borderTop: "1px solid rgba(184,160,74,0.06)" }}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 1 }}>
              Logged in as
            </div>
            <div style={{ fontFamily: FONTS.body, fontSize: 12, color: CREAM, marginTop: 4, wordBreak: "break-all" }}>
              {user?.emailAddresses?.[0]?.emailAddress || "Unknown"}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="admin-main" style={{ flex: 1, padding: "32px 36px", maxWidth: 900 }}>

          {/* DASHBOARD TAB */}
          {tab === "dashboard" && (
            <div>
              <h2 style={{ fontFamily: FONTS.display, fontSize: 28, color: CREAM, fontWeight: 300, margin: "0 0 28px" }}>
                Dashboard
              </h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 36 }}>
                {stats.map(s => (
                  <div key={s.id} style={{ border: "1px solid rgba(184,160,74,0.12)", padding: "20px", background: "rgba(14,13,11,0.6)" }}>
                    <div style={{ fontFamily: FONTS.display, fontSize: 36, color: GOLD, fontWeight: 300 }}>{s.value}</div>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2, marginTop: 6, textTransform: "uppercase" }}>{s.key.replace(/_/g, " ")}</div>
                  </div>
                ))}
                <div style={{ border: "1px solid rgba(184,160,74,0.12)", padding: "20px", background: "rgba(14,13,11,0.6)" }}>
                  <div style={{ fontFamily: FONTS.display, fontSize: 36, color: CREAM, fontWeight: 300 }}>{projects.length}</div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2, marginTop: 6, textTransform: "uppercase" }}>Total Projects</div>
                </div>
                <div style={{ border: "1px solid rgba(184,160,74,0.12)", padding: "20px", background: "rgba(14,13,11,0.6)" }}>
                  <div style={{ fontFamily: FONTS.display, fontSize: 36, color: "#4caf50", fontWeight: 300 }}>{members.filter(m => m.status === "active").length}</div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2, marginTop: 6, textTransform: "uppercase" }}>Active Members</div>
                </div>
                <div style={{ border: "1px solid rgba(184,160,74,0.12)", padding: "20px", background: "rgba(14,13,11,0.6)" }}>
                  <div style={{ fontFamily: FONTS.display, fontSize: 36, color: RUBY, fontWeight: 300 }}>{members.filter(m => m.status === "banned").length}</div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 2, marginTop: 6, textTransform: "uppercase" }}>Banned</div>
                </div>
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: CREAM_DIM, lineHeight: 2 }}>
                <div>Use the sidebar tabs to manage your site content.</div>
                <div>Changes save directly to Supabase and reflect on the live site.</div>
                <div>Keyboard shortcut: <span style={{ color: GOLD }}>Ctrl + Shift + A</span> to toggle this panel.</div>
              </div>
            </div>
          )}

          {/* MEMBERS TAB */}
          {tab === "members" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 14 }}>
                <h2 style={{ fontFamily: FONTS.display, fontSize: 28, color: CREAM, fontWeight: 300, margin: 0 }}>
                  Members <span style={{ fontFamily: FONTS.mono, fontSize: 13, color: GOLD_DIM }}>({members.length})</span>
                </h2>
                <input
                  type="text" placeholder="Search members..."
                  value={memberSearch} onChange={e => setMemberSearch(e.target.value)}
                  style={{ ...inputStyle, width: 260, padding: "8px 14px", fontSize: 12 }}
                />
              </div>

              {/* Member stats row */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12, marginBottom: 24 }}>
                {[
                  { label: "TOTAL", val: members.length, color: CREAM },
                  { label: "ACTIVE", val: members.filter(m => m.status === "active").length, color: "#4caf50" },
                  { label: "ADMINS", val: members.filter(m => m.role === "admin").length, color: GOLD },
                  { label: "BANNED", val: members.filter(m => m.status === "banned").length, color: RUBY },
                ].map(s => (
                  <div key={s.label} style={{ border: "1px solid rgba(184,160,74,0.08)", padding: "14px", background: "rgba(14,13,11,0.5)" }}>
                    <div style={{ fontFamily: FONTS.display, fontSize: 28, color: s.color, fontWeight: 300 }}>{s.val}</div>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Members list */}
              {filteredMembers.length > 0 ? filteredMembers.map(m => (
                <div key={m.id} style={{
                  border: `1px solid ${m.status === "banned" ? "rgba(155,27,48,0.2)" : "rgba(184,160,74,0.08)"}`,
                  padding: "20px", background: m.status === "banned" ? "rgba(155,27,48,0.04)" : "rgba(14,13,11,0.5)",
                  marginBottom: 10,
                }}>
                  {/* Header row */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%",
                        background: m.status === "banned" ? "rgba(155,27,48,0.15)" : "rgba(184,160,74,0.08)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: FONTS.mono, fontSize: 12, color: m.status === "banned" ? RUBY : GOLD,
                        border: `1px solid ${m.status === "banned" ? "rgba(155,27,48,0.3)" : "rgba(184,160,74,0.15)"}`,
                      }}>{(m.email || "?")[0].toUpperCase()}</div>
                      <div>
                        <div style={{ fontFamily: FONTS.body, fontSize: 14, color: CREAM, fontWeight: 500 }}>
                          {m.username || m.email || "Unknown"}
                        </div>
                        <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM }}>{m.email}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {/* Role badge */}
                      <span style={{
                        fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 2, padding: "3px 10px",
                        textTransform: "uppercase",
                        color: m.role === "admin" ? GOLD : m.role === "banned" ? RUBY : "#4caf50",
                        border: `1px solid ${m.role === "admin" ? "rgba(184,160,74,0.3)" : m.role === "banned" ? "rgba(155,27,48,0.3)" : "rgba(76,175,80,0.3)"}`,
                      }}>{m.role || "member"}</span>
                      {/* Status dot */}
                      <div style={{
                        width: 8, height: 8, borderRadius: "50%",
                        background: m.status === "active" ? "#4caf50" : m.status === "banned" ? RUBY : "#ff9800",
                      }} />
                    </div>
                  </div>

                  {/* Info row */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 14 }}>
                    <div>
                      <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 4 }}>ROLE</label>
                      <select value={m.role || "member"} onChange={e => updateMember(m.id, "role", e.target.value)}
                        style={{ ...inputStyle, padding: "8px 10px", fontSize: 12, cursor: "pointer" }}>
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                        <option value="vip">VIP</option>
                        <option value="moderator">Moderator</option>
                        <option value="banned">Banned</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 4 }}>STATUS</label>
                      <select value={m.status || "active"} onChange={e => updateMember(m.id, "status", e.target.value)}
                        style={{ ...inputStyle, padding: "8px 10px", fontSize: 12, cursor: "pointer" }}>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="banned">Banned</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 4 }}>NOTES</label>
                      <input style={{ ...inputStyle, padding: "8px 10px", fontSize: 12 }}
                        value={m.notes || ""} placeholder="Admin notes..."
                        onChange={e => updateMember(m.id, "notes", e.target.value)} />
                    </div>
                  </div>

                  {/* Meta + Actions */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, paddingTop: 12, borderTop: "1px solid rgba(184,160,74,0.06)" }}>
                    <div style={{ display: "flex", gap: 16 }}>
                      <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM }}>
                        Joined: {m.created_at ? new Date(m.created_at).toLocaleDateString() : "N/A"}
                      </span>
                      <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM }}>
                        Last active: {m.last_accessed ? new Date(m.last_accessed).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Never"}
                      </span>
                      {m.clerk_id && <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: "rgba(212,205,180,0.25)" }}>ID: {m.clerk_id.slice(0, 12)}...</span>}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {m.status !== "banned" ? (
                        <button onClick={() => banMember(m.id, true)} style={dangerBtnStyle}>Ban</button>
                      ) : (
                        <button onClick={() => banMember(m.id, false)} style={saveBtnStyle}>Unban</button>
                      )}
                      <button onClick={() => { if (window.confirm("Delete this member permanently?")) deleteMember(m.id); }} style={{ ...dangerBtnStyle, borderColor: "rgba(155,27,48,0.6)" }}>Delete</button>
                    </div>
                  </div>
                </div>
              )) : (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 13, color: CREAM_DIM }}>
                    {memberSearch ? "No members match your search" : "No members yet. Run the members SQL schema in Supabase."}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PROJECTS TAB */}
          {tab === "projects" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
                <h2 style={{ fontFamily: FONTS.display, fontSize: 28, color: CREAM, fontWeight: 300, margin: 0 }}>Projects</h2>
                <button onClick={addProject} style={saveBtnStyle}>+ Add Project</button>
              </div>
              {projects.map((p, i) => (
                <div key={p.id} style={{
                  border: "1px solid rgba(184,160,74,0.1)", padding: "24px",
                  background: "rgba(14,13,11,0.5)", marginBottom: 16,
                }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                    <div>
                      <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 6 }}>TITLE</label>
                      <input style={inputStyle} value={p.title} onChange={e => updateProject(p.id, "title", e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 6 }}>TYPE</label>
                      <input style={inputStyle} value={p.type} onChange={e => updateProject(p.id, "type", e.target.value)} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 6 }}>DESCRIPTION</label>
                    <textarea style={textareaStyle} value={p.description} onChange={e => updateProject(p.id, "description", e.target.value)} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
                    <div>
                      <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 6 }}>TECH (comma sep)</label>
                      <input style={inputStyle} value={p.tech} onChange={e => updateProject(p.id, "tech", e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 6 }}>YEAR</label>
                      <input style={inputStyle} value={p.year} onChange={e => updateProject(p.id, "year", e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 6 }}>STATUS</label>
                      <input style={inputStyle} value={p.status} onChange={e => updateProject(p.id, "status", e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 6 }}>ACCENT COLOR</label>
                      <input style={inputStyle} value={p.accent_color} onChange={e => updateProject(p.id, "accent_color", e.target.value)} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 6 }}>URL (optional - shows Visit Site button)</label>
                    <input style={inputStyle} value={p.url || ""} placeholder="https://example.com"
                      onChange={e => updateProject(p.id, "url", e.target.value)} />
                  </div>
                  <button onClick={() => deleteProject(p.id)} style={dangerBtnStyle}>Delete Project</button>
                </div>
              ))}
              {projects.length === 0 && (
                <div style={{ fontFamily: FONTS.body, fontSize: 14, color: CREAM_DIM, padding: 40, textAlign: "center" }}>
                  No projects yet. Click "Add Project" to create one.
                </div>
              )}
            </div>
          )}

          {/* STATS TAB */}
          {tab === "stats" && (
            <div>
              <h2 style={{ fontFamily: FONTS.display, fontSize: 28, color: CREAM, fontWeight: 300, margin: "0 0 28px" }}>
                Site Stats
              </h2>
              <div style={{ display: "grid", gap: 16 }}>
                {stats.map(s => (
                  <div key={s.id} style={{
                    display: "flex", alignItems: "center", gap: 20,
                    border: "1px solid rgba(184,160,74,0.1)", padding: "16px 20px",
                    background: "rgba(14,13,11,0.5)",
                  }}>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 12, color: GOLD, letterSpacing: 2, textTransform: "uppercase", width: 200 }}>
                      {s.key.replace(/_/g, " ")}
                    </div>
                    <input
                      type="number" style={{ ...inputStyle, width: 120 }}
                      value={s.value}
                      onChange={e => setStats(prev => prev.map(x => x.id === s.id ? { ...x, value: e.target.value } : x))}
                    />
                    <button onClick={() => updateStat(s.id, s.value)} style={saveBtnStyle} disabled={saving}>
                      {saving ? "..." : "Save"}
                    </button>
                    <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM }}>
                      Updated: {new Date(s.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, marginTop: 20, lineHeight: 1.8 }}>
                These values appear in the "Live Stats" panel on the About section.
                Changes save to Supabase and update on the live site instantly.
              </div>
            </div>
          )}

          {/* CONTENT TAB */}
          {tab === "content" && (
            <div>
              <h2 style={{ fontFamily: FONTS.display, fontSize: 28, color: CREAM, fontWeight: 300, margin: "0 0 28px" }}>
                Site Content
              </h2>
              {content.length > 0 ? content.map(c => (
                <div key={c.id} style={{
                  border: "1px solid rgba(184,160,74,0.1)", padding: "20px",
                  background: "rgba(14,13,11,0.5)", marginBottom: 14,
                }}>
                  <label style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD, letterSpacing: 2, display: "block", marginBottom: 8, textTransform: "uppercase" }}>
                    {c.key.replace(/_/g, " ")}
                  </label>
                  <textarea
                    style={textareaStyle} value={c.value}
                    onChange={e => setContent(prev => prev.map(x => x.id === c.id ? { ...x, value: e.target.value } : x))}
                  />
                  <button onClick={() => updateContent(c.id, c.value)} style={{ ...saveBtnStyle, marginTop: 10 }}>Save</button>
                </div>
              )) : (
                <div style={{ fontFamily: FONTS.body, fontSize: 14, color: CREAM_DIM, marginBottom: 20 }}>
                  No content entries yet. Add your first one below.
                </div>
              )}
              <div style={{ border: "1px solid rgba(184,160,74,0.06)", padding: "20px", marginTop: 20 }}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD, letterSpacing: 2, marginBottom: 12 }}>ADD NEW CONTENT</div>
                <NewContentForm onAdd={addContent} />
              </div>
            </div>
          )}

          {/* TRACKING CONFIG TAB */}
          {tab === "tracking" && (
            <div>
              <h2 style={{ fontFamily: FONTS.display, fontSize: 28, color: CREAM, fontWeight: 300, margin: "0 0 28px" }}>
                Command Center Config
              </h2>
              <div style={{ border: "1px solid rgba(184,160,74,0.1)", padding: "24px", background: "rgba(14,13,11,0.5)", marginBottom: 20 }}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD, letterSpacing: 2, marginBottom: 12 }}>TRACKED CRYPTOCURRENCIES</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM_DIM, marginBottom: 12 }}>
                  Currently tracking: BTC, ETH, SOL, DOGE, XRP, ADA, DOT, LINK, AVAX, LTC
                </div>
                <div style={{ fontFamily: FONTS.body, fontSize: 12, color: CREAM_DIM, lineHeight: 1.8 }}>
                  To change coins, edit the CoinGecko API URL in the Terminal component.
                  Find valid coin IDs at: api.coingecko.com/api/v3/coins/list
                </div>
              </div>
              <div style={{ border: "1px solid rgba(184,160,74,0.1)", padding: "24px", background: "rgba(14,13,11,0.5)", marginBottom: 20 }}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD, letterSpacing: 2, marginBottom: 12 }}>LIVE DATA SOURCES</div>
                <div style={{ display: "grid", gap: 10 }}>
                  {[
                    { name: "CoinGecko", status: "ACTIVE", desc: "Crypto prices, market cap, volume", free: "30 calls/min" },
                    { name: "Alternative.me", status: "ACTIVE", desc: "Fear & Greed Index", free: "Unlimited" },
                    { name: "ExchangeRate API", status: "ACTIVE", desc: "Live forex rates (12 pairs)", free: "1,500/month" },
                    { name: "Finnhub", status: "ACTIVE", desc: "Stock prices, indices (AAPL, TSLA, NVDA)", free: "60 calls/min" },
                    { name: "Twelve Data", status: "STANDBY", desc: "Backup feed for stocks, forex, ETFs", free: "800 calls/day" },
                  ].map(api => (
                    <div key={api.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "rgba(20,20,16,0.5)" }}>
                      <div>
                        <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: CREAM }}>{api.name}</span>
                        <span style={{ fontFamily: FONTS.body, fontSize: 11, color: CREAM_DIM, marginLeft: 12 }}>{api.desc}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM }}>{api.free}</span>
                        <span style={{
                          fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 1, padding: "3px 8px",
                          color: api.status === "ACTIVE" ? "#4caf50" : "#ff9800",
                          border: `1px solid ${api.status === "ACTIVE" ? "rgba(76,175,80,0.3)" : "rgba(255,152,0,0.3)"}`,
                        }}>{api.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ border: "1px solid rgba(184,160,74,0.1)", padding: "24px", background: "rgba(14,13,11,0.5)" }}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: GOLD, letterSpacing: 2, marginBottom: 12 }}>REFRESH RATE</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM_DIM }}>
                  Currently set to <span style={{ color: GOLD }}>15 seconds</span>. Edit the setInterval value in the Terminal component to change.
                </div>
              </div>
            </div>
          )}

          {/* SOCIAL STATS TAB */}
          {tab === "social" && (
            <div>
              <h2 style={{ fontFamily: FONTS.display, fontSize: 28, color: CREAM, fontWeight: 300, margin: "0 0 8px" }}>
                Social Media Stats
              </h2>
              <p style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM_DIM, marginBottom: 28 }}>
                Update these from your platform dashboards. Changes show in the Terminal's Social tab.
              </p>
              {socialData.length > 0 ? socialData.map(platform => (
                <div key={platform.id} style={{
                  border: "1px solid rgba(184,160,74,0.1)", padding: "24px",
                  background: "rgba(14,13,11,0.5)", marginBottom: 16,
                }}>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 14, color: GOLD, letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>
                    {platform.platform}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
                    {[
                      { field: "followers", label: "Followers" },
                      { field: "impressions", label: "Impressions" },
                      { field: "engagements", label: "Engagements" },
                      { field: "engagement_rate", label: "Eng. Rate %" },
                      { field: "likes", label: "Likes" },
                      { field: "views", label: "Views" },
                      { field: "replies", label: "Replies" },
                      { field: "reposts", label: "Reposts" },
                      { field: "subscribers", label: "Subscribers" },
                      { field: "profile_visits", label: "Profile Visits" },
                    ].map(f => (
                      <div key={f.field}>
                        <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 4 }}>{f.label}</label>
                        <input
                          type="number" step={f.field === "engagement_rate" ? "0.01" : "1"}
                          style={{ ...inputStyle, padding: "8px 10px", fontSize: 13 }}
                          value={platform[f.field] || 0}
                          onChange={e => setSocialData(prev => prev.map(s => s.id === platform.id ? { ...s, [f.field]: f.field === "engagement_rate" ? parseFloat(e.target.value) || 0 : parseInt(e.target.value) || 0 } : s))}
                        />
                      </div>
                    ))}
                  </div>
                  <button onClick={() => saveSocial(platform.id)} style={{ ...saveBtnStyle, marginTop: 14 }} disabled={saving}>
                    {saving ? "Saving..." : "Save " + platform.platform}
                  </button>
                </div>
              )) : (
                <div style={{ fontFamily: FONTS.body, fontSize: 14, color: CREAM_DIM, padding: 40, textAlign: "center" }}>
                  No social stats found. Run the social-stats-schema.sql in Supabase first.
                </div>
              )}
            </div>
          )}

          {/* SPORTSAI BOT TAB */}
          {tab === "sportsai" && (
            <div>
              <h2 style={{ fontFamily: FONTS.display, fontSize: 28, color: CREAM, fontWeight: 300, margin: "0 0 10px" }}>
                🏆 SportsAI Bot
              </h2>
              <p style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM_DIM, marginBottom: 28, lineHeight: 1.7, maxWidth: 680 }}>
                Management dashboard for the NautX SportsAI Telegram bot. View recent picks, user stats, and trigger actions across all users.
              </p>

              {/* Bot status card */}
              <div style={{ border: "1px solid rgba(184,160,74,0.15)", padding: 20, marginBottom: 24, background: "rgba(14,13,11,0.6)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#4caf50", animation: "livePulse 2s infinite" }} />
                      <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: "rgba(76,175,80,0.9)", letterSpacing: 2 }}>BOT ONLINE</span>
                    </div>
                    <div style={{ fontFamily: FONTS.body, fontSize: 16, color: CREAM }}>SportsAI Predictive Bot V3</div>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, marginTop: 2 }}>Python • Claude Opus 4.7 • Google Sheets</div>
                  </div>
                  <a href="https://t.me" target="_blank" rel="noopener noreferrer" style={{
                    padding: "8px 16px", background: "rgba(184,160,74,0.08)", border: "1px solid rgba(184,160,74,0.3)",
                    color: GOLD, textDecoration: "none", fontFamily: FONTS.mono, fontSize: 10,
                    letterSpacing: 2, textTransform: "uppercase",
                  }}>Open in Telegram &rarr;</a>
                </div>
              </div>

              {/* Stat grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 28 }}>
                {[
                  { label: "Total Users", value: "—", color: GOLD },
                  { label: "Picks Today", value: "—", color: CREAM },
                  { label: "W/L Rate", value: "—", color: "#4caf50" },
                  { label: "Bankroll ROI", value: "—", color: OLIVE },
                ].map((s, i) => (
                  <div key={i} style={{ border: "1px solid rgba(184,160,74,0.1)", padding: 16, background: "rgba(14,13,11,0.5)" }}>
                    <div style={{ fontFamily: FONTS.display, fontSize: 28, color: s.color, fontWeight: 300 }}>{s.value}</div>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, marginTop: 4, textTransform: "uppercase" }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Setup instructions */}
              <div style={{ border: "1px solid rgba(184,160,74,0.12)", padding: 24, background: "rgba(14,13,11,0.5)", marginBottom: 20 }}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: RUBY, letterSpacing: 3, marginBottom: 10 }}>⚙ SETUP REQUIRED</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM, marginBottom: 14, fontWeight: 500 }}>
                  Connect Google Sheets to unlock pick history and user stats
                </div>
                <div style={{ fontFamily: FONTS.body, fontSize: 12, color: CREAM_DIM, lineHeight: 1.9, marginBottom: 16 }}>
                  The bot stores all picks in the <span style={{ color: GOLD, fontFamily: FONTS.mono }}>NautXSportsTracker</span> spreadsheet. To display that data here, add a Google Service Account to Vercel:
                </div>
                <ol style={{ fontFamily: FONTS.body, fontSize: 12, color: CREAM_DIM, lineHeight: 1.8, paddingLeft: 20, marginBottom: 16 }}>
                  <li>In Vercel dashboard → Settings → Environment Variables</li>
                  <li>Add <span style={{ color: GOLD, fontFamily: FONTS.mono }}>GOOGLE_SERVICE_ACCOUNT_JSON</span> (paste entire credentials.json contents)</li>
                  <li>Add <span style={{ color: GOLD, fontFamily: FONTS.mono }}>ANTHROPIC_API_KEY</span> (same one the bot uses)</li>
                  <li>Deploy the <span style={{ color: GOLD, fontFamily: FONTS.mono }}>/api/sportsai-admin</span> serverless function (see repo)</li>
                </ol>
                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: "rgba(212,205,180,0.5)", lineHeight: 1.7, padding: 12, background: "rgba(20,20,16,0.6)", border: "1px solid rgba(184,160,74,0.08)" }}>
                  Once configured, this tab will display live user data, recent picks, W/L tracking, and let you trigger morning briefings or clear stale picks across all users.
                </div>
              </div>

              {/* Quick actions */}
              <div style={{ fontFamily: FONTS.mono, fontSize: 11, color: GOLD, letterSpacing: 2, marginBottom: 12, textTransform: "uppercase" }}>Quick Actions</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                {[
                  { label: "Trigger Morning Briefing", desc: "Send daily slate to all users", disabled: true },
                  { label: "View Recent Picks", desc: "Last 50 picks across users", disabled: true },
                  { label: "Export W/L Report", desc: "CSV of current records", disabled: true },
                  { label: "Broadcast Message", desc: "Send to all bot users", disabled: true },
                ].map((a, i) => (
                  <button key={i} disabled={a.disabled} style={{
                    padding: "12px 14px", textAlign: "left", border: "1px solid rgba(184,160,74,0.1)",
                    background: "rgba(14,13,11,0.5)", cursor: a.disabled ? "not-allowed" : "pointer",
                    opacity: a.disabled ? 0.5 : 1,
                  }}>
                    <div style={{ fontFamily: FONTS.body, fontSize: 13, color: CREAM, marginBottom: 3 }}>{a.label}</div>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 1 }}>{a.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewContentForm({ onAdd }) {
  const [key, setKey] = useState("");
  const [val, setVal] = useState("");
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
      <div style={{ flex: 1 }}>
        <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 6 }}>KEY</label>
        <input style={inputStyle} value={key} onChange={e => setKey(e.target.value)} placeholder="e.g. about_text" />
      </div>
      <div style={{ flex: 2 }}>
        <label style={{ fontFamily: FONTS.mono, fontSize: 9, color: CREAM_DIM, letterSpacing: 2, display: "block", marginBottom: 6 }}>VALUE</label>
        <input style={inputStyle} value={val} onChange={e => setVal(e.target.value)} placeholder="Content value..." />
      </div>
      <button onClick={() => { if (key && val) { onAdd(key, val); setKey(""); setVal(""); } }} style={saveBtnStyle}>Add</button>
    </div>
  );
}

function AdminPanel({ onClose }) {
  return (
    <SignedOut>
      <div style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(6,6,4,0.97)", backdropFilter: "blur(20px)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{ fontFamily: FONTS.display, fontSize: 24, color: CREAM, letterSpacing: 6, marginBottom: 8 }}>
          NAUT<span style={{ color: RUBY }}>X</span> ADMIN
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: CREAM_DIM, letterSpacing: 3, marginBottom: 32 }}>
          SIGN IN TO CONTINUE
        </div>
        <SignIn appearance={{
          variables: {
            colorPrimary: "#b8a04a",
            colorBackground: "#0e0e0c",
            colorText: "#d4cdb4",
            colorInputBackground: "#141410",
            colorInputText: "#d4cdb4",
          }
        }} />
        <button onClick={onClose} style={{ ...adminBtnStyle, marginTop: 24, fontSize: 10, padding: "8px 20px" }}>Cancel</button>
      </div>
    </SignedOut>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  MAIN APP
 * ═══════════════════════════════════════════════════════════════ */
function NautXSite() {
  const [active, setActive] = useState("");
  const [adminOpen, setAdminOpen] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [showCrypto, setShowCrypto] = useState(false);

  // Set tab title, favicon, and viewport
  useEffect(() => {
    document.title = "NXMG | Naut X Media Group";

    // Ensure viewport meta exists for mobile
    if (!document.querySelector('meta[name="viewport"]')) {
      const meta = document.createElement("meta");
      meta.name = "viewport";
      meta.content = "width=device-width, initial-scale=1";
      document.head.appendChild(meta);
    }

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" fill="#08080a"/><rect x="1" y="1" width="30" height="30" fill="none" stroke="#b8a04a" stroke-width="0.5" opacity="0.4"/><text x="16" y="13" text-anchor="middle" font-family="serif" font-size="7" font-weight="600" letter-spacing="1" fill="#d4cdb4">NAUT</text><text x="16" y="23" text-anchor="middle" font-family="monospace" font-size="6" font-weight="700" letter-spacing="2" fill="#9b1b30">X</text><line x1="6" y1="27" x2="26" y2="27" stroke="#b8a04a" stroke-width="0.5" opacity="0.3"/></svg>`;
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    let link = document.querySelector("link[rel*='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.href = url;
  }, []);

  // Ctrl+Shift+A to toggle admin panel
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === "A") {
        e.preventDefault();
        setAdminOpen(prev => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const ids = ["contact", "terminal", "work", "services", "about"];
    const handler = () => {
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < window.innerHeight * 0.45) {
          setActive(id);
          return;
        }
      }
      setActive("");
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  /* ─── SUPABASE: Uncomment to pull stats from DB ───
  const [dbStats, setDbStats] = useState(null);
  useEffect(() => {
    const fetchStats = async () => {
      const { data } = await supabase.from("site_stats").select("*");
      if (data) {
        const map = {};
        data.forEach(row => { map[row.key] = row.value; });
        setDbStats(map);
      }
    };
    fetchStats();
    // Real-time subscription
    const channel = supabase
      .channel("stats_changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "site_stats" }, fetchStats)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  // Track page view
  useEffect(() => {
    supabase.from("page_views").insert({
      page: window.location.pathname,
      referrer: document.referrer,
      user_agent: navigator.userAgent,
    });
  }, []);
  ─── END SUPABASE BLOCK ─── */

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Outfit:wght@200;300;400;500;600&family=JetBrains+Mono:wght@300;400;500&display=swap');

        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        body {
          background: ${BG};
          color: ${CREAM};
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          overflow-x: hidden;
        }
        ::selection { background: rgba(184,160,74,0.25); color: #fff; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: ${BG}; }
        ::-webkit-scrollbar-thumb { background: rgba(184,160,74,0.15); }
        ::-webkit-scrollbar-thumb:hover { background: rgba(184,160,74,0.3); }

        @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes scrollPulse { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }
        @keyframes livePulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.85); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes chatPulse {
          0% { box-shadow: 0 8px 24px rgba(184,160,74,0.4), 0 0 0 0 rgba(184,160,74,0.5); }
          70% { box-shadow: 0 8px 24px rgba(184,160,74,0.4), 0 0 0 16px rgba(184,160,74,0); }
          100% { box-shadow: 0 8px 24px rgba(184,160,74,0.4), 0 0 0 0 rgba(184,160,74,0); }
        }
        @keyframes chatSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes typingDot {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-4px); }
        }
        @keyframes g1 {
          0%,92% { transform: translate(0); }
          93% { transform: translate(-2px, 1px); }
          94% { transform: translate(2px, -1px); }
          95% { transform: translate(0); }
          96% { transform: translate(1px, 2px); }
          97%,100% { transform: translate(0); }
        }
        @keyframes g2 {
          0%,90% { transform: translate(0); }
          91% { transform: translate(1px, -2px); }
          92% { transform: translate(-2px, 1px); }
          93%,100% { transform: translate(0); }
        }

        @media (max-width: 768px) {
          .nav-desk { display: none !important; }
          .nav-mob { display: flex !important; }
          .about-grid { grid-template-columns: 1fr !important; gap: 32px !important; }

          /* Global mobile padding */
          section { padding-left: 16px !important; padding-right: 16px !important; }
          nav { padding-left: 16px !important; padding-right: 16px !important; }
          footer { padding-left: 16px !important; padding-right: 16px !important; flex-direction: column !important; text-align: center !important; gap: 8px !important; }

          /* Hero mobile */
          h1 { font-size: 42px !important; }

          /* Hide cursor effects on touch devices */
          .cursor-glow { display: none !important; }

          /* Terminal mobile - scrollable tabs */
          .terminal-tabs { overflow-x: auto !important; -webkit-overflow-scrolling: touch !important; }
          .terminal-tabs::-webkit-scrollbar { display: none; }
          .terminal-tabs button { white-space: nowrap !important; flex-shrink: 0 !important; padding: 12px 16px !important; }

          /* Terminal table mobile scroll */
          table { font-size: 11px !important; }
          td, th { padding: 10px 8px !important; }

          /* Project cards single column */
          .projects-grid { grid-template-columns: 1fr !important; }

          /* Services grid mobile */
          .services-row { grid-template-columns: 40px 1fr auto !important; gap: 16px !important; }

          /* All grids responsive */
          [style*="repeat(auto-fit"] { grid-template-columns: 1fr !important; }

          /* Touch-friendly button sizing */
          button, a[style] { min-height: 44px; }
          input, select, textarea { font-size: 16px !important; }

          /* Scroll indicator hide */
          .scroll-indicator { display: none !important; }

          /* Ticker smaller on mobile */
          .ticker-text { font-size: 11px !important; letter-spacing: 2px !important; }

          /* Admin panel mobile - horizontal tabs */
          .admin-layout { flex-direction: column !important; }
          .admin-sidebar {
            width: 100% !important; border-right: none !important;
            border-bottom: 1px solid rgba(184,160,74,0.08) !important;
            padding: 8px 0 !important; display: flex !important;
            flex-direction: row !important; overflow-x: auto !important;
            flex-shrink: 0 !important;
          }
          .admin-sidebar::-webkit-scrollbar { display: none; }
          .admin-sidebar button {
            white-space: nowrap !important; padding: 10px 14px !important;
            border-left: none !important;
            border-bottom: 2px solid transparent !important;
            display: inline-block !important; width: auto !important;
          }
          .admin-sidebar-user { display: none !important; }
          .admin-main { padding: 20px 16px !important; }

          /* Member cards mobile */
          .member-info-grid { grid-template-columns: 1fr !important; }
          .member-actions { flex-direction: column !important; align-items: stretch !important; }

          /* Crypto center mobile */
          .crypto-charts-grid { grid-template-columns: 1fr !important; }
        }

        @media (max-width: 480px) {
          h1 { font-size: 36px !important; }
          section { padding-top: 80px !important; padding-bottom: 80px !important; }
          .stats-panel-grid { grid-template-columns: 1fr 1fr !important; gap: 16px !important; }
        }

        @media (min-width: 769px) {
          .nav-mob { display: none !important; }
        }

        a { text-decoration: none; }

        /* Scrollbar for table */
        table::-webkit-scrollbar { height: 4px; }
        table::-webkit-scrollbar-thumb { background: rgba(184,160,74,0.1); }
      `}</style>

      <MatrixRain />
      <CursorGlow />
      <Grain />
      <Nav active={active} onAdmin={() => setAdminOpen(true)} />
      <Hero onCrypto={() => setShowCrypto(true)} />
      {/* Content backdrop — dark layer so text is crisp over the rain */}
      <div style={{ position: "relative", zIndex: 2, background: "rgba(8,8,10,0.88)" }}>
        <Ticker words={["DESIGN", "DEVELOP", "DEPLOY", "DOMINATE", "CRYPTO", "WEB3", "FULL-STACK", "NAUT X", "NO LIMITS"]} />
        <About />
        <Services />
        <Work />
        <Ticker words={["LIVE DATA", "REAL-TIME", "COMMAND CENTER", "ALWAYS ON", "NAUT X MEDIA GROUP"]} speed={35} accent />
        <Terminal />
        <Contact onLinks={() => { setShowLinks(true); window.scrollTo(0, 0); }} />
        <Footer />
      </div>

      {/* Links page overlay */}
      {showLinks && <LinksPage onBack={() => setShowLinks(false)} />}

      {/* Crypto Center overlay */}
      {showCrypto && <CryptoCenter onBack={() => setShowCrypto(false)} />}

      {/* Admin Panel — Ctrl+Shift+A or triple-click MG.
          Signed-out users see Clerk sign-in (AdminPanel),
          signed-in users see the real panel (AdminInner). */}
      {adminOpen && (
        <>
          <SignedOut>
            <AdminPanel onClose={() => setAdminOpen(false)} />
          </SignedOut>
          <SignedIn>
            <AdminInner onClose={() => setAdminOpen(false)} />
          </SignedIn>
        </>
      )}
    </>
  );
}

/* APP EXPORT - Wrapped with ClerkProvider */
export default function App() {
  return (
    <ClerkProvider publishableKey={CLERK_KEY}>
      <NautXSite />
    </ClerkProvider>
  );
}
